export type LanguageCode = 'en' | 'lt' | 'ru' | 'fr' | 'de'

export interface LanguageOption {
  code: LanguageCode
  label: string
  name: string
  locale: string
}

export type TranslationTable = Record<string, Partial<Record<LanguageCode, string>>>

export interface SiteMeta {
  title: Partial<Record<LanguageCode, string>>
  description: Partial<Record<LanguageCode, string>>
}

export const STORAGE_KEY = 'uliunai-language'

export const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'EN', name: 'English', locale: 'en-US' },
  { code: 'lt', label: 'LT', name: 'Lietuvių', locale: 'lt-LT' },
  { code: 'ru', label: 'RU', name: 'Русский', locale: 'ru-RU' },
  { code: 'fr', label: 'FR', name: 'Français', locale: 'fr-FR' },
  { code: 'de', label: 'DE', name: 'Deutsch', locale: 'de-DE' },
]

export const DEFAULT_LANGUAGE: LanguageCode = 'en'
export const SUPPORTED_LANGUAGE_CODES = LANGUAGES.map((language) => language.code)

export function normalizeLanguage(value: unknown): LanguageCode {
  if (typeof value !== 'string') return DEFAULT_LANGUAGE
  const code = value.toLowerCase().split(/[-_]/)[0]
  return SUPPORTED_LANGUAGE_CODES.includes(code as LanguageCode)
    ? (code as LanguageCode)
    : DEFAULT_LANGUAGE
}

export function getLanguageOption(code: LanguageCode): LanguageOption {
  return LANGUAGES.find((language) => language.code === code) ?? LANGUAGES[0]
}

export function getInitialLanguage(): LanguageCode {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored) return normalizeLanguage(stored)
  return normalizeLanguage(window.navigator.language)
}

export function persistLanguage(language: LanguageCode) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, language)
}

export function translateText(
  text: string,
  language: LanguageCode,
  translations: TranslationTable,
): string {
  if (language === DEFAULT_LANGUAGE) return text
  const key = resolveTranslationKey(text, translations)
  return translations[key]?.[language] ?? text
}

function normalizeTranslationKey(value: string): string {
  return value
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s*[—–]\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim()
}

function resolveTranslationKey(text: string, translations: TranslationTable): string {
  if (translations[text]) return text
  const normalized = normalizeTranslationKey(text)
  return translations[normalized] ? normalized : text
}

export function languageQueryParam(language: LanguageCode): string {
  return `lang=${encodeURIComponent(language)}`
}

export function withLanguageParam(path: string, language: LanguageCode): string {
  const [base, hash = ''] = path.split('#')
  const separator = base.includes('?') ? '&' : '?'
  const localized = `${base}${separator}${languageQueryParam(language)}`
  return hash ? `${localized}#${hash}` : localized
}

export function formatLocalizedDate(value: string | undefined, language: LanguageCode): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(getLanguageOption(language).locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

export function newsTagLabel(tag: string | undefined): string {
  switch ((tag || '').toLowerCase()) {
    case 'kf1':
      return 'Killing Floor 1'
    case 'kf2':
      return 'Killing Floor 2'
    case 'minecraft':
      return 'Minecraft'
    case 'general':
      return 'General'
    default:
      return tag || ''
  }
}

export function applyDocumentLanguage(language: LanguageCode, meta?: SiteMeta) {
  if (typeof document === 'undefined') return
  document.documentElement.lang = language
  if (!meta) return

  const title = meta.title[language] ?? meta.title.en
  const description = meta.description[language] ?? meta.description.en

  if (title) {
    document.title = title
    setMeta('property', 'og:title', title)
    setMeta('name', 'twitter:title', title)
  }

  if (description) {
    setMeta('name', 'description', description)
    setMeta('property', 'og:description', description)
    setMeta('name', 'twitter:description', description)
  }
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  const element = document.querySelector(`meta[${attribute}="${key}"]`)
  if (element) element.setAttribute('content', content)
}

export function createReverseTranslations(translations: TranslationTable): Record<string, string> {
  const reverse: Record<string, string> = {}
  for (const [source, targets] of Object.entries(translations)) {
    for (const target of Object.values(targets)) {
      if (target) reverse[target] = source
    }
  }
  return reverse
}

export function applyDomTranslations(
  root: ParentNode,
  language: LanguageCode,
  translations: TranslationTable,
) {
  if (typeof document === 'undefined') return

  const reverse = createReverseTranslations(translations)
  translateTextNodes(root, language, translations, reverse)
  translateAttributes(root, language, translations, reverse)
}

function originalFromRendered(value: string, reverse: Record<string, string>): string {
  return reverse[value] ?? value
}

function translateTextNodes(
  root: ParentNode,
  language: LanguageCode,
  translations: TranslationTable,
  reverse: Record<string, string>,
) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      if (parent.closest('[data-no-translate], script, style, textarea, input')) {
        return NodeFilter.FILTER_REJECT
      }
      return node.nodeValue && node.nodeValue.trim().length > 1
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT
    },
  })

  const nodes: Text[] = []
  while (walker.nextNode()) nodes.push(walker.currentNode as Text)

  for (const node of nodes) {
    const value = node.nodeValue ?? ''
    const leading = value.match(/^\s*/)?.[0] ?? ''
    const trailing = value.match(/\s*$/)?.[0] ?? ''
    const trimmed = value.trim()
    const source = originalFromRendered(trimmed, reverse)
    const translated = translateText(source, language, translations)
    node.nodeValue = `${leading}${translated}${trailing}`
  }
}

function translateAttributes(
  root: ParentNode,
  language: LanguageCode,
  translations: TranslationTable,
  reverse: Record<string, string>,
) {
  const attributes = ['aria-label', 'title', 'placeholder', 'alt']
  const elements =
    root instanceof Element
      ? [root, ...Array.from(root.querySelectorAll('*'))]
      : Array.from(root.querySelectorAll('*'))

  for (const element of elements) {
    if (element.closest('[data-no-translate]')) continue
    for (const attribute of attributes) {
      const value = element.getAttribute(attribute)
      if (!value) continue
      const sourceAttr = `data-l10n-source-${attribute.replace(':', '-')}`
      const source = element.getAttribute(sourceAttr) ?? originalFromRendered(value.trim(), reverse)
      element.setAttribute(sourceAttr, source)
      element.setAttribute(attribute, translateText(source, language, translations))
    }
  }
}

export const COMMON_TRANSLATIONS: TranslationTable = {
  Home: { lt: 'Pradžia', ru: 'Главная', fr: 'Accueil', de: 'Startseite' },
  About: { lt: 'Apie', ru: 'О нас', fr: 'À propos', de: 'Über uns' },
  // Nav chrome labels — keep short so the hub top bar fits LT/RU/FR/DE.
  // Longer phrasing belongs in page titles / body copy (e.g. Code of Conduct).
  Games: { lt: 'Žaidimai', ru: 'Игры', fr: 'Jeux', de: 'Spiele' },
  Community: { lt: 'Bendruomenė', ru: 'Сообщество', fr: 'Communauté', de: 'Community' },
  Discord: { lt: 'Discord', ru: 'Discord', fr: 'Discord', de: 'Discord' },
  Conduct: { lt: 'Elgesys', ru: 'Правила', fr: 'Conduite', de: 'Regeln' },
  Team: { lt: 'Komanda', ru: 'Команда', fr: 'Équipe', de: 'Team' },
  News: { lt: 'Naujienos', ru: 'Новости', fr: 'Actus', de: 'News' },
  Contact: { lt: 'Kontaktai', ru: 'Контакты', fr: 'Contact', de: 'Kontakt' },
  Stats: { lt: 'Statistika', ru: 'Статистика', fr: 'Stats', de: 'Stats' },
  Servers: { lt: 'Serveriai', ru: 'Серверы', fr: 'Serveurs', de: 'Server' },
  Mods: { lt: 'Modai', ru: 'Моды', fr: 'Mods', de: 'Mods' },
  Join: { lt: 'Prisijungti', ru: 'Подключиться', fr: 'Rejoindre', de: 'Beitreten' },
  Rules: { lt: 'Taisyklės', ru: 'Правила', fr: 'Règles', de: 'Regeln' },
  Gallery: { lt: 'Galerija', ru: 'Галерея', fr: 'Galerie', de: 'Galerie' },
  VIP: { lt: 'VIP', ru: 'VIP', fr: 'VIP', de: 'VIP' },
  Perks: { lt: 'Perkai', ru: 'Перки', fr: 'Atouts', de: 'Perks' },
  Language: { lt: 'Kalba', ru: 'Язык', fr: 'Langue', de: 'Sprache' },
  'Select language': {
    lt: 'Pasirinkite kalbą',
    ru: 'Выберите язык',
    fr: 'Choisir la langue',
    de: 'Sprache wählen',
  },
  'Server Info': {
    lt: 'Serverio informacija',
    ru: 'Информация о сервере',
    fr: 'Infos serveur',
    de: 'Serverinfo',
  },
  'Join Discord': {
    lt: 'Prisijungti prie Discord',
    ru: 'Войти в Discord',
    fr: 'Rejoindre Discord',
    de: 'Discord beitreten',
  },
  'Open menu': {
    lt: 'Atidaryti meniu',
    ru: 'Открыть меню',
    fr: 'Ouvrir le menu',
    de: 'Menü öffnen',
  },
  'Close menu': {
    lt: 'Uždaryti meniu',
    ru: 'Закрыть меню',
    fr: 'Fermer le menu',
    de: 'Menü schließen',
  },
  'Toggle menu': {
    lt: 'Perjungti meniu',
    ru: 'Переключить меню',
    fr: 'Afficher le menu',
    de: 'Menü umschalten',
  },
  'Scroll to top': {
    lt: 'Grįžti į viršų',
    ru: 'Наверх',
    fr: 'Retour en haut',
    de: 'Nach oben',
  },
  'Scroll to games': {
    lt: 'Slinkti prie žaidimų',
    ru: 'Перейти к играм',
    fr: 'Aller aux jeux',
    de: 'Zu den Spielen scrollen',
  },
  'Uliunai home': {
    lt: 'Uliunai pradžia',
    ru: 'Главная Uliunai',
    fr: 'Accueil Uliunai',
    de: 'Uliunai Startseite',
  },
  Primary: { lt: 'Pagrindinis', ru: 'Основное', fr: 'Principal', de: 'Primär' },
  Mobile: { lt: 'Mobilus meniu', ru: 'Мобильное меню', fr: 'Mobile', de: 'Mobil' },
  Footer: { lt: 'Poraštė', ru: 'Нижняя навигация', fr: 'Pied de page', de: 'Fußbereich' },
  Legal: { lt: 'Teisinė informacija', ru: 'Правовая информация', fr: 'Mentions légales', de: 'Rechtliches' },
  'Community social links': {
    lt: 'Bendruomenės socialinės nuorodos',
    ru: 'Социальные ссылки сообщества',
    fr: 'Liens sociaux de la communauté',
    de: 'Community Social Links',
  },
  'Community Hub': {
    lt: 'Bendruomenės centras',
    ru: 'Центр сообщества',
    fr: 'Hub communauté',
    de: 'Community-Hub',
  },
  'Uliunai Servers': {
    lt: 'Uliunai serveriai',
    ru: 'Серверы Uliunai',
    fr: 'Serveurs Uliunai',
    de: 'Uliunai Server',
  },
  'Killing Floor': {
    lt: 'Killing Floor',
    ru: 'Killing Floor',
    fr: 'Killing Floor',
    de: 'Killing Floor',
  },
  'Killing Floor 1': { lt: 'Killing Floor 1', ru: 'Killing Floor 1', fr: 'Killing Floor 1', de: 'Killing Floor 1' },
  'Killing Floor 2': { lt: 'Killing Floor 2', ru: 'Killing Floor 2', fr: 'Killing Floor 2', de: 'Killing Floor 2' },
  Minecraft: { lt: 'Minecraft', ru: 'Minecraft', fr: 'Minecraft', de: 'Minecraft' },
  'One community.': {
    lt: 'Viena bendruomenė.',
    ru: 'Одно сообщество.',
    fr: 'Une communauté.',
    de: 'Eine Community.',
  },
  Every: { lt: 'Kiekvienas', ru: 'Единство', fr: 'Chaque', de: 'Jedes' },
  battlefield: { lt: 'mūšio laukas', ru: 'в сражении', fr: 'champ de bataille', de: 'Schlachtfeld' },
  'Every battlefield.': {
    lt: 'Kiekvienas mūšio laukas.',
    ru: 'Единство в сражении.',
    fr: 'Chaque champ de bataille.',
    de: 'Jedes Schlachtfeld.',
  },
  'Our games': { lt: 'Mūsų žaidimai', ru: 'Наши игры', fr: 'Nos jeux', de: 'Unsere Spiele' },
  'Choose Your World': {
    lt: 'Pasirink savo pasaulį',
    ru: 'Выбери свой мир',
    fr: 'Choisis ton monde',
    de: 'Wähle deine Welt',
  },
  'On the Horizon': {
    lt: 'Netrukus',
    ru: 'На горизонте',
    fr: 'À venir',
    de: 'Am Horizont',
  },
  'Live now': { lt: 'Veikia dabar', ru: 'Уже онлайн', fr: 'En ligne', de: 'Jetzt online' },
  'Coming soon': { lt: 'Netrukus', ru: 'Скоро', fr: 'Bientôt', de: 'Bald verfügbar' },
  Soon: { lt: 'Netrukus', ru: 'Скоро', fr: 'Bientôt', de: 'Bald' },
  Play: { lt: 'Žaisti', ru: 'Играть', fr: 'Jouer', de: 'Spielen' },
  'Sneak Peek': { lt: 'Žvilgtelėti', ru: 'Посмотреть', fr: 'Aperçu', de: 'Vorschau' },
  'Co-op zed survival': {
    lt: 'Kooperacinis zedų išgyvenimas',
    ru: 'Кооперативное выживание против зедов',
    fr: 'Survie zed en coop',
    de: 'Koop-Zed-Survival',
  },
  'Build & survive together': {
    lt: 'Statyk ir išgyvenk kartu',
    ru: 'Стройте и выживайте вместе',
    fr: 'Construire et survivre ensemble',
    de: 'Gemeinsam bauen und überleben',
  },
  'Six-player zed-slaying': {
    lt: 'Šešių žaidėjų zedų skerdynės',
    ru: 'Уничтожение зедов на шесть игроков',
    fr: 'Massacre de zeds à six joueurs',
    de: 'Zed-Jagd für sechs Spieler',
  },
  'Classic tactical FPS': {
    lt: 'Klasikinė taktinė FPS',
    ru: 'Классический тактический FPS',
    fr: 'FPS tactique classique',
    de: 'Klassischer taktischer FPS',
  },
  'Modern tactical FPS': {
    lt: 'Moderni taktinė FPS',
    ru: 'Современный тактический FPS',
    fr: 'FPS tactique moderne',
    de: 'Moderner taktischer FPS',
  },
  'Co-op zombie shooter': {
    lt: 'Kooperacinė zombių šaudyklė',
    ru: 'Кооперативный зомби-шутер',
    fr: 'Shooter zombie en coop',
    de: 'Koop-Zombie-Shooter',
  },
  'COMING SOON': { lt: 'NETRUKUS', ru: 'СКОРО', fr: 'BIENTÔT', de: 'BALD' },
  'Jump into any of our community servers. Each game has its own home - pick one and play.': {
    lt: 'Šok į bet kurį mūsų bendruomenės serverį. Kiekvienas žaidimas turi savo namus - rinkis ir žaisk.',
    ru: 'Заходи на любой сервер нашего сообщества. У каждой игры есть свой дом - выбирай и играй.',
    fr: 'Rejoins n’importe lequel de nos serveurs communautaires. Chaque jeu a sa propre maison - choisis et joue.',
    de: 'Spring auf einen unserer Community-Server. Jedes Spiel hat sein eigenes Zuhause - wähle eins und spiel los.',
  },
  'More worlds joining the Uliunai community soon.': {
    lt: 'Netrukus prie Uliunai bendruomenės prisijungs daugiau pasaulių.',
    ru: 'Скоро к сообществу Uliunai присоединятся новые миры.',
    fr: 'D’autres mondes rejoindront bientôt la communauté Uliunai.',
    de: 'Bald schließen sich weitere Welten der Uliunai Community an.',
  },
  'Who we are': { lt: 'Kas mes esame', ru: 'Кто мы', fr: 'Qui nous sommes', de: 'Wer wir sind' },
  'The Uliunai Community': {
    lt: 'Uliunai bendruomenė',
    ru: 'Сообщество Uliunai',
    fr: 'La communauté Uliunai',
    de: 'Die Uliunai Community',
  },
  'Uliunai has been bringing players together since': {
    lt: 'Uliunai buria žaidėjus nuo',
    ru: 'Uliunai объединяет игроков с',
    fr: 'Uliunai rassemble les joueurs depuis',
    de: 'Uliunai bringt Spieler zusammen seit',
  },
  'home to the': {
    lt: 'čia veikia',
    ru: 'здесь находится',
    fr: 'avec le',
    de: 'Heimat des',
  },
  'longest-running Killing Floor server': {
    lt: 'ilgiausiai veikiantis Killing Floor serveris',
    ru: 'самый долго работающий сервер Killing Floor',
    fr: 'serveur Killing Floor le plus ancien',
    de: 'am längsten laufenden Killing Floor Servers',
  },
  'anywhere, and now a growing family of community servers across many games.': {
    lt: 'ir dabar auganti bendruomenės serverių šeima daugelyje žaidimų.',
    ru: 'а теперь это растущая семья серверов сообщества в разных играх.',
    fr: 'et désormais une famille grandissante de serveurs communautaires sur plusieurs jeux.',
    de: 'und inzwischen eine wachsende Familie von Community-Servern über viele Spiele hinweg.',
  },
  "We're built on something simple: a friendly, welcoming place to play. No elitism, no toxicity - just good people, fair games, and servers that have stayed online, tuned, and loved for well over a decade.": {
    lt: 'Mūsų pagrindas paprastas: draugiška, svetinga vieta žaisti. Jokio elitizmo, jokio toksiškumo - tik geri žmonės, sąžiningi žaidimai ir serveriai, kurie daugiau nei dešimtmetį išliko prižiūrimi, derinami ir mylimi.',
    ru: 'Мы строим всё на простой идее: дружелюбное место для игры. Без элитизма, без токсичности - только хорошие люди, честные игры и серверы, которые уже больше десятилетия остаются онлайн, настраиваются и любимы игроками.',
    fr: 'Notre base est simple : un lieu accueillant et amical pour jouer. Pas d’élitisme, pas de toxicité - seulement de bonnes personnes, des parties équitables et des serveurs maintenus, réglés et aimés depuis plus de dix ans.',
    de: 'Unsere Grundlage ist einfach: ein freundlicher, einladender Ort zum Spielen. Kein Elitedenken, keine Toxizität - nur gute Leute, faire Spiele und Server, die seit weit über einem Jahrzehnt online, gepflegt und beliebt sind.',
  },
  'Friendly First': {
    lt: 'Draugiškumas pirmiausia',
    ru: 'Дружелюбие прежде всего',
    fr: 'Accueil avant tout',
    de: 'Freundlich zuerst',
  },
  'A welcoming home for veterans and newcomers alike.': {
    lt: 'Svetingi namai ir veteranams, ir naujokams.',
    ru: 'Приветливый дом и для ветеранов, и для новичков.',
    fr: 'Un foyer accueillant pour les vétérans comme pour les nouveaux.',
    de: 'Ein einladendes Zuhause für Veteranen und Neulinge.',
  },
  'Fair & Clean': {
    lt: 'Sąžininga ir švaru',
    ru: 'Честно и чисто',
    fr: 'Juste et propre',
    de: 'Fair und sauber',
  },
  'Well-moderated servers where everyone gets a fair game.': {
    lt: 'Gerai moderuojami serveriai, kuriuose visi gauna sąžiningą žaidimą.',
    ru: 'Хорошо модерируемые серверы, где каждый получает честную игру.',
    fr: 'Des serveurs bien modérés où chacun joue équitablement.',
    de: 'Gut moderierte Server, auf denen jeder ein faires Spiel bekommt.',
  },
  'Built to Last': {
    lt: 'Sukurta ilgam',
    ru: 'Создано надолго',
    fr: 'Construit pour durer',
    de: 'Für Dauer gebaut',
  },
  'Continuously maintained and improved since 2010.': {
    lt: 'Nuolat prižiūrima ir tobulinama nuo 2010 m.',
    ru: 'Постоянно поддерживается и улучшается с 2010 года.',
    fr: 'Maintenu et amélioré en continu depuis 2010.',
    de: 'Seit 2010 kontinuierlich gepflegt und verbessert.',
  },
  'Join Our Community': {
    lt: 'Prisijunk prie bendruomenės',
    ru: 'Присоединяйся к сообществу',
    fr: 'Rejoins notre communauté',
    de: 'Tritt unserer Community bei',
  },
  'Chat with players, get server updates, find teammates, and connect with admins': {
    lt: 'Kalbėkis su žaidėjais, gauk serverio naujienas, rask komandos draugų ir susisiek su adminais',
    ru: 'Общайся с игроками, получай новости серверов, находи напарников и связывайся с админами',
    fr: 'Discute avec les joueurs, reçois les mises à jour serveur, trouve des coéquipiers et contacte les admins',
    de: 'Chatte mit Spielern, erhalte Server-Updates, finde Mitspieler und erreiche Admins',
  },
  'Active Community': {
    lt: 'Aktyvi bendruomenė',
    ru: 'Активное сообщество',
    fr: 'Communauté active',
    de: 'Aktive Community',
  },
  'Server Announcements': {
    lt: 'Serverio pranešimai',
    ru: 'Объявления сервера',
    fr: 'Annonces serveur',
    de: 'Server-Ankündigungen',
  },
  'Admin Support': {
    lt: 'Adminų pagalba',
    ru: 'Поддержка админов',
    fr: 'Support admin',
    de: 'Admin-Support',
  },
  'Event Updates': {
    lt: 'Renginių naujienos',
    ru: 'Новости событий',
    fr: 'Mises à jour événements',
    de: 'Event-Updates',
  },
  'The people': { lt: 'Žmonės', ru: 'Люди', fr: 'Les personnes', de: 'Die Menschen' },
  'Meet the Team': {
    lt: 'Susipažink su komanda',
    ru: 'Познакомься с командой',
    fr: "L'équipe",
    de: 'Das Team',
  },
  'The crew that keeps Uliunai running across every game - from the core team to the admins and moderators who look after the community day to day.': {
    lt: 'Komanda, kuri palaiko Uliunai visuose žaidimuose - nuo pagrindinės komandos iki adminų ir moderatorių, kasdien prižiūrinčių bendruomenę.',
    ru: 'Команда, которая поддерживает Uliunai во всех играх - от основного состава до админов и модераторов, ежедневно заботящихся о сообществе.',
    fr: 'L’équipe qui fait tourner Uliunai sur tous les jeux - du noyau principal aux admins et modérateurs qui veillent sur la communauté au quotidien.',
    de: 'Das Team, das Uliunai über alle Spiele hinweg am Laufen hält - vom Kernteam bis zu Admins und Moderatoren, die sich täglich um die Community kümmern.',
  },
  Administrators: {
    lt: 'Administratoriai',
    ru: 'Администраторы',
    fr: 'Administrateurs',
    de: 'Administratoren',
  },
  Moderators: { lt: 'Moderatoriai', ru: 'Модераторы', fr: 'Modérateurs', de: 'Moderatoren' },
  Specialties: { lt: 'Specializacijos', ru: 'Специализации', fr: 'Spécialités', de: 'Spezialgebiete' },
  Owner: { lt: 'Savininkas', ru: 'Владелец', fr: 'Propriétaire', de: 'Owner' },
  'Core Team': { lt: 'Pagrindinė komanda', ru: 'Основная команда', fr: 'Équipe centrale', de: 'Kernteam' },
  Admin: { lt: 'Adminas', ru: 'Админ', fr: 'Admin', de: 'Admin' },
  Mod: { lt: 'Moderatorius', ru: 'Модератор', fr: 'Modo', de: 'Mod' },
  'Owner & Community Lead': {
    lt: 'Savininkas ir bendruomenės vadovas',
    ru: 'Владелец и лидер сообщества',
    fr: 'Propriétaire et responsable communauté',
    de: 'Owner und Community Lead',
  },
  'Platform Engineer': {
    lt: 'Platformos inžinierius',
    ru: 'Инженер платформы',
    fr: 'Ingénieur plateforme',
    de: 'Plattform-Ingenieur',
  },
  'Game Developer': {
    lt: 'Žaidimo kūrėjas',
    ru: 'Разработчик игры',
    fr: 'Développeur jeu',
    de: 'Game Developer',
  },
  'Discord Administrator': {
    lt: 'Discord administratorius',
    ru: 'Администратор Discord',
    fr: 'Administrateur Discord',
    de: 'Discord-Administrator',
  },
  Administrator: { lt: 'Administratorius', ru: 'Администратор', fr: 'Administrateur', de: 'Administrator' },
  Moderator: { lt: 'Moderatorius', ru: 'Модератор', fr: 'Modérateur', de: 'Moderator' },
  'Leads the Uliunai.com community. Responsible for community moderation and ensuring a welcoming environment for all players.': {
    lt: 'Vadovauja Uliunai.com bendruomenei. Atsakingas už moderavimą ir draugišką aplinką visiems žaidėjams.',
    ru: 'Руководит сообществом Uliunai.com. Отвечает за модерацию и дружелюбную атмосферу для всех игроков.',
    fr: 'Dirige la communauté Uliunai.com. Responsable de la modération et d’un environnement accueillant pour tous les joueurs.',
    de: 'Leitet die Uliunai.com Community. Verantwortlich für Moderation und eine einladende Umgebung für alle Spieler.',
  },
  'Built the Uliunai.com platform from the ground up. Handles the website, admin tools, server infrastructure, and deployment pipeline.': {
    lt: 'Sukūrė Uliunai.com platformą nuo nulio. Prižiūri svetainę, admino įrankius, serverių infrastruktūrą ir diegimo eigą.',
    ru: 'Создал платформу Uliunai.com с нуля. Ведет сайт, админ-инструменты, серверную инфраструктуру и деплой.',
    fr: 'A construit la plateforme Uliunai.com de zéro. Gère le site, les outils admin, l’infrastructure serveur et le déploiement.',
    de: 'Hat die Uliunai.com Plattform von Grund auf gebaut. Betreut Website, Admin-Tools, Server-Infrastruktur und Deployment.',
  },
  'The mind behind the gameplay. Develops custom mutators, perks, and weapons, and fine-tunes game balance to keep every match fresh and challenging.': {
    lt: 'Žmogus už gameplay. Kuria pasirinktinius mutatorius, perkus ir ginklus bei derina balansą, kad kiekviena partija liktų įdomi.',
    ru: 'Отвечает за геймплей. Разрабатывает кастомные мутаторы, перки и оружие, настраивает баланс, чтобы матчи оставались свежими.',
    fr: 'Le cerveau du gameplay. Développe mutators, perks et armes personnalisés, puis ajuste l’équilibrage pour garder chaque partie vivante.',
    de: 'Der Kopf hinter dem Gameplay. Entwickelt Mutators, Perks und Waffen und stimmt die Balance ab, damit jedes Match frisch bleibt.',
  },
  'Keeps the Discord running and supports the community day to day.': {
    lt: 'Prižiūri Discord ir kasdien padeda bendruomenei.',
    ru: 'Поддерживает Discord и ежедневно помогает сообществу.',
    fr: 'Fait tourner le Discord et aide la communauté au quotidien.',
    de: 'Hält Discord am Laufen und unterstützt die Community im Alltag.',
  },
  'Part of the admin team.': {
    lt: 'Adminų komandos narys.',
    ru: 'Часть команды администраторов.',
    fr: 'Membre de l’équipe admin.',
    de: 'Teil des Admin-Teams.',
  },
  'Community Moderation': { lt: 'Bendruomenės moderavimas', ru: 'Модерация сообщества', fr: 'Modération communauté', de: 'Community-Moderation' },
  'Player Support': { lt: 'Žaidėjų pagalba', ru: 'Поддержка игроков', fr: 'Support joueurs', de: 'Spieler-Support' },
  'Web Development': { lt: 'Web kūrimas', ru: 'Веб-разработка', fr: 'Développement web', de: 'Webentwicklung' },
  'Platform Design': { lt: 'Platformos dizainas', ru: 'Дизайн платформы', fr: 'Design plateforme', de: 'Plattformdesign' },
  'Server Infrastructure': { lt: 'Serverių infrastruktūra', ru: 'Серверная инфраструктура', fr: 'Infrastructure serveur', de: 'Server-Infrastruktur' },
  'Game Balancing': { lt: 'Žaidimo balansas', ru: 'Баланс игры', fr: 'Équilibrage jeu', de: 'Game Balancing' },
  'Mutator Development': { lt: 'Mutatorių kūrimas', ru: 'Разработка мутаторов', fr: 'Développement de mutators', de: 'Mutator-Entwicklung' },
  'Custom Content': { lt: 'Pasirinktinis turinys', ru: 'Кастомный контент', fr: 'Contenu personnalisé', de: 'Custom Content' },
  'Be Respectful': { lt: 'Būk pagarbus', ru: 'Будь уважителен', fr: 'Sois respectueux', de: 'Sei respektvoll' },
  'No insulting, provoking, harassment or discrimination.': {
    lt: 'Jokių įžeidinėjimų, provokacijų, priekabiavimo ar diskriminacijos.',
    ru: 'Без оскорблений, провокаций, домогательств и дискриминации.',
    fr: 'Pas d’insultes, provocations, harcèlement ou discrimination.',
    de: 'Keine Beleidigungen, Provokationen, Belästigung oder Diskriminierung.',
  },
  'No cheating, exploits, or third-party software.': {
    lt: 'Jokių cheatų, exploitų ar trečiųjų šalių programų.',
    ru: 'Без читов, эксплойтов и стороннего ПО.',
    fr: 'Pas de triche, exploits ou logiciels tiers.',
    de: 'Keine Cheats, Exploits oder Drittsoftware.',
  },
  'Keep Chat Clean': { lt: 'Laikyk pokalbį tvarkingą', ru: 'Держи чат чистым', fr: 'Garde le chat propre', de: 'Chat sauber halten' },
  'No spam, flooding, impersonation, or advertising.': {
    lt: 'Jokio šlamšto, floodo, apsimetinėjimo ar reklamos.',
    ru: 'Без спама, флуда, выдачи себя за других и рекламы.',
    fr: 'Pas de spam, flood, usurpation ou publicité.',
    de: 'Kein Spam, Flooding, Imitieren oder Werbung.',
  },
  'Accounts & Security': { lt: 'Paskyros ir saugumas', ru: 'Аккаунты и безопасность', fr: 'Comptes et sécurité', de: 'Accounts und Sicherheit' },
  'One account per player, kept secure and yours alone.': {
    lt: 'Viena paskyra žaidėjui, saugi ir tik tavo.',
    ru: 'Один аккаунт на игрока, безопасный и только твой.',
    fr: 'Un compte par joueur, sécurisé et personnel.',
    de: 'Ein Account pro Spieler, sicher und nur deiner.',
  },
  'Staff & Enforcement': { lt: 'Komanda ir taisyklių taikymas', ru: 'Персонал и меры', fr: 'Staff et application', de: 'Team und Durchsetzung' },
  'Staff keep things fair - follow their calls and appeal calmly.': {
    lt: 'Komanda palaiko sąžiningumą - vykdyk nurodymus ir apeliuok ramiai.',
    ru: 'Персонал поддерживает честность - выполняй решения и подавай апелляции спокойно.',
    fr: 'Le staff garde le jeu équitable - suis ses décisions et fais appel calmement.',
    de: 'Das Team hält es fair - folge Entscheidungen und lege ruhig Einspruch ein.',
  },
  'Reporting & Appeals': { lt: 'Pranešimai ir apeliacijos', ru: 'Жалобы и апелляции', fr: 'Signalements et appels', de: 'Meldungen und Einsprüche' },
  "See a problem? Report it - don't take matters into your own hands.": {
    lt: 'Matai problemą? Pranešk - nespręsk savarankiškai.',
    ru: 'Видишь проблему? Сообщи - не бери правосудие в свои руки.',
    fr: 'Tu vois un problème ? Signale-le - ne règle pas ça toi-même.',
    de: 'Siehst du ein Problem? Melde es - nimm es nicht selbst in die Hand.',
  },
  "Treat everyone the way you'd want to be treated - veterans and newcomers alike.": {
    lt: 'Elkis su visais taip, kaip norėtum, kad elgtųsi su tavimi - tiek su veteranais, tiek su naujokais.',
    ru: 'Относись ко всем так, как хотел бы, чтобы относились к тебе - и к ветеранам, и к новичкам.',
    fr: 'Traite tout le monde comme tu voudrais être traité - vétérans comme nouveaux venus.',
    de: 'Behandle alle so, wie du selbst behandelt werden willst - Veteranen und Neulinge gleichermaßen.',
  },
  'No harassment, hate speech, racism, sexism, or discrimination of any kind.': {
    lt: 'Jokio priekabiavimo, neapykantos kalbos, rasizmo, seksizmo ar bet kokios diskriminacijos.',
    ru: 'Никаких домогательств, языка ненависти, расизма, сексизма или дискриминации.',
    fr: 'Aucun harcèlement, discours haineux, racisme, sexisme ou discrimination.',
    de: 'Keine Belästigung, Hassrede, Rassismus, Sexismus oder Diskriminierung.',
  },
  'No personal attacks, insulting, or deliberately provoking and starting drama.': {
    lt: 'Jokių asmeninių išpuolių, įžeidinėjimų ar sąmoningo provokavimo ir dramų kūrimo.',
    ru: 'Без личных нападок, оскорблений, намеренных провокаций и драм.',
    fr: 'Pas d’attaques personnelles, insultes ou provocations pour créer du drama.',
    de: 'Keine persönlichen Angriffe, Beleidigungen oder absichtliche Provokationen.',
  },
  'No witch-hunting or rallying the community against another member.': {
    lt: 'Jokio persekiojimo ar bendruomenės kurstymo prieš kitą narį.',
    ru: 'Без травли и натравливания сообщества на другого участника.',
    fr: 'Pas de chasse aux sorcières ni de mobilisation contre un membre.',
    de: 'Keine Hetzjagden oder Mobilisierung gegen andere Mitglieder.',
  },
  "Respect privacy - never share anyone's personal information (doxxing).": {
    lt: 'Gerbk privatumą - niekada nesidalink kito asmens informacija (doxxing).',
    ru: 'Уважай приватность - никогда не публикуй личные данные других людей (doxxing).',
    fr: 'Respecte la vie privée - ne partage jamais les informations personnelles de quelqu’un.',
    de: 'Respektiere Privatsphäre - teile niemals persönliche Daten anderer.',
  },
  'Keep it family-friendly: no NSFW, gore, shock, or illegal content.': {
    lt: 'Laikyk turinį tinkamą visiems: jokio NSFW, gore, šoko ar nelegalaus turinio.',
    ru: 'Держи контент безопасным: без NSFW, gore, шок-контента и незаконных материалов.',
    fr: 'Garde un contenu propre : pas de NSFW, gore, choc ou contenu illégal.',
    de: 'Halte es sauber: kein NSFW, Gore, Schock- oder illegaler Inhalt.',
  },
  'No cheats, hacks, macros, or third-party software that give an unfair advantage.': {
    lt: 'Jokių cheatų, hackų, makrokomandų ar programų, suteikiančių nesąžiningą pranašumą.',
    ru: 'Без читов, хаков, макросов или стороннего ПО, дающего нечестное преимущество.',
    fr: 'Pas de cheats, hacks, macros ou logiciels tiers donnant un avantage injuste.',
    de: 'Keine Cheats, Hacks, Makros oder Drittsoftware mit unfairen Vorteilen.',
  },
  'No exploiting bugs, glitches, or unintended mechanics - report them to staff instead.': {
    lt: 'Nenaudok klaidų, glitchų ar nenumatytų mechanikų - vietoj to pranešk komandai.',
    ru: 'Не используй баги, глитчи или непредвиденные механики - сообщай персоналу.',
    fr: 'N’exploite pas bugs, glitches ou mécaniques non prévues - signale-les au staff.',
    de: 'Nutze keine Bugs, Glitches oder unbeabsichtigten Mechaniken - melde sie dem Team.',
  },
  'No boosting, win-trading, or manipulating stats and leaderboards.': {
    lt: 'Jokio boostinimo, pergalių mainų ar statistikos ir lentelių manipuliavimo.',
    ru: 'Без бустинга, обмена победами и манипуляций статистикой или лидербордами.',
    fr: 'Pas de boosting, win-trading ou manipulation de stats et classements.',
    de: 'Kein Boosting, Win-Trading oder Manipulieren von Stats und Leaderboards.',
  },
  'Play legit and earn your progress.': {
    lt: 'Žaisk sąžiningai ir užsidirbk progresą.',
    ru: 'Играй честно и зарабатывай прогресс.',
    fr: 'Joue légit et gagne ta progression.',
    de: 'Spiel fair und verdiene deinen Fortschritt.',
  },
  'No spamming, flooding, excessive caps, or chain messages in text or voice.': {
    lt: 'Jokio spamo, floodo, perteklinių DIDŽIŲJŲ raidžių ar grandininių žinučių.',
    ru: 'Без спама, флуда, капса и цепных сообщений в тексте или голосе.',
    fr: 'Pas de spam, flood, caps excessifs ou chaînes en texte ou vocal.',
    de: 'Kein Spam, Flooding, übermäßige Caps oder Kettennachrichten.',
  },
  'No advertising other servers, communities, products, or services without permission.': {
    lt: 'Nereklamuok kitų serverių, bendruomenių, produktų ar paslaugų be leidimo.',
    ru: 'Не рекламируй другие серверы, сообщества, продукты или услуги без разрешения.',
    fr: 'Pas de publicité pour d’autres serveurs, communautés, produits ou services sans permission.',
    de: 'Keine Werbung für andere Server, Communities, Produkte oder Dienste ohne Erlaubnis.',
  },
  'No mic spam, soundboards, or voice changers used to annoy others.': {
    lt: 'Jokio mikrofono spamo, soundboardų ar balso keitiklių kitų erzinimui.',
    ru: 'Без микрофон-спама, саундбордов и изменителей голоса для раздражения других.',
    fr: 'Pas de mic spam, soundboards ou modificateurs de voix pour gêner les autres.',
    de: 'Kein Mic-Spam, Soundboards oder Voice-Changer zum Stören anderer.',
  },
  'Keep channels on-topic and use the right channel for the right thing.': {
    lt: 'Laikykis kanalų temos ir naudok tinkamą kanalą tinkamam reikalui.',
    ru: 'Пиши по теме каналов и используй подходящий канал.',
    fr: 'Reste dans le sujet des salons et utilise le bon salon.',
    de: 'Bleib in Kanälen beim Thema und nutze den passenden Kanal.',
  },
  'Use English in shared/global channels so staff can moderate fairly.': {
    lt: 'Bendruose/globaliuose kanaluose naudok anglų kalbą, kad komanda galėtų sąžiningai moderuoti.',
    ru: 'Используй английский в общих каналах, чтобы персонал мог честно модерировать.',
    fr: 'Utilise l’anglais dans les salons globaux pour permettre une modération équitable.',
    de: 'Nutze Englisch in gemeinsamen/globalen Kanälen, damit das Team fair moderieren kann.',
  },
  'One account per person - alternate accounts need staff approval.': {
    lt: 'Viena paskyra žmogui - alternatyvioms paskyroms reikia komandos leidimo.',
    ru: 'Один аккаунт на человека - альтернативные аккаунты требуют одобрения персонала.',
    fr: 'Un compte par personne - les comptes alternatifs nécessitent l’accord du staff.',
    de: 'Ein Account pro Person - alternative Accounts brauchen Team-Freigabe.',
  },
  "You are responsible for everything done on your account; don't share it.": {
    lt: 'Esi atsakingas už viską, kas vyksta tavo paskyroje; nesidalink ja.',
    ru: 'Ты отвечаешь за все действия на своем аккаунте; не передавай его другим.',
    fr: 'Tu es responsable de tout ce qui est fait sur ton compte ; ne le partage pas.',
    de: 'Du bist für alles auf deinem Account verantwortlich; teile ihn nicht.',
  },
  'No impersonating staff, other members, or anyone else.': {
    lt: 'Neapsimesk komandos nariu, kitu nariu ar bet kuo kitu.',
    ru: 'Не выдавай себя за персонал, других участников или кого-либо еще.',
    fr: 'N’usurpe pas l’identité du staff, de membres ou de quelqu’un d’autre.',
    de: 'Gib dich nicht als Teammitglied, anderes Mitglied oder jemand anderes aus.',
  },
  'No real-money trading - buying or selling accounts, ranks, or in-game items for cash.': {
    lt: 'Jokios prekybos už realius pinigus - paskyrų, rangų ar žaidimo daiktų pirkimo/pardavimo.',
    ru: 'Без торговли за реальные деньги - покупки или продажи аккаунтов, рангов и игровых предметов.',
    fr: 'Pas d’échanges contre argent réel - comptes, rangs ou objets en jeu.',
    de: 'Kein Echtgeldhandel - Accounts, Ränge oder Ingame-Items kaufen/verkaufen.',
  },
  "Follow staff instructions; they're here to keep the community fair and fun.": {
    lt: 'Vykdyk komandos nurodymus; jie palaiko bendruomenę sąžiningą ir smagią.',
    ru: 'Следуй указаниям персонала; они поддерживают честность и комфорт сообщества.',
    fr: 'Suis les instructions du staff ; il garde la communauté juste et agréable.',
    de: 'Folge Team-Anweisungen; sie halten die Community fair und angenehm.',
  },
  "Don't argue with or backseat-moderate staff in public - raise concerns politely.": {
    lt: 'Nesiginčyk su komanda viešai ir nemoderuok iš šalies - išsakyk rūpesčius mandagiai.',
    ru: 'Не спорь с персоналом публично и не модерируй за них - поднимай вопросы вежливо.',
    fr: 'Ne contredis pas le staff en public et ne modère pas à sa place - exprime tes soucis poliment.',
    de: 'Streite nicht öffentlich mit dem Team und moderiere nicht von außen - sprich Anliegen höflich an.',
  },
  'Punishments scale with severity: warning - mute - kick - temporary ban - permanent ban.': {
    lt: 'Bausmės priklauso nuo rimtumo: įspėjimas - mute - kick - laikinas banas - nuolatinis banas.',
    ru: 'Наказания зависят от тяжести: предупреждение - мут - кик - временный бан - постоянный бан.',
    fr: 'Les sanctions suivent la gravité : avertissement - mute - kick - ban temporaire - ban permanent.',
    de: 'Strafen skalieren nach Schwere: Warnung - Mute - Kick - temporärer Ban - permanenter Ban.',
  },
  'Ban evasion (using alts to dodge a punishment) resets and extends it.': {
    lt: 'Bano apėjimas (altų naudojimas bausmei vengti) ją atnaujina ir pratęsia.',
    ru: 'Обход бана через альты сбрасывает и продлевает наказание.',
    fr: 'Contourner une sanction avec des alts la réinitialise et l’allonge.',
    de: 'Ban-Umgehung mit Alts setzt die Strafe zurück und verlängert sie.',
  },
  'Staff have the final say on enforcement; honest mistakes are handled fairly.': {
    lt: 'Komanda turi galutinį žodį; sąžiningos klaidos sprendžiamos teisingai.',
    ru: 'Персонал имеет последнее слово; честные ошибки рассматриваются справедливо.',
    fr: 'Le staff a le dernier mot ; les erreurs honnêtes sont traitées équitablement.',
    de: 'Das Team hat das letzte Wort; ehrliche Fehler werden fair behandelt.',
  },
  'Report rule-breakers through a Discord support ticket or by DMing an admin.': {
    lt: 'Apie pažeidėjus pranešk per Discord support ticket arba parašydamas adminui.',
    ru: 'Сообщай о нарушителях через Discord ticket или личное сообщение админу.',
    fr: 'Signale les infractions via un ticket Discord ou en DM à un admin.',
    de: 'Melde Regelbrecher per Discord-Support-Ticket oder DM an einen Admin.',
  },
  "Don't retaliate or take justice into your own hands - let staff handle it.": {
    lt: 'Neatsakyk tuo pačiu ir nespręsk pats - leisk komandai tvarkyti situaciją.',
    ru: 'Не мсти и не бери правосудие в свои руки - пусть персонал решит ситуацию.',
    fr: 'Ne riposte pas et ne fais pas justice toi-même - laisse le staff gérer.',
    de: 'Vergelte nicht und nimm es nicht selbst in die Hand - lass das Team handeln.',
  },
  'Appeal bans or mutes through Discord tickets, not public chat.': {
    lt: 'Bano ar mute apeliacijas teik per Discord ticketus, ne viešame chate.',
    ru: 'Апеллируй баны и мюты через Discord tickets, не в публичном чате.',
    fr: 'Fais appel des bans ou mutes via les tickets Discord, pas en chat public.',
    de: 'Lege Einspruch gegen Bans oder Mutes per Discord-Ticket ein, nicht öffentlich.',
  },
  'Include evidence (screenshots or clips) where you can - it helps staff act quickly.': {
    lt: 'Jei gali, pridėk įrodymų (screenshotų ar klipų) - tai padeda komandai greičiau veikti.',
    ru: 'Прикладывай доказательства (скриншоты или клипы), если можешь - это помогает персоналу быстрее реагировать.',
    fr: 'Ajoute des preuves (captures ou clips) quand possible - cela aide le staff à agir vite.',
    de: 'Füge Beweise hinzu (Screenshots oder Clips), wenn möglich - das hilft dem Team schnell zu handeln.',
  },
  'The ground rules': {
    lt: 'Pagrindinės taisyklės',
    ru: 'Основные правила',
    fr: 'Les règles de base',
    de: 'Grundregeln',
  },
  'Code of Conduct': {
    lt: 'Elgesio kodeksas',
    ru: 'Кодекс поведения',
    fr: 'Code de conduite',
    de: 'Verhaltenskodex',
  },
  "A shared code of conduct that applies across every Uliunai server. Each game also has its own game-specific rules - check the rules page on that game's site.": {
    lt: 'Bendras elgesio kodeksas galioja visuose Uliunai serveriuose. Kiekvienas žaidimas taip pat turi savo taisykles - jas rasi to žaidimo svetainės taisyklių puslapyje.',
    ru: 'Общий кодекс поведения действует на всех серверах Uliunai. У каждой игры также есть свои правила - смотри страницу правил на сайте этой игры.',
    fr: 'Un code de conduite partagé s’applique à tous les serveurs Uliunai. Chaque jeu a aussi ses règles spécifiques - consulte la page des règles sur le site du jeu.',
    de: 'Ein gemeinsamer Verhaltenskodex gilt auf allen Uliunai Servern. Jedes Spiel hat zusätzlich eigene Regeln - sieh auf der Regel-Seite der jeweiligen Website nach.',
  },
  'Read the full Code of Conduct': {
    lt: 'Skaityti visą elgesio kodeksą',
    ru: 'Читать весь кодекс',
    fr: 'Lire tout le code de conduite',
    de: 'Vollständigen Kodex lesen',
  },
  'Latest updates': {
    lt: 'Naujausi atnaujinimai',
    ru: 'Последние обновления',
    fr: 'Dernières mises à jour',
    de: 'Neueste Updates',
  },
  'Community News': {
    lt: 'Bendruomenės naujienos',
    ru: 'Новости сообщества',
    fr: 'Actualités communauté',
    de: 'Community-News',
  },
  'Latest News': {
    lt: 'Naujausios naujienos',
    ru: 'Последние новости',
    fr: 'Dernières nouvelles',
    de: 'Neueste News',
  },
  Featured: { lt: 'Išskirta', ru: 'Важно', fr: 'À la une', de: 'Empfohlen' },
  Pinned: { lt: 'Prisegta', ru: 'Закреплено', fr: 'Épinglé', de: 'Angeheftet' },
  'Read full update': {
    lt: 'Skaityti visą atnaujinimą',
    ru: 'Читать полное обновление',
    fr: 'Lire la mise à jour',
    de: 'Ganzes Update lesen',
  },
  'Read more': { lt: 'Skaityti daugiau', ru: 'Читать дальше', fr: 'Lire plus', de: 'Mehr lesen' },
  'View all news': {
    lt: 'Visos naujienos',
    ru: 'Все новости',
    fr: 'Toutes les actualités',
    de: 'Alle News ansehen',
  },
  'No news yet': {
    lt: 'Naujienų dar nėra',
    ru: 'Новостей пока нет',
    fr: 'Pas encore de nouvelles',
    de: 'Noch keine News',
  },
  "Couldn't load the news feed": {
    lt: 'Nepavyko įkelti naujienų',
    ru: 'Не удалось загрузить новости',
    fr: "Impossible de charger l'actualité",
    de: 'News konnten nicht geladen werden',
  },
  'Please try again in a little while.': {
    lt: 'Bandykite dar kartą vėliau.',
    ru: 'Попробуйте позже.',
    fr: 'Réessayez dans un moment.',
    de: 'Bitte versuche es später erneut.',
  },
  'Back to news': {
    lt: 'Atgal į naujienas',
    ru: 'Назад к новостям',
    fr: 'Retour aux actualités',
    de: 'Zurück zu den News',
  },
  'All news': { lt: 'Visos naujienos', ru: 'Все новости', fr: 'Toutes les actualités', de: 'Alle News' },
  'Post not found': {
    lt: 'Įrašas nerastas',
    ru: 'Запись не найдена',
    fr: 'Article introuvable',
    de: 'Beitrag nicht gefunden',
  },
  'No further details.': {
    lt: 'Daugiau detalių nėra.',
    ru: 'Подробностей нет.',
    fr: 'Aucun détail supplémentaire.',
    de: 'Keine weiteren Details.',
  },
  'Source / read more': {
    lt: 'Šaltinis / daugiau',
    ru: 'Источник / подробнее',
    fr: 'Source / lire plus',
    de: 'Quelle / mehr lesen',
  },
  'No further details for this post.': {
    lt: 'Daugiau detalių šiam įrašui nėra.',
    ru: 'Для этой записи больше нет подробностей.',
    fr: 'Aucun détail supplémentaire pour cet article.',
    de: 'Keine weiteren Details zu diesem Beitrag.',
  },
  'Previous featured post': {
    lt: 'Ankstesnis išskirtas įrašas',
    ru: 'Предыдущая важная запись',
    fr: 'Article précédent',
    de: 'Vorheriger Beitrag',
  },
  'Next featured post': {
    lt: 'Kitas išskirtas įrašas',
    ru: 'Следующая важная запись',
    fr: 'Article suivant',
    de: 'Nächster Beitrag',
  },
  'General Inquiry': {
    lt: 'Bendra užklausa',
    ru: 'Общий вопрос',
    fr: 'Question générale',
    de: 'Allgemeine Anfrage',
  },
  'Technical Support': {
    lt: 'Techninė pagalba',
    ru: 'Техподдержка',
    fr: 'Support technique',
    de: 'Technischer Support',
  },
  'Community / Discord': {
    lt: 'Bendruomenė / Discord',
    ru: 'Сообщество / Discord',
    fr: 'Communauté / Discord',
    de: 'Community / Discord',
  },
  Suggestion: { lt: 'Pasiūlymas', ru: 'Предложение', fr: 'Suggestion', de: 'Vorschlag' },
  Other: { lt: 'Kita', ru: 'Другое', fr: 'Autre', de: 'Sonstiges' },
  Name: { lt: 'Vardas', ru: 'Имя', fr: 'Nom', de: 'Name' },
  Email: { lt: 'El. paštas', ru: 'Email', fr: 'E-mail', de: 'E-Mail' },
  Subject: { lt: 'Tema', ru: 'Тема', fr: 'Sujet', de: 'Betreff' },
  Message: { lt: 'Žinutė', ru: 'Сообщение', fr: 'Message', de: 'Nachricht' },
  'Send Message': {
    lt: 'Siųsti žinutę',
    ru: 'Отправить',
    fr: 'Envoyer',
    de: 'Nachricht senden',
  },
  Sending: { lt: 'Siunčiama', ru: 'Отправка', fr: 'Envoi', de: 'Wird gesendet' },
  'Message sent successfully.': {
    lt: 'Žinutė sėkmingai išsiųsta.',
    ru: 'Сообщение отправлено.',
    fr: 'Message envoyé.',
    de: 'Nachricht gesendet.',
  },
  'Something went wrong. Please try again.': {
    lt: 'Kažkas nepavyko. Bandykite dar kartą.',
    ru: 'Что-то пошло не так. Попробуйте снова.',
    fr: 'Une erreur est survenue. Réessayez.',
    de: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
  },
  'Privacy Policy': {
    lt: 'Privatumo politika',
    ru: 'Политика конфиденциальности',
    fr: 'Politique de confidentialité',
    de: 'Datenschutz',
  },
  'Terms of Service': {
    lt: 'Paslaugų sąlygos',
    ru: 'Условия использования',
    fr: "Conditions d'utilisation",
    de: 'Nutzungsbedingungen',
  },
  'Quick Links': {
    lt: 'Greitos nuorodos',
    ru: 'Быстрые ссылки',
    fr: 'Liens rapides',
    de: 'Schnelllinks',
  },
  'Server IP:': { lt: 'Serverio IP:', ru: 'IP сервера:', fr: 'IP serveur :', de: 'Server-IP:' },
  'Server IP': { lt: 'Serverio IP', ru: 'IP сервера', fr: 'IP serveur', de: 'Server-IP' },
  'The longest-running Killing Floor 1 server with custom content, active community, and years of reliable service. Join thousands of players in the ultimate zombie survival experience.': {
    lt: 'Ilgiausiai veikiantis Killing Floor 1 serveris su pasirinktiniu turiniu, aktyvia bendruomene ir patikimu veikimu daugelį metų. Prisijunk prie tūkstančių žaidėjų galutinėje zombių išgyvenimo patirtyje.',
    ru: 'Самый долго работающий сервер Killing Floor 1 с кастомным контентом, активным сообществом и многими годами надежной работы. Присоединяйся к тысячам игроков в настоящем zombie survival.',
    fr: 'Le serveur Killing Floor 1 le plus ancien avec contenu personnalisé, communauté active et des années de service fiable. Rejoins des milliers de joueurs dans l’expérience zombie survival ultime.',
    de: 'Der am längsten laufende Killing Floor 1 Server mit Custom Content, aktiver Community und Jahren zuverlässigem Betrieb. Tritt tausenden Spielern im ultimativen Zombie-Survival bei.',
  },
  'Not affiliated with Tripwire Interactive.': {
    lt: 'Nesusiję su Tripwire Interactive.',
    ru: 'Не связано с Tripwire Interactive.',
    fr: 'Non affilié à Tripwire Interactive.',
    de: 'Nicht mit Tripwire Interactive verbunden.',
  },
  Players: { lt: 'Žaidėjai', ru: 'Игроки', fr: 'Joueurs', de: 'Spieler' },
  'Current Map': {
    lt: 'Dabartinis žemėlapis',
    ru: 'Текущая карта',
    fr: 'Carte actuelle',
    de: 'Aktuelle Karte',
  },
  Difficulty: { lt: 'Sunkumas', ru: 'Сложность', fr: 'Difficulté', de: 'Schwierigkeit' },
  'Server Live': { lt: 'Serveris veikia', ru: 'Сервер онлайн', fr: 'Serveur en ligne', de: 'Server live' },
  'Server Offline': {
    lt: 'Serveris išjungtas',
    ru: 'Сервер офлайн',
    fr: 'Serveur hors ligne',
    de: 'Server offline',
  },
  'Server data unavailable': {
    lt: 'Serverio duomenys nepasiekiami',
    ru: 'Данные сервера недоступны',
    fr: 'Données serveur indisponibles',
    de: 'Serverdaten nicht verfügbar',
  },
  'Server offline': {
    lt: 'Serveris išjungtas',
    ru: 'Сервер офлайн',
    fr: 'Serveur hors ligne',
    de: 'Server offline',
  },
  'Server online - no players': {
    lt: 'Serveris veikia - žaidėjų nėra',
    ru: 'Сервер онлайн - игроков нет',
    fr: 'Serveur en ligne - aucun joueur',
    de: 'Server online - keine Spieler',
  },
  'player online': {
    lt: 'žaidėjas prisijungęs',
    ru: 'игрок онлайн',
    fr: 'joueur en ligne',
    de: 'Spieler online',
  },
  'players online': {
    lt: 'žaidėjai prisijungę',
    ru: 'игроков онлайн',
    fr: 'joueurs en ligne',
    de: 'Spieler online',
  },
  Wave: { lt: 'Banga', ru: 'Волна', fr: 'Vague', de: 'Welle' },
  Progress: { lt: 'progresas', ru: 'прогресс', fr: 'progression', de: 'Fortschritt' },
  'Live Scoreboard': {
    lt: 'Gyva rezultatų lentelė',
    ru: 'Таблица в реальном времени',
    fr: 'Scoreboard en direct',
    de: 'Live-Scoreboard',
  },
  'View All-Time Player Stats': {
    lt: 'Žiūrėti visą žaidėjų statistiką',
    ru: 'Вся статистика игроков',
    fr: 'Voir les stats globales',
    de: 'Alle Spieler-Stats ansehen',
  },
  'Server Features': {
    lt: 'Serverio funkcijos',
    ru: 'Возможности сервера',
    fr: 'Fonctions serveur',
    de: 'Server-Features',
  },
  'Custom Perks': {
    lt: 'Unikalūs perkai',
    ru: 'Пользовательские перки',
    fr: 'Atouts personnalisés',
    de: 'Eigene Perks',
  },
  'Server Rules': {
    lt: 'Serverio taisyklės',
    ru: 'Правила сервера',
    fr: 'Règles serveur',
    de: 'Serverregeln',
  },
  'How to Join': {
    lt: 'Kaip prisijungti',
    ru: 'Как подключиться',
    fr: 'Comment rejoindre',
    de: 'So trittst du bei',
  },
  'Player Stats': {
    lt: 'Žaidėjų statistika',
    ru: 'Статистика игроков',
    fr: 'Stats joueurs',
    de: 'Spieler-Stats',
  },
  'Ready to play?': {
    lt: 'Pasiruošęs žaisti?',
    ru: 'Готов играть?',
    fr: 'Prêt à jouer ?',
    de: 'Bereit zu spielen?',
  },
  'Our servers': {
    lt: 'Mūsų serveriai',
    ru: 'Наши серверы',
    fr: 'Nos serveurs',
    de: 'Unsere Server',
  },
  'Two worlds, one community': {
    lt: 'Du pasauliai, viena bendruomenė',
    ru: 'Два мира, одно сообщество',
    fr: 'Deux mondes, une communauté',
    de: 'Zwei Welten, eine Community',
  },
  'Vanilla Server': {
    lt: 'Vanilla serveris',
    ru: 'Vanilla сервер',
    fr: 'Serveur Vanilla',
    de: 'Vanilla-Server',
  },
  'Survival Server': {
    lt: 'Survival serveris',
    ru: 'Сервер Survival',
    fr: 'Serveur Survival',
    de: 'Survival Server',
  },
  'Modded Server': {
    lt: 'Modded serveris',
    ru: 'Modded сервер',
    fr: 'Serveur moddé',
    de: 'Modded-Server',
  },
  'Questions, reports, modpack issues or suggestions. Send it here and it reaches the Uliunai team.': {
    lt: 'Klausimai, pranešimai, modpack problemos ar pasiūlymai. Išsiųsk čia ir tai pasieks Uliunai komandą.',
    ru: 'Вопросы, жалобы, проблемы modpack или предложения. Отправь здесь, и это попадет команде Uliunai.',
    fr: 'Questions, signalements, problèmes de modpack ou suggestions. Envoie ici, cela arrive à l’équipe Uliunai.',
    de: 'Fragen, Meldungen, Modpack-Probleme oder Vorschläge. Sende es hier, und es erreicht das Uliunai Team.',
  },
  'Fastest help for server issues and launch updates.': {
    lt: 'Greičiausia pagalba dėl serverio problemų ir starto naujienų.',
    ru: 'Самая быстрая помощь по проблемам сервера и новостям запуска.',
    fr: 'Aide la plus rapide pour soucis serveur et mises à jour de lancement.',
    de: 'Schnellste Hilfe für Serverprobleme und Launch-Updates.',
  },
  'Both servers are still launching soon.': {
    lt: 'Abu serveriai vis dar startuos netrukus.',
    ru: 'Оба сервера все еще готовятся к запуску.',
    fr: 'Les deux serveurs arrivent bientôt.',
    de: 'Beide Server starten bald.',
  },
  'Follow the wider Uliunai community.': {
    lt: 'Sek platesnę Uliunai bendruomenę.',
    ru: 'Следи за большим сообществом Uliunai.',
    fr: 'Suis la communauté Uliunai élargie.',
    de: 'Folge der größeren Uliunai Community.',
  },
  'Copy IP': { lt: 'Kopijuoti IP', ru: 'Скопировать IP', fr: 'Copier IP', de: 'IP kopieren' },
  Copied: { lt: 'Nukopijuota', ru: 'Скопировано', fr: 'Copié', de: 'Kopiert' },
  'Copy server address': {
    lt: 'Kopijuoti serverio adresą',
    ru: 'Скопировать адрес сервера',
    fr: "Copier l'adresse serveur",
    de: 'Serveradresse kopieren',
  },
  'Launching soon': {
    lt: 'Startuos netrukus',
    ru: 'Скоро запуск',
    fr: 'Lancement bientôt',
    de: 'Startet bald',
  },
  Online: { lt: 'Veikia', ru: 'Онлайн', fr: 'En ligne', de: 'Online' },
  Offline: { lt: 'Išjungta', ru: 'Офлайн', fr: 'Hors ligne', de: 'Offline' },
  'Offline - launching soon': {
    lt: 'Išjungta - startuos netrukus',
    ru: 'Офлайн - скоро запуск',
    fr: 'Hors ligne - lancement bientôt',
    de: 'Offline - startet bald',
  },
  Loading: { lt: 'Kraunama', ru: 'Загрузка', fr: 'Chargement', de: 'Lädt' },
  Latest: { lt: 'Naujausia', ru: 'Последние', fr: 'Dernier', de: 'Neueste' },
  Custom: { lt: 'Unikalūs', ru: 'Пользовательские', fr: 'Personnalisé', de: 'Eigene' },
  Features: { lt: 'Funkcijos', ru: 'Возможности', fr: 'Fonctions', de: 'Features' },
  'Coming Soon': { lt: 'Netrukus', ru: 'Скоро', fr: 'Bientôt', de: 'Bald' },
  'Back to Home': { lt: 'Atgal į pradžią', ru: 'Назад на главную', fr: "Retour à l'accueil", de: 'Zurück zur Startseite' },
  'Back to home': { lt: 'Atgal į pradžią', ru: 'Назад на главную', fr: "Retour à l'accueil", de: 'Zurück zur Startseite' },
  'View servers': { lt: 'Žiūrėti serverius', ru: 'Смотреть серверы', fr: 'Voir les serveurs', de: 'Server ansehen' },
  'Back to the hub': { lt: 'Atgal į hubą', ru: 'Назад в хаб', fr: 'Retour au hub', de: 'Zurück zum Hub' },
  'Back to all news': { lt: 'Atgal į visas naujienas', ru: 'Назад ко всем новостям', fr: 'Retour à toutes les actualités', de: 'Zurück zu allen News' },
  'About Our Community': { lt: 'Apie mūsų bendruomenę', ru: 'О нашем сообществе', fr: 'À propos de notre communauté', de: 'Über unsere Community' },
  'Meet Our Team': { lt: 'Susipažink su mūsų komanda', ru: 'Познакомься с нашей командой', fr: 'Rencontre notre équipe', de: 'Lerne unser Team kennen' },
  'Get In Touch': { lt: 'Susisiek', ru: 'Связаться', fr: 'Nous contacter', de: 'Kontakt aufnehmen' },
  'Get In': { lt: 'Susisiek', ru: 'Связаться', fr: 'Nous', de: 'Kontakt' },
  Touch: { lt: '', ru: '', fr: 'contacter', de: 'aufnehmen' },
  "Have questions, suggestions, or need support? We're here to help!": {
    lt: 'Turi klausimų, pasiūlymų ar reikia pagalbos? Mes padėsime!',
    ru: 'Есть вопросы, предложения или нужна помощь? Мы поможем!',
    fr: 'Des questions, suggestions ou besoin d’aide ? Nous sommes là !',
    de: 'Fragen, Vorschläge oder Support nötig? Wir helfen.',
  },
  'Message must be 500 characters or less': {
    lt: 'Žinutė turi būti 500 simbolių arba trumpesnė',
    ru: 'Сообщение должно быть не длиннее 500 символов',
    fr: 'Le message doit faire 500 caractères ou moins',
    de: 'Die Nachricht darf höchstens 500 Zeichen lang sein',
  },
  'Message sent successfully!': {
    lt: 'Žinutė sėkmingai išsiųsta!',
    ru: 'Сообщение отправлено!',
    fr: 'Message envoyé !',
    de: 'Nachricht gesendet!',
  },
  'Failed to send message. Please try again.': {
    lt: 'Nepavyko išsiųsti žinutės. Bandyk dar kartą.',
    ru: 'Не удалось отправить сообщение. Попробуй снова.',
    fr: 'Impossible d’envoyer le message. Réessaie.',
    de: 'Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.',
  },
  'Connect With Us': { lt: 'Susisiek su mumis', ru: 'Свяжись с нами', fr: 'Nous rejoindre', de: 'Verbinde dich mit uns' },
  'Join our community chat': { lt: 'Prisijunk prie bendruomenės pokalbio', ru: 'Заходи в чат сообщества', fr: 'Rejoins le chat communautaire', de: 'Tritt dem Community-Chat bei' },
  'Follow for updates': { lt: 'Sek naujienas', ru: 'Следи за обновлениями', fr: 'Suis les mises à jour', de: 'Für Updates folgen' },
  'Game Server': { lt: 'Žaidimo serveris', ru: 'Игровой сервер', fr: 'Serveur de jeu', de: 'Gameserver' },
  'Direct connect': { lt: 'Tiesioginis prisijungimas', ru: 'Прямое подключение', fr: 'Connexion directe', de: 'Direkt verbinden' },
  'Join via Steam': { lt: 'Prisijungti per Steam', ru: 'Зайти через Steam', fr: 'Rejoindre via Steam', de: 'Über Steam beitreten' },
  'For immediate assistance, join our Discord server where our admins are most active.': {
    lt: 'Skubiai pagalbai prisijunk prie Discord serverio, kur adminai aktyviausi.',
    ru: 'Для быстрой помощи заходи в Discord, где админы наиболее активны.',
    fr: 'Pour une aide immédiate, rejoins notre Discord où les admins sont les plus actifs.',
    de: 'Für schnelle Hilfe tritt unserem Discord bei; dort sind die Admins am aktivsten.',
  },
  'Game Gallery': { lt: 'Žaidimo galerija', ru: 'Галерея игры', fr: 'Galerie du jeu', de: 'Spielgalerie' },
  'Support Our Server': { lt: 'Palaikyk mūsų serverį', ru: 'Поддержи наш сервер', fr: 'Soutiens notre serveur', de: 'Unterstütze unseren Server' },
  'VIP Coming Soon': { lt: 'VIP netrukus', ru: 'VIP скоро', fr: 'VIP bientôt', de: 'VIP bald' },
  'Thank You!': { lt: 'Ačiū!', ru: 'Спасибо!', fr: 'Merci !', de: 'Danke!' },
  'Welcome to VIP!': { lt: 'Sveiki atvykę į VIP!', ru: 'Добро пожаловать в VIP!', fr: 'Bienvenue en VIP !', de: 'Willkommen bei VIP!' },
  'Player Statistics': { lt: 'Žaidėjų statistika', ru: 'Статистика игроков', fr: 'Statistiques joueurs', de: 'Spielerstatistiken' },
  'One community. Every battlefield.': { lt: 'Viena bendruomenė. Kiekvienas mūšio laukas.', ru: 'Одно сообщество. Единство в сражении', fr: 'Une communauté. Chaque champ de bataille.', de: 'Eine Community. Jedes Schlachtfeld.' },
  'Questions, reports, suggestions, or community help. Send a message and it lands with the Uliunai team.': {
    lt: 'Klausimai, pranešimai, pasiūlymai ar pagalba bendruomenei. Išsiųsk žinutę ir ji pasieks Uliunai komandą.',
    ru: 'Вопросы, жалобы, предложения или помощь сообществу. Отправь сообщение, и оно попадет команде Uliunai.',
    fr: 'Questions, signalements, suggestions ou aide communautaire. Envoie un message et il arrive à l’équipe Uliunai.',
    de: 'Fragen, Meldungen, Vorschläge oder Community-Hilfe. Sende eine Nachricht, und sie landet beim Uliunai Team.',
  },
  'Send us a Message': { lt: 'Siųsk mums žinutę', ru: 'Отправь нам сообщение', fr: 'Envoie-nous un message', de: 'Sende uns eine Nachricht' },
  'Send a Message': { lt: 'Siųsti žinutę', ru: 'Отправить сообщение', fr: 'Envoyer un message', de: 'Nachricht senden' },
  'Name *': { lt: 'Vardas *', ru: 'Имя *', fr: 'Nom *', de: 'Name *' },
  'Email *': { lt: 'El. paštas *', ru: 'Email *', fr: 'E-mail *', de: 'E-Mail *' },
  'Subject *': { lt: 'Tema *', ru: 'Тема *', fr: 'Sujet *', de: 'Betreff *' },
  'Message * (Max 500 characters)': { lt: 'Žinutė * (maks. 500 simbolių)', ru: 'Сообщение * (макс. 500 символов)', fr: 'Message * (500 caractères max)', de: 'Nachricht * (max. 500 Zeichen)' },
  'Select a subject': { lt: 'Pasirink temą', ru: 'Выбери тему', fr: 'Choisir un sujet', de: 'Betreff auswählen' },
  'Your name': { lt: 'Tavo vardas', ru: 'Твое имя', fr: 'Ton nom', de: 'Dein Name' },
  'your@email.com': { lt: 'tavo@email.com', ru: 'your@email.com', fr: 'ton@email.com', de: 'deine@email.com' },
  'Your message here...': { lt: 'Tavo žinutė...', ru: 'Твое сообщение...', fr: 'Ton message ici...', de: 'Deine Nachricht hier...' },
  'Sending...': { lt: 'Siunčiama...', ru: 'Отправка...', fr: 'Envoi...', de: 'Wird gesendet...' },
  'Failed to send message. Please try again or use Discord.': { lt: 'Nepavyko išsiųsti žinutės. Bandyk dar kartą arba naudok Discord.', ru: 'Не удалось отправить сообщение. Попробуй снова или используй Discord.', fr: 'Impossible d’envoyer le message. Réessaie ou utilise Discord.', de: 'Nachricht konnte nicht gesendet werden. Versuche es erneut oder nutze Discord.' },
  'Discord Server': { lt: 'Discord serveris', ru: 'Discord сервер', fr: 'Serveur Discord', de: 'Discord-Server' },
  'Fastest way to reach the community.': { lt: 'Greičiausias būdas pasiekti bendruomenę.', ru: 'Самый быстрый способ связаться с сообществом.', fr: 'Le moyen le plus rapide de joindre la communauté.', de: 'Der schnellste Weg zur Community.' },
  'Open Discord': { lt: 'Atidaryti Discord', ru: 'Открыть Discord', fr: 'Ouvrir Discord', de: 'Discord öffnen' },
  'Steam Group': { lt: 'Steam grupė', ru: 'Группа Steam', fr: 'Groupe Steam', de: 'Steam-Gruppe' },
  'Follow Uliunai updates and events.': { lt: 'Sek Uliunai naujienas ir renginius.', ru: 'Следи за новостями и событиями Uliunai.', fr: 'Suis les mises à jour et événements Uliunai.', de: 'Folge Uliunai Updates und Events.' },
  'Steam community': { lt: 'Steam bendruomenė', ru: 'Сообщество Steam', fr: 'Communauté Steam', de: 'Steam-Community' },
  'Quick Support': { lt: 'Greita pagalba', ru: 'Быстрая поддержка', fr: 'Support rapide', de: 'Schneller Support' },
  'For urgent server issues, Discord is usually fastest. Contact form messages are still routed to staff.': { lt: 'Skubiems serverio klausimams Discord dažniausiai greičiausias. Kontaktinės formos žinutės taip pat perduodamos komandai.', ru: 'Для срочных проблем сервера Discord обычно быстрее всего. Сообщения формы также попадают персоналу.', fr: 'Pour les urgences serveur, Discord est généralement le plus rapide. Les messages du formulaire arrivent aussi au staff.', de: 'Bei dringenden Serverproblemen ist Discord meist am schnellsten. Kontaktformular-Nachrichten gehen ebenfalls ans Team.' },
  'Response Time:': { lt: 'Atsakymo laikas:', ru: 'Время ответа:', fr: 'Temps de réponse :', de: 'Antwortzeit:' },
  'Usually within 1-2 hours': { lt: 'Paprastai per 1-2 val.', ru: 'Обычно в течение 1-2 часов', fr: 'Généralement sous 1-2 heures', de: 'Meist innerhalb von 1-2 Stunden' },
  'Support Hours:': { lt: 'Pagalbos laikas:', ru: 'Часы поддержки:', fr: 'Horaires support :', de: 'Supportzeiten:' },
  '24/7 via Discord': { lt: '24/7 per Discord', ru: '24/7 через Discord', fr: '24/7 via Discord', de: '24/7 über Discord' },
  'VIP Membership': { lt: 'VIP narystė', ru: 'VIP членство', fr: 'Adhésion VIP', de: 'VIP-Mitgliedschaft' },

  // --- VIP storefront (home section + /vip page) ---
  'Support Our': { lt: 'Palaikyk mūsų', ru: 'Поддержи наш', fr: 'Soutiens notre', de: 'Unterstütze unseren' },
  'Server': { lt: 'serverį', ru: 'сервер', fr: 'serveur', de: 'Server' },
  'Help keep our server running and get exclusive benefits with our VIP membership tiers': {
    lt: 'Padėk išlaikyti serverį ir gauk išskirtinių privalumų su VIP narystės lygiais',
    ru: 'Помоги поддерживать сервер и получи эксклюзивные бонусы с VIP подписками',
    fr: 'Aide à faire tourner le serveur et débloque des avantages exclusifs avec nos abonnements VIP',
    de: 'Hilf, unseren Server am Laufen zu halten, und erhalte exklusive Vorteile mit VIP-Stufen',
  },
  'MOST POPULAR': { lt: 'POPULIARIAUSIAS', ru: 'САМЫЙ ПОПУЛЯРНЫЙ', fr: 'LE PLUS POPULAIRE', de: 'BELIEBTESTE' },
  '/month': { lt: '/mėn.', ru: '/мес.', fr: '/mois', de: '/Monat' },
  'Recurring subscription - cancel anytime': { lt: 'Periodinė prenumeratą - atšauk bet kada', ru: 'Повторяющаяся подписка - отмена в любой момент', fr: 'Abonnement récurrent - annule quand tu veux', de: 'Wiederkehrendes Abo - jederzeit kündbar' },
  'Double Experience (2x XP)': { lt: 'Dviguba patirtis (2x XP)', ru: 'Двойной опыт (2x XP)', fr: 'Double expérience (2x XP)', de: 'Doppelte Erfahrung (2x XP)' },
  'Custom Skins': { lt: 'Unikalūs skinai', ru: 'Кастомные скины', fr: 'Skins personnalisés', de: 'Eigene Skins' },
  'Custom Weapons': { lt: 'Unikalūs ginklai', ru: 'Кастомное оружие', fr: 'Armes personnalisées', de: 'Eigene Waffen' },
  'Custom Boosts': { lt: 'Unikalūs boostai', ru: 'Кастомные бусты', fr: 'Boosts personnalisés', de: 'Eigene Boosts' },
  'VIP Tag on Scoreboard': { lt: 'VIP žyma rezultatuose', ru: 'VIP метка в таблице', fr: 'Tag VIP au scoreboard', de: 'VIP-Tag im Scoreboard' },
  'Green Nickname Color': { lt: 'Žalia slapyvardžio spalva', ru: 'Зелёный никнейм', fr: 'Pseudo en vert', de: 'Gruner Nickname' },
  'Extended AFK Timer (15 min)': { lt: 'Ilgesnis AFK laikmatis (15 min)', ru: 'Увеличенный AFK таймер (15 мин)', fr: 'Timer AFK étendu (15 min)', de: 'Verlangerter AFK-Timer (15 Min.)' },
  'Triple Experience (3x XP)': { lt: 'Triguba patirtis (3x XP)', ru: 'Тройной опыт (3x XP)', fr: 'Triple expérience (3x XP)', de: 'Dreifache Erfahrung (3x XP)' },
  'All VIP Benefits Included': { lt: 'Visi VIP privalumai iįskaičiuoti', ru: 'Все преимущества VIP включены', fr: 'Tous les avantages VIP inclus', de: 'Alle VIP-Vorteile inklusive' },
  'Exclusive VIP+ Skins': { lt: 'Išskirtiniai VIP+ skinai', ru: 'Эксклюзивные VIP+ скины', fr: 'Skins VIP+ exclusifs', de: 'Exklusive VIP+-Skins' },
  'Exclusive VIP+ Weapons': { lt: 'Išskirtiniai VIP+ ginklai', ru: 'Эксклюзивное VIP+ оружие', fr: 'Armes VIP+ exclusives', de: 'Exklusive VIP+-Waffen' },
  'Red VIP+ Tag on Scoreboard': { lt: 'Raudona VIP+ žyma rezultatuose', ru: 'Красная VIP+ метка в таблице', fr: 'Tag VIP+ rouge au scoreboard', de: 'Roter VIP+-Tag im Scoreboard' },
  'Red Nickname Color': { lt: 'Raudona slapyvardžio spalva', ru: 'Красный никнейм', fr: 'Pseudo en rouge', de: 'Roter Nickname' },
  'Extended AFK Timer (30 min)': { lt: 'Ilgesnis AFK laikmatis (30 min)', ru: 'Увеличенный AFK таймер (30 мин)', fr: 'Timer AFK étendu (30 min)', de: 'Verlangerter AFK-Timer (30 Min.)' },
  'Get VIP Now': { lt: 'Gauk VIP dabar', ru: 'Получить VIP', fr: 'Devenir VIP', de: 'Jetzt VIP holen' },
  'Get VIP+ Now': { lt: 'Gauk VIP+ dabar', ru: 'Получить VIP+', fr: 'Devenir VIP+', de: 'Jetzt VIP+ holen' },
  'Become a': { lt: 'Tapk', ru: 'Стань', fr: 'Deviens', de: 'Werde' },
  'Member': { lt: 'nariu', ru: 'участником', fr: 'membre', de: 'Mitglied' },
  'Become a VIP Member': { lt: 'Tapk VIP nariu', ru: 'Стань VIP участником', fr: 'Deviens membre VIP', de: 'Werde VIP-Mitglied' },
  'Support our server and unlock exclusive perks, rewards, and recognition': {
    lt: 'Palaikyk mūsų serverį ir atrakink išskirtinius privalumųs, apdovanojimus ir pripažinimą',
    ru: 'Поддержи сервер и открой эксклюзивные перки, награды и признание',
    fr: 'Soutiens notre serveur et débloque perks, récompenses et reconnaissance exclusifs',
    de: 'Unterstütze unseren Server und schalte exklusive Perks, Belohnungen und Anerkennung frei',
  },
  'All plans are recurring subscriptions - you can cancel anytime': { lt: 'Visi planąi yra periodinės prenumeratos - gali atšaukti bet kada', ru: 'Все планы - повторяющиеся подписки, отмена в любой момент', fr: 'Tous les plans sont des abonnements récurrents - annule quand tu veux', de: 'Alle Pläne sind wiederkehrende Abos - jederzeit kündbar' },
  'Loading plans...': { lt: 'Kraunami planąi...', ru: 'Загрузка планов...', fr: 'Chargement des plans...', de: 'Plane werden geladen...' },
  'Pricing unavailable right now. Please try again later.': { lt: 'Kainos dabar nepasiekiamos. Bandyk vėliau.', ru: 'Цены сейчас недоступны. Попробуй позже.', fr: 'Tarifs indisponibles pour le moment. Réessaie plus tard.', de: 'Preise derzeit nicht verfügbar. Bitte später erneut versuchen.' },
  'Failed to load pricing': { lt: 'Nepavyko iįkelti kainų', ru: 'Не удалось загрузить цены', fr: 'Impossible de charger les tarifs', de: 'Preise konnten nicht geladen werden' },
  'Stripe test mode': { lt: 'Stripe testavimo režimas', ru: 'Тестовый режим Stripe', fr: 'Mode test Stripe', de: 'Stripe-Testmodus' },
  'Checking Stripe mode': { lt: 'Tikrinamas Stripe režimas', ru: 'Проверка режима Stripe', fr: 'Vérification du mode Stripe', de: 'Stripe-Modus wird geprüft' },
  'No real charge will be made. Checkout is restricted to the approved test account.': {
    lt: 'Realūs mokėjimai nebus nuskaičiuoti. Apmokėjimas galimas tik patvirtintai testinęi paskyrai.',
    ru: 'Реальных списаний не будет. Оплата доступна только одобренному тестовому аккаунту.',
    fr: 'Aucun vrai paiement. Le checkout est limité au compte de test approuvé.',
    de: 'Es wird nichts echt abgebucht. Checkout nur für das freigegebene Testkonto.',
  },
  'Checkout remains disabled until Stripe configuration is confirmed.': { lt: 'Apmokėjimas išjungtas, kol Stripe konfigūracija nepatvirtinta.', ru: 'Оплата отключена, пока не подтверждена конфигурация Stripe.', fr: 'Le checkout reste désactivé tant que Stripe nest pas confirme.', de: 'Checkout bleibt deaktiviert, bis Stripe bestatigt ist.' },
  'Choose Your': { lt: 'Pasirink', ru: 'Выбери', fr: 'Choisis ton', de: 'Wähle deinen' },
  'Plan': { lt: 'planą', ru: 'план', fr: 'forfait', de: 'Plan' },
  'Choose Your Plan': { lt: 'Pasirink planą', ru: 'Выбери план', fr: 'Choisis ton forfait', de: 'Wähle deinen Plan' },
  'Popular': { lt: 'Populiaru', ru: 'Популярный', fr: 'Populaire', de: 'Beliebt' },
  'Best Value': { lt: 'Geriausia vertė', ru: 'Лучшая цена', fr: 'Meilleure offre', de: 'Bester Wert' },
  'POPULAR': { lt: 'POPULIARU', ru: 'ПОПУЛЯРНЫЙ', fr: 'POPULAIRE', de: 'BELIEBT' },
  'BEST VALUE': { lt: 'GERIAUSIA VERTĖ', ru: 'ЛУЧШАЯ ЦЕНА', fr: 'MEILLEURE OFFRE', de: 'BESTER WERT' },
  'Month': { lt: 'mėnuo', ru: 'месяц', fr: 'mois', de: 'Monat' },
  'Months': { lt: 'mėn.', ru: 'мес.', fr: 'mois', de: 'Monate' },
  'Save': { lt: 'Sutaupyk', ru: 'Экономия', fr: 'Économise', de: 'Spare' },
  'Complete Your Purchase': { lt: 'Užbaik pirkimą', ru: 'Заверши покупку', fr: 'Finalise ton achat', de: 'Kauf abschließen' },
  'Billed every': { lt: 'Apmokama kas', ru: 'Списание каждые', fr: 'Facturé tous les', de: 'Abrechnung alle' },
  'month': { lt: 'mėnesį', ru: 'месяц', fr: 'mois', de: 'Monat' },
  'months': { lt: 'mėnesįus', ru: 'месяца', fr: 'mois', de: 'Monate' },
  'Total': { lt: 'Iš viso', ru: 'Итого', fr: 'Total', de: 'Gesamt' },
  'Your Steam ID': { lt: 'Tavo Steam ID', ru: 'Твой Steam ID', fr: 'Ton Steam ID', de: 'Deine Steam-ID' },
  'Find your Steam64 ID at': { lt: 'Rask savo Steam64 ID čia:', ru: 'Найди свой Steam64 ID здесь:', fr: 'Trouve ton Steam64 ID sur', de: 'Finde deine Steam64-ID unter' },
  'Please enter your Steam ID': { lt: 'Įvesk savo Steam ID', ru: 'Введи свой Steam ID', fr: 'Entre ton Steam ID', de: 'Bitte gib deine Steam-ID ein' },
  'Please enter a valid Steam64 ID (17 digits starting with 7656119...)': { lt: 'Įvesk galiojantį Steam64 ID (17 skaitmenų, prasideda 7656119...)', ru: 'Введи корректный Steam64 ID (17 цифр, начинается с 7656119...)', fr: 'Entre un Steam64 ID valide (17 chiffres commencant par 7656119...)', de: 'Bitte eine gültige Steam64-ID eingeben (17 Ziffern, beginnend mit 7656119...)' },
  'Please select a plan': { lt: 'Pasirink planą', ru: 'Выбери план', fr: 'Choisis un forfait', de: 'Bitte wahle einen Plan' },
  'Failed to create checkout session': { lt: 'Nepavyko sukurti apmokėjimo sesijos', ru: 'Не удалось создать сессию оплаты', fr: 'Impossible de créer la session de paiement', de: 'Checkout-Sitzung konnte nicht erstellt werden' },
  'Could not open subscription management': { lt: 'Nepavyko atidaryti prenumeratos valdymo', ru: 'Не удалось открыть управление подпиской', fr: 'Impossible douvrir la gestion dabonnement', de: 'Abo-Verwaltung konnte nicht geöffnet werden' },
  'Stripe did not return a portal URL': { lt: 'Stripe negrąžino portalo nuorodos', ru: 'Stripe не вернул URL портала', fr: 'Stripe na pas renvoye dURL de portail', de: 'Stripe hat keine Portal-URL zurückgegeben' },
  'Redirecting to checkout...': { lt: 'Nukreipiama į apmokėjimą...', ru: 'Переход к оплате...', fr: 'Redirection vers le paiement...', de: 'Weiterleitung zum Checkout...' },
  'Subscribe Now': { lt: 'Prenumeruoti dabar', ru: 'Оформить подписку', fr: 'Sabonner maintenant', de: 'Jetzt abonnieren' },
  'Test Subscription': { lt: 'Testinė prenumeratą', ru: 'Тестовая подписка', fr: 'Abonnement test', de: 'Test-Abo' },
  'Secure payment powered by Stripe': { lt: 'Saugus mokėjimas per Stripe', ru: 'Безопасная оплата через Stripe', fr: 'Paiement sécurisé via Stripe', de: 'Sichere Zahlung über Stripe' },
  'By subscribing, you agree to the': { lt: 'Prenumeruodamas sutinki su', ru: 'Оформляя подписку, ты соглашаешься с', fr: 'En tabonnant, tu acceptes les', de: 'Mit dem Abo stimmst du den' },
  'and': { lt: 'ir', ru: 'и', fr: 'et', de: 'und' },
  'Manage Your Subscription': { lt: 'Valdyk prenumeratą', ru: 'Управляй подпиской', fr: 'Gère ton abonnement', de: 'Abo verwalten' },
  'Manage Subscription': { lt: 'Valdyti prenumeratą', ru: 'Управлять подпиской', fr: 'Gérer labonnement', de: 'Abo verwalten' },
  'Manage Test Subscription': { lt: 'Valdyti testinę prenumeratą', ru: 'Управлять тестовой подпиской', fr: 'Gérer labonnement test', de: 'Test-Abo verwalten' },
  'After checkout, use the Manage Subscription button on the confirmation screen to update or cancel through Stripes secure portal.': {
    lt: 'Po apmokėjimo naudok mygtuką Valdyti prenumeratą patvirtinimo lange, kad atnaujintum ar atšauktum per saugų Stripe portalą.',
    ru: 'После оплаты используй кнопку Управлять подпиской на экране подтверждения, чтобы изменить или отменить через безопасный портал Stripe.',
    fr: 'Après le paiement, utilise le bouton Gérer labonnement sur lecran de confirmation pour modifier ou annuler via le portail sécurisé Stripe.',
    de: 'Nutze nach dem Checkout die Schaltfläche Abo verwalten auf dem Bestätigungsbildschirm, um uber das sichere Stripe-Portal zu ändern oder zu kündigen.',
  },
  'Need help? Contact us on': { lt: 'Reikia pagalbos? Susisiek per', ru: 'Нужна помощь? Свяжись через', fr: 'Besoin daide ? Contacte-nous sur', de: 'Brauchst du Hilfe? Melde dich uber' },
  'Secure Payments': { lt: 'Saugus mokėjimai', ru: 'Безопасные платежи', fr: 'Paiements sécurisés', de: 'Sichere Zahlungen' },
  'All payments processed securely through Stripe': { lt: 'Visi mokėjimai saugiai apdorojami per Stripe', ru: 'Все платежи безопасно обрабатываются через Stripe', fr: 'Tous les paiements sont traités en toute sécurité via Stripe', de: 'Alle Zahlungen werden sicher über Stripe abgewickelt' },
  'Fast Activation': { lt: 'Greitas aktyvavimas', ru: 'Быстрая активация', fr: 'Activation rapide', de: 'Schnelle Aktivierung' },
  'Your VIP perks activate on the next map change after payment': { lt: 'Tavo VIP privalumai įsijungs po kito žemėlapio keitimo po apmokėjimo', ru: 'VIP бонусы активируются после следующей смены карты после оплаты', fr: 'Tes avantages VIP sactivent au prochain changement de carte apres le paiement', de: 'Deine VIP-Perks aktivieren sich nach der Zahlung beim nächsten Map-Wechsel' },
  'Cancel Anytime': { lt: 'Atšauk bet kada', ru: 'Отмена в любой момент', fr: 'Annule quand tu veux', de: 'Jederzeit kündbar' },
  'No commitment - cancel your subscription whenever you want': { lt: 'Jokių įsipareigojimų - atšauk prenumeratą kada nori', ru: 'Без обязательств - отмени подписку когда угодно', fr: 'Sans engagement - annule ton abonnement quand tu veux', de: 'Keine Bindung - kundige dein Abo, wann du willst' },
  'Thank': { lt: 'Ačiū', ru: 'Спасибо', fr: 'Merci', de: 'Danke' },
  'You': { lt: 'tau', ru: 'тебе', fr: 'à toi', de: 'dir' },
  'Welcome to': { lt: 'Sveiki atvykė į', ru: 'Добро пожаловать в', fr: 'Bienvenue en', de: 'Willkommen bei' },
  'Your donation means a lot! Every contribution helps keep the Uliunai server running.': {
    lt: 'Tavo parama labai svarbi! Kiekvienas įnašas padeda išlaikyti Uliunai serverį.',
    ru: 'Твоё пожертвование очень важно! Каждый вклад помогает поддерживать сервер Uliunai.',
    fr: 'Ton don compte beaucoup ! Chaque contribution aide à faire tourner le serveur Uliunai.',
    de: 'Deine Spende bedeutet uns viel! Jeder Beitrag hält den Uliunai-Server am Laufen.',
  },
  'Your VIP perks will activate on the next map change. Rejoin the server after a map change to see your new perks!': {
    lt: 'Tavo VIP privalumai įsijungs po kito žemėlapio keitimo. Prisijunk iš naujo po map change, kad pamatytum naujus privalumųs!',
    ru: 'VIP бонусы активируются после следующей смены карты. Перезайди на сервер после смены карты, чтобы увидеть новые бонусы!',
    fr: 'Tes avantages VIP sactiveront au prochain changement de carte. Rejoins le serveur apres un map change pour les voir !',
    de: 'Deine VIP-Perks aktivieren sich beim nächsten Map-Wechsel. Tritt danach erneut bei, um sie zu sehen!',
  },
  'Manage or cancel securely through Stripe using the button below.': { lt: 'Valdyk ar atšauk saugiai per Stripe naudodamas mygtuką žemiau.', ru: 'Управляй или отменяй безопасно через Stripe кнопкой ниже.', fr: 'Gere ou annule en toute sécurité via Stripe avec le bouton ci-dessous.', de: 'Verwalte oder kundige sicher über Stripe mit der Schaltfläche unten.' },
  'Opening Stripe...': { lt: 'Atidaromas Stripe...', ru: 'Открываем Stripe...', fr: 'Ouverture de Stripe...', de: 'Stripe wird geöffnet...' },
  'Close': { lt: 'Uždaryti', ru: 'Закрыть', fr: 'Fermer', de: 'Schließen' },
  'No news yet - check back here for vanilla updates and community events.': {
    lt: 'Naujienų dar nėra - grizk vėliau del vanilla atnaujinimų ir bendruomenės renginiu.',
    ru: 'Новостей пока нет - загляни позже за обновлениями vanilla и событиями сообщества.',
    fr: 'Pas encore de news - reviens ici pour les mises a jour vanilla et les événements communautaires.',
    de: 'Noch keine News - schau später für Vanilla-Updates und Community-Events vorbei.',
  },
  'Recurring subscription — cancel anytime': { lt: 'Periodinė prenumeratą — atšauk bet kada', ru: 'Повторяющаяся подписка — отмена в любой момент', fr: 'Abonnement récurrent — annule quand tu veux', de: 'Wiederkehrendes Abo — jederzeit kündbar' },
  'All plans are recurring subscriptions — you can cancel anytime': { lt: 'Visi planąi yra periodinės prenumeratos — gali atšaukti bet kada', ru: 'Все планы — повторяющиеся подписки, отмена в любой момент', fr: 'Tous les plans sont des abonnements récurrents — annule quand tu veux', de: 'Alle Pläne sind wiederkehrende Abos — jederzeit kündbar' },
  'No commitment — cancel your subscription whenever you want': { lt: 'Jokių įsipareigojimų — atšauk prenumeratą kada nori', ru: 'Без обязательств — отмени подписку когда угодно', fr: 'Sans engagement — annule ton abonnement quand tu veux', de: 'Keine Bindung — kundige dein Abo, wann du willst' },
  'No news yet — check back here for vanilla updates and community events.': {
    lt: 'Naujienų dar nėra — grizk vėliau del vanilla atnaujinimų ir bendruomenės renginiu.',
    ru: 'Новостей пока нет — загляни позже за обновлениями vanilla и событиями сообщества.',
    fr: 'Pas encore de news — reviens ici pour les mises a jour vanilla et les événements communautaires.',
    de: 'Noch keine News — schau später für Vanilla-Updates und Community-Events vorbei.',
  },
  'Report Player': { lt: 'Pranešti apie žaidėją', ru: 'Пожаловаться на игрока', fr: 'Signaler un joueur', de: 'Spieler melden' },
  'Modpack Issue': { lt: 'Modpack problema', ru: 'Проблема modpack', fr: 'Problème de modpack', de: 'Modpack-Problem' },
  'Downloads': { lt: 'Atsisiuntimai', ru: 'Загрузки', fr: 'Téléchargements', de: 'Downloads' },
  'Community Rules': { lt: 'Bendruomenės taisyklės', ru: 'Правила сообщества', fr: 'Règles communauté', de: 'Community-Regeln' },
  'IP:': { lt: 'IP:', ru: 'IP:', fr: 'IP :', de: 'IP:' },
  'Game:': { lt: 'Žaidimas:', ru: 'Игра:', fr: 'Jeu :', de: 'Spiel:' },
  'Max Players:': { lt: 'Maks. žaidėjų:', ru: 'Макс. игроков:', fr: 'Joueurs max :', de: 'Max. Spieler:' },
  'Location:': { lt: 'Vieta:', ru: 'Локация:', fr: 'Emplacement :', de: 'Standort:' },
  'Uptime:': { lt: 'Veikimo laikas:', ru: 'Аптайм:', fr: 'Disponibilité :', de: 'Uptime:' },
  'Server is currently offline': { lt: 'Serveris šiuo metu išjungtas', ru: 'Сервер сейчас офлайн', fr: 'Le serveur est actuellement hors ligne', de: 'Der Server ist derzeit offline' },
  'The scoreboard will appear when the server comes back online': { lt: 'Rezultatų lentelė pasirodys, kai serveris vėl įsijungs', ru: 'Таблица появится, когда сервер снова будет онлайн', fr: 'Le scoreboard apparaîtra quand le serveur reviendra en ligne', de: 'Das Scoreboard erscheint, wenn der Server wieder online ist' },
  'No survivors in the field': { lt: 'Lauke nėra išgyvenusiųjų', ru: 'В поле нет выживших', fr: 'Aucun survivant sur le terrain', de: 'Keine Überlebenden im Feld' },
  'Join the server and be the first!': { lt: 'Prisijunk prie serverio ir būk pirmas!', ru: 'Заходи на сервер и будь первым!', fr: 'Rejoins le serveur et sois le premier !', de: 'Tritt dem Server bei und sei der Erste!' },
  Player: { lt: 'Žaidėjas', ru: 'Игрок', fr: 'Joueur', de: 'Spieler' },
  Perk: { lt: 'Perkas', ru: 'Перк', fr: 'Atout', de: 'Perk' },
  Kills: { lt: 'Nužudymai', ru: 'Убийства', fr: 'Kills', de: 'Kills' },
  Cash: { lt: 'Pinigai', ru: 'Деньги', fr: 'Argent', de: 'Geld' },
  HP: { lt: 'HP', ru: 'HP', fr: 'PV', de: 'HP' },
  Ping: { lt: 'Ping', ru: 'Пинг', fr: 'Ping', de: 'Ping' },
  DEAD: { lt: 'MIRĘS', ru: 'МЕРТВ', fr: 'MORT', de: 'TOT' },
  'Zeds Alive': { lt: 'Gyvi zedai', ru: 'Живые зеды', fr: 'Zeds vivants', de: 'Zeds am Leben' },
  'Now Playing': { lt: 'Dabar žaidžiama', ru: 'Сейчас играет', fr: 'En cours', de: 'Jetzt läuft' },
  'Trader Time': { lt: 'Prekeivio laikas', ru: 'Время торговца', fr: 'Temps marchand', de: 'Trader-Zeit' },
  'In Progress': { lt: 'Vyksta', ru: 'Идет', fr: 'En cours', de: 'Läuft' },
  Waiting: { lt: 'Laukiama', ru: 'Ожидание', fr: 'En attente', de: 'Warten' },
  'Fast Download': { lt: 'Greitas atsisiuntimas', ru: 'Быстрая загрузка', fr: 'Téléchargement rapide', de: 'Fast Download' },
  'Content packs are retired. The server now serves required custom files through the in-game fast-download redirect when you connect.': { lt: 'Turinio paketai nebenaudojami. Prisijungiant serveris reikiamus failus pateikia per žaidimo greito atsisiuntimo nukreipimą.', ru: 'Пакеты контента больше не используются. При подключении сервер отдает нужные файлы через игровой fast-download redirect.', fr: 'Les packs de contenu sont retirés. Le serveur fournit maintenant les fichiers requis via la redirection fast-download en jeu.', de: 'Content-Packs sind eingestellt. Der Server liefert benötigte Dateien beim Verbinden über den Ingame-Fast-Download-Redirect.' },
  'Direct KF1 server': { lt: 'Tiesioginis KF1 serveris', ru: 'Прямой сервер KF1', fr: 'Serveur KF1 direct', de: 'Direkter KF1-Server' },
  'Automatic redirect downloads': { lt: 'Automatiniai atsisiuntimai per nukreipimą', ru: 'Автоматические загрузки через redirect', fr: 'Téléchargements redirect automatiques', de: 'Automatische Redirect-Downloads' },
  'Join Server': { lt: 'Prisijungti prie serverio', ru: 'Зайти на сервер', fr: 'Rejoindre le serveur', de: 'Server beitreten' },
  'What Changed': { lt: 'Kas pasikeitė', ru: 'Что изменилось', fr: 'Ce qui a changé', de: 'Was sich geändert hat' },
  'Server Address': { lt: 'Serverio adresas', ru: 'Адрес сервера', fr: 'Adresse serveur', de: 'Serveradresse' },
  'Content Delivery': { lt: 'Turinio pateikimas', ru: 'Доставка контента', fr: 'Livraison de contenu', de: 'Content-Auslieferung' },
  'No Pre-download Required': { lt: 'Išankstinio atsisiuntimo nereikia', ru: 'Предзагрузка не нужна', fr: 'Aucun pré-téléchargement requis', de: 'Kein Vorab-Download nötig' },
  'Legacy Pack Links Retired': { lt: 'Seni paketų linkai pašalinti', ru: 'Старые ссылки пакетов отключены', fr: 'Anciens liens de packs retirés', de: 'Alte Pack-Links eingestellt' },
  'Join Steps': { lt: 'Prisijungimo žingsniai', ru: 'Шаги подключения', fr: 'Étapes de connexion', de: 'Beitrittsschritte' },
  Troubleshooting: { lt: 'Problemų sprendimas', ru: 'Устранение проблем', fr: 'Dépannage', de: 'Fehlerbehebung' },
  'Players Tracked': { lt: 'Sekami žaidėjai', ru: 'Отслеживаемые игроки', fr: 'Joueurs suivis', de: 'Erfasste Spieler' },
  'Total Kills': { lt: 'Iš viso nužudymų', ru: 'Всего убийств', fr: 'Kills totaux', de: 'Kills gesamt' },
  'Total Playtime': { lt: 'Bendras žaidimo laikas', ru: 'Общее время игры', fr: 'Temps de jeu total', de: 'Gesamtspielzeit' },
  'Total Victories': { lt: 'Pergalės iš viso', ru: 'Всего побед', fr: 'Victoires totales', de: 'Siege gesamt' },
  General: { lt: 'Bendra', ru: 'Общее', fr: 'Général', de: 'Allgemein' },
  'Enemy Kills': { lt: 'Priešų nužudymai', ru: 'Убийства врагов', fr: 'Ennemis tués', de: 'Gegner-Kills' },
  Misc: { lt: 'Kita', ru: 'Разное', fr: 'Divers', de: 'Sonstiges' },
  Explore: { lt: 'Naršyti', ru: 'Изучить', fr: 'Explorer', de: 'Entdecken' },
  'Server Addresses': { lt: 'Serverių adresai', ru: 'Адреса серверов', fr: 'Adresses serveur', de: 'Serveradressen' },
  Vanilla: { lt: 'Vanilla', ru: 'Vanilla', fr: 'Vanilla', de: 'Vanilla' },
  'Survival': { lt: 'Survival', ru: 'Survival', fr: 'Survival', de: 'Survival' },
  Modded: { lt: 'Modded', ru: 'Modded', fr: 'Moddé', de: 'Modded' },
  'Survival and a curated modpack on Minecraft Java Edition. Friendly community, clear rules, active staff, and a welcoming crew.': {
    lt: 'Survival ir atrinktas modpack Minecraft Java Edition. Draugiška bendruomenė, aiškios taisyklės, aktyvi komanda ir svetingi žmonės.',
    ru: 'Survival и подобранный modpack на Minecraft Java Edition. Дружелюбное сообщество, понятные правила, активный персонал и теплая команда.',
    fr: 'Survival et modpack sélectionné sur Minecraft Java Edition. Communauté amicale, règles claires, staff actif et équipe accueillante.',
    de: 'Survival und ein kuratiertes Modpack auf Minecraft Java Edition. Freundliche Community, klare Regeln, aktives Team und eine einladende Runde.',
  },
  'Vanilla survival and a curated modpack on Minecraft Java Edition. Friendly community, clear rules, active staff, and a welcoming crew.': {
    lt: 'Vanilla išgyvenimas ir atrinktas modpack Minecraft Java Edition. Draugiška bendruomenė, aiškios taisyklės, aktyvi komanda ir svetingi žmonės.',
    ru: 'Vanilla survival и подобранный modpack на Minecraft Java Edition. Дружелюбное сообщество, понятные правила, активный персонал и теплая команда.',
    fr: 'Survie Vanilla et modpack sélectionné sur Minecraft Java Edition. Communauté amicale, règles claires, staff actif et équipe accueillante.',
    de: 'Vanilla Survival und ein kuratiertes Modpack auf Minecraft Java Edition. Freundliche Community, klare Regeln, aktives Team und eine einladende Runde.',
  },
  'Part of the': { lt: 'Dalis', ru: 'Часть', fr: 'Fait partie de la', de: 'Teil der' },
  'Uliunai community': { lt: 'Uliunai bendruomenės', ru: 'сообщества Uliunai', fr: 'communauté Uliunai', de: 'Uliunai Community' },
  'Part of the Uliunai community': { lt: 'Uliunai bendruomenės dalis', ru: 'Часть сообщества Uliunai', fr: 'Fait partie de la communauté Uliunai', de: 'Teil der Uliunai Community' },
  'Part of the Uliunai community - learn more': {
    lt: 'Uliunai bendruomenės dalis - sužinok daugiau',
    ru: 'Часть сообщества Uliunai - узнать больше',
    fr: 'Fait partie de la communauté Uliunai - en savoir plus',
    de: 'Teil der Uliunai Community - mehr erfahren',
  },
  'Part of the Uliunai community →': { lt: 'Uliunai bendruomenės dalis →', ru: 'Часть сообщества Uliunai →', fr: 'Fait partie de la communauté Uliunai →', de: 'Teil der Uliunai Community →' },
  'Switch Uliunai game': { lt: 'Perjungti Uliunai žaidimą', ru: 'Переключить игру Uliunai', fr: 'Changer de jeu Uliunai', de: 'Uliunai Spiel wechseln' },
  'ULIUNAI NETWORK': { lt: 'ULIUNAI TINKLAS', ru: 'СЕТЬ ULIUNAI', fr: 'RÉSEAU ULIUNAI', de: 'ULIUNAI NETZWERK' },
  'Uliunai Network': { lt: 'Uliunai tinklas', ru: 'Сеть Uliunai', fr: 'Réseau Uliunai', de: 'Uliunai Netzwerk' },
  'Uliunai Hub': { lt: 'Uliunai hubas', ru: 'Хаб Uliunai', fr: 'Hub Uliunai', de: 'Uliunai Hub' },
  'Minecraft News': { lt: 'Minecraft naujienos', ru: 'Новости Minecraft', fr: 'Actualités Minecraft', de: 'Minecraft News' },
  'Loading the latest updates…': { lt: 'Kraunami naujausi atnaujinimai…', ru: 'Загружаем последние обновления…', fr: 'Chargement des dernières mises à jour…', de: 'Neueste Updates werden geladen…' },
  'Loading the latest updates...': { lt: 'Kraunami naujausi atnaujinimai...', ru: 'Загружаем последние обновления...', fr: 'Chargement des dernières mises à jour...', de: 'Neueste Updates werden geladen...' },
  'Loading…': { lt: 'Kraunama…', ru: 'Загрузка…', fr: 'Chargement…', de: 'Lädt…' },
  'News is taking a break right now — check back soon for the latest Minecraft updates.': { lt: 'Naujienos trumpam ilsisi - netrukus patikrink naujausius Minecraft atnaujinimus.', ru: 'Новости сейчас недоступны - скоро проверь последние обновления Minecraft.', fr: 'Les news font une pause - reviens bientôt pour les dernières mises à jour Minecraft.', de: 'Die News machen gerade Pause - schau bald wieder für Minecraft Updates vorbei.' },
  'No news yet — but the servers are coming soon. Watch this space!': { lt: 'Naujienų dar nėra - bet serveriai netrukus startuos. Stebėk šią vietą!', ru: 'Новостей пока нет - но серверы скоро появятся. Следи за этим местом!', fr: 'Pas encore de news - mais les serveurs arrivent bientôt. Garde un œil ici !', de: 'Noch keine News - aber die Server kommen bald. Behalte diesen Bereich im Blick!' },
  'This post is unavailable right now — please try again soon.': {
    lt: 'Šis įrašas šiuo metu nepasiekiamas - bandyk dar kartą netrukus.',
    ru: 'Эта запись сейчас недоступна - попробуй снова скоро.',
    fr: 'Cet article est indisponible pour le moment - réessaie bientôt.',
    de: 'Dieser Beitrag ist gerade nicht verfügbar - versuche es bald erneut.',
  },
  "This post no longer exists or hasn't been published.": {
    lt: 'Šis įrašas nebeegzistuoja arba dar nepaskelbtas.',
    ru: 'Эта запись больше не существует или еще не опубликована.',
    fr: "Cet article n'existe plus ou n'a pas encore été publié.",
    de: 'Dieser Beitrag existiert nicht mehr oder wurde noch nicht veröffentlicht.',
  },
  'Pure Survival': { lt: 'Grynas išgyvenimas', ru: 'Чистое выживание', fr: 'Survie pure', de: 'Pures Survival' },
  'Plugins · no modpack': {
    lt: 'Pluginai · be modpack',
    ru: 'Плагины · без модпака',
    fr: 'Plugins · sans modpack',
    de: 'Plugins · kein Modpack',
  },
  'Modpack Experience': { lt: 'Modpack patirtis', ru: 'Опыт modpack', fr: 'Expérience modpack', de: 'Modpack-Erlebnis' },
  Edition: { lt: 'Leidimas', ru: 'Издание', fr: 'Édition', de: 'Edition' },
  'Server address': { lt: 'Serverio adresas', ru: 'Адрес сервера', fr: 'Adresse serveur', de: 'Serveradresse' },
  'How to join': { lt: 'Kaip prisijungti', ru: 'Как подключиться', fr: 'Comment rejoindre', de: 'So trittst du bei' },
  'The Modpack': { lt: 'Modpackas', ru: 'Modpack', fr: 'Le modpack', de: 'Das Modpack' },
  'Server Plugins': { lt: 'Serverio pluginai', ru: 'Плагины сервера', fr: 'Plugins serveur', de: 'Server-Plugins' },
  'Paper plugins plus Velocity auth for Uliunai Survival — no client modpack required.': {
    lt: 'Paper pluginai ir Velocity auth Uliunai Survival — kliento modpack nereikia.',
    ru: 'Paper-плагины и Velocity auth для Uliunai Survival — клиентский modpack не нужен.',
    fr: 'Plugins Paper et auth Velocity pour Uliunai Survival — aucun modpack client requis.',
    de: 'Paper-Plugins plus Velocity-Auth für Uliunai Survival — kein Client-Modpack nötig.',
  },
  List: { lt: 'Sąrašas', ru: 'Список', fr: 'Liste', de: 'Liste' },
  Cards: { lt: 'Kortelės', ru: 'Карточки', fr: 'Cartes', de: 'Karten' },
  All: { lt: 'Visi', ru: 'Все', fr: 'Tout', de: 'Alle' },
  Content: { lt: 'Turinys', ru: 'Контент', fr: 'Contenu', de: 'Inhalt' },
  Plugins: { lt: 'Pluginai', ru: 'Плагины', fr: 'Plugins', de: 'Plugins' },
  'Proxy / Auth': { lt: 'Proxy / Auth', ru: 'Proxy / Auth', fr: 'Proxy / Auth', de: 'Proxy / Auth' },
  Categories: { lt: 'Kategorijos', ru: 'Категории', fr: 'Catégories', de: 'Kategorien' },
  Claims: { lt: 'Claimai', ru: 'Клеймы', fr: 'Claims', de: 'Claims' },
  Permissions: { lt: 'Leidimai', ru: 'Права', fr: 'Permissions', de: 'Berechtigungen' },
  Economy: { lt: 'Ekonomika', ru: 'Экономика', fr: 'Économie', de: 'Wirtschaft' },
  Auth: { lt: 'Auth', ru: 'Auth', fr: 'Auth', de: 'Auth' },
  Proxy: { lt: 'Proxy', ru: 'Proxy', fr: 'Proxy', de: 'Proxy' },
  'Resource packs': { lt: 'Resursų paketai', ru: 'Ресурс-паки', fr: 'Packs de ressources', de: 'Resource Packs' },
  Shaders: { lt: 'Šeideriai', ru: 'Шейдеры', fr: 'Shaders', de: 'Shader' },
  Type: { lt: 'Tipas', ru: 'Тип', fr: 'Type', de: 'Typ' },
  Size: { lt: 'Dydis', ru: 'Размер', fr: 'Taille', de: 'Größe' },
  'Project ID': { lt: 'Projekto ID', ru: 'ID проекта', fr: 'ID projet', de: 'Projekt-ID' },
  'File ID': { lt: 'Failo ID', ru: 'ID файла', fr: 'ID fichier', de: 'Datei-ID' },
  'Versions and hashes': { lt: 'Versijos ir hashai', ru: 'Версии и хэши', fr: 'Versions et hash', de: 'Versionen und Hashes' },
  'Get CurseForge': { lt: 'Gauti CurseForge', ru: 'Получить CurseForge', fr: 'Obtenir CurseForge', de: 'CurseForge holen' },
  'Load more mods': { lt: 'Įkelti daugiau modų', ru: 'Загрузить еще моды', fr: 'Charger plus de mods', de: 'Mehr Mods laden' },
  'Load more plugins': {
    lt: 'Įkelti daugiau pluginų',
    ru: 'Загрузить еще плагины',
    fr: 'Charger plus de plugins',
    de: 'Mehr Plugins laden',
  },
  Prev: { lt: 'Ankstesnis', ru: 'Назад', fr: 'Préc.', de: 'Zurück' },
  Next: { lt: 'Kitas', ru: 'Вперед', fr: 'Suivant', de: 'Weiter' },
  Address: { lt: 'Adresas', ru: 'Адрес', fr: 'Adresse', de: 'Adresse' },
  Port: { lt: 'Portas', ru: 'Порт', fr: 'Port', de: 'Port' },
  'Connection details are placeholders — the server isn\'t live yet.': { lt: 'Prisijungimo duomenys yra laikini - serveris dar neveikia.', ru: 'Данные подключения временные - сервер еще не запущен.', fr: 'Les détails de connexion sont provisoires - le serveur n’est pas encore en ligne.', de: 'Verbindungsdaten sind Platzhalter - der Server ist noch nicht live.' },
  'What makes it special': { lt: 'Kuo tai ypatinga', ru: 'Что делает его особенным', fr: 'Ce qui le rend spécial', de: 'Was ihn besonders macht' },
  'How to join Survival': { lt: 'Kaip prisijungti prie Survival', ru: 'Как подключиться к Survival', fr: 'Comment rejoindre Survival', de: 'So trittst du Survival bei' },
  'How to join Modded': { lt: 'Kaip prisijungti prie Modded', ru: 'Как подключиться к Modded', fr: 'Comment rejoindre Modded', de: 'So trittst du Modded bei' },
  'View the mod list': { lt: 'Žiūrėti modų sąrašą', ru: 'Посмотреть список модов', fr: 'Voir la liste des mods', de: 'Modliste ansehen' },
  'View the plugin list': {
    lt: 'Žiūrėti pluginų sąrašą',
    ru: 'Посмотреть список плагинов',
    fr: 'Voir la liste des plugins',
    de: 'Pluginliste ansehen',
  },
  'View plugins': {
    lt: 'Žiūrėti pluginus',
    ru: 'Смотреть плагины',
    fr: 'Voir les plugins',
    de: 'Plugins ansehen',
  },
  'View mods': {
    lt: 'Žiūrėti modų',
    ru: 'Смотреть моды',
    fr: 'Voir les mods',
    de: 'Mods ansehen',
  },
  Commands: {
    lt: 'Komandos',
    ru: 'Команды',
    fr: 'Commandes',
    de: 'Befehle',
  },
  'Player commands': {
    lt: 'Žaidėjų komandos',
    ru: 'Команды для игроков',
    fr: 'Commandes joueur',
    de: 'Spielerbefehle',
  },
  'Default member commands on Uliunai Survival — type them in-game or use tab completion where available.': {
    lt: 'Numatytosios nario komandos Uliunai Survival — rašyk jas žaidime arba naudok Tab užbaigimą, kur galima.',
    ru: 'Стандартные команды участника на Uliunai Survival — вводите в игре или используйте Tab, где доступно.',
    fr: 'Commandes membre par défaut sur Uliunai Survival — tape-les en jeu ou utilise Tab quand c’est possible.',
    de: 'Standard-Member-Befehle auf Uliunai Survival — im Spiel eingeben oder Tab-Vervollständigung nutzen, wo möglich.',
  },
  'These are the main commands every member can use on Survival. Staff-only tools are not listed here.': {
    lt: 'Tai pagrindinės komandos, kurias gali naudoti kiekvienas narys Survival. Personalui skirtų įrankių čia nėra.',
    ru: 'Это основные команды, доступные каждому участнику на Survival. Инструменты персонала здесь не перечислены.',
    fr: 'Ce sont les principales commandes que chaque membre peut utiliser sur Survival. Les outils staff ne sont pas listés ici.',
    de: 'Das sind die wichtigsten Befehle für jedes Member auf Survival. Staff-Tools sind hier nicht aufgeführt.',
  },
  'Auth (cracked clients only)': {
    lt: 'Autentifikacija (tik cracked klientams)',
    ru: 'Авторизация (только cracked-клиенты)',
    fr: 'Auth (clients cracked uniquement)',
    de: 'Auth (nur Cracked-Clients)',
  },
  'Premium Microsoft/Mojang accounts log in automatically. These commands are only for non-premium launchers on the auth screen.': {
    lt: 'Premium Microsoft/Mojang paskyros prisijungia automatiškai. Šios komandos tik ne premium paleidimo priemonėms autentifikacijos ekrane.',
    ru: 'Premium-аккаунты Microsoft/Mojang входят автоматически. Эти команды только для non-premium лаунчеров на экране авторизации.',
    fr: 'Les comptes premium Microsoft/Mojang se connectent automatiquement. Ces commandes sont réservées aux launchers non premium sur l’écran d’auth.',
    de: 'Premium Microsoft/Mojang-Konten loggen sich automatisch ein. Diese Befehle gelten nur für Non-Premium-Launcher auf dem Auth-Bildschirm.',
  },
  'Create your account the first time you join with a cracked client.': {
    lt: 'Sukurk paskyrą pirmą kartą prisijungęs su cracked klientu.',
    ru: 'Создайте аккаунт при первом входе с cracked-клиента.',
    fr: 'Crée ton compte lors de ta première connexion avec un client cracked.',
    de: 'Erstelle dein Konto beim ersten Join mit einem Cracked-Client.',
  },
  'Sign in on later visits with the same username and password.': {
    lt: 'Vėlesniems apsilankymams prisijunk tuo pačiu vardu ir slaptažodžiu.',
    ru: 'При следующих входах используйте то же имя и пароль.',
    fr: 'Connecte-toi plus tard avec le même pseudo et mot de passe.',
    de: 'Melde dich später mit demselben Namen und Passwort an.',
  },
  'Land claims': {
    lt: 'Žemės claim\'ai',
    ru: 'Приват территорий',
    fr: 'Claims de terrain',
    de: 'Land-Claims',
  },
  'Claim tools protect your builds. Extra claim settings are available through the claim commands and in-game menus.': {
    lt: 'Claim įrankiai saugo tavo statinius. Papildomi claim nustatymai — per claim komandas ir meniu žaidime.',
    ru: 'Claim защищает постройки. Дополнительные настройки — через команды claim и меню в игре.',
    fr: 'Les claims protègent tes builds. Réglages supplémentaires via les commandes claim et les menus en jeu.',
    de: 'Claims schützen deine Builds. Weitere Einstellungen über Claim-Befehle und Menüs im Spiel.',
  },
  'Claim the chunk you are standing in so others cannot grief it.': {
    lt: 'Užclaim\'ink chunk\'ą, ant kurio stovi, kad kiti negalėtų jo grief\'inti.',
    ru: 'Заприватите чанк, на котором стоите, чтобы его не grief\'или.',
    fr: 'Claim le chunk où tu te trouves pour éviter le grief.',
    de: 'Claime den Chunk, auf dem du stehst, damit niemand griefen kann.',
  },
  'Remove your claim on the current chunk.': {
    lt: 'Pašalink claim\'ą ant dabartinio chunk\'o.',
    ru: 'Снимите приват с текущего чанка.',
    fr: 'Retire ton claim sur le chunk actuel.',
    de: 'Entferne deinen Claim im aktuellen Chunk.',
  },
  'List or manage your claims.': {
    lt: 'Peržiūrėk ar valdyk savo claim\'us.',
    ru: 'Список и управление вашими claim.',
    fr: 'Liste ou gère tes claims.',
    de: 'Liste oder verwalte deine Claims.',
  },
  Chat: { lt: 'Pokalbiai', ru: 'Чат', fr: 'Chat', de: 'Chat' },
  'Send a private message to another player.': {
    lt: 'Siųsk privačią žinutę kitam žaidėjui.',
    ru: 'Отправить личное сообщение другому игроку.',
    fr: 'Envoie un message privé à un autre joueur.',
    de: 'Sende einem anderen Spieler eine private Nachricht.',
  },
  'Reply to the last player who messaged you.': {
    lt: 'Atsakyk paskutiniam žaidėjui, kuris tau rašė.',
    ru: 'Ответить последнему игроку, который писал вам.',
    fr: 'Réponds au dernier joueur qui t’a écrit.',
    de: 'Antworte dem letzten Spieler, der dir geschrieben hat.',
  },
  'Ignore chat and messages from a player.': {
    lt: 'Ignoruok pokalbius ir žinutes nuo žaidėjo.',
    ru: 'Игнорировать чат и сообщения от игрока.',
    fr: 'Ignore le chat et les messages d’un joueur.',
    de: 'Ignoriere Chat und Nachrichten eines Spielers.',
  },
  'See who is online.': {
    lt: 'Sužinok, kas prisijungę.',
    ru: 'Посмотреть, кто онлайн.',
    fr: 'Voir qui est en ligne.',
    de: 'Sieh, wer online ist.',
  },
  'Open Essentials help.': {
    lt: 'Atidaryk Essentials pagalbą.',
    ru: 'Открыть справку Essentials.',
    fr: 'Ouvre l’aide Essentials.',
    de: 'Öffne die Essentials-Hilfe.',
  },
  'Read the server rules in chat.': {
    lt: 'Perskaityk serverio taisykles pokalbyje.',
    ru: 'Прочитать правила сервера в чате.',
    fr: 'Lis les règles du serveur dans le chat.',
    de: 'Lies die Serverregeln im Chat.',
  },
  'Money & shops': {
    lt: 'Pinigai ir parduotuvės',
    ru: 'Деньги и магазины',
    fr: 'Argent et boutiques',
    de: 'Geld & Shops',
  },
  'Check your balance.': {
    lt: 'Patikrink savo balansą.',
    ru: 'Проверить баланс.',
    fr: 'Consulte ton solde.',
    de: 'Prüfe dein Guthaben.',
  },
  'Send money to another player.': {
    lt: 'Persiųsk pinigus kitam žaidėjui.',
    ru: 'Перевести деньги другому игроку.',
    fr: 'Envoie de l’argent à un autre joueur.',
    de: 'Sende Geld an einen anderen Spieler.',
  },
  'See the richest players on the server.': {
    lt: 'Sužinok turtingiausius serverio žaidėjus.',
    ru: 'Топ богатейших игроков на сервере.',
    fr: 'Voir les joueurs les plus riches du serveur.',
    de: 'Sieh die reichsten Spieler auf dem Server.',
  },
  'Open the server shop GUI.': {
    lt: 'Atidaryk serverio parduotuvės langą.',
    ru: 'Открыть GUI серверного магазина.',
    fr: 'Ouvre la boutique serveur.',
    de: 'Öffne den Server-Shop.',
  },
  'Sell items through the shop system.': {
    lt: 'Parduok daiktus per parduotuvės sistemą.',
    ru: 'Продать предметы через систему магазина.',
    fr: 'Vends des objets via le système de boutique.',
    de: 'Verkaufe Items über das Shop-System.',
  },
  'Sell the item in your hand.': {
    lt: 'Parduok daiktą rankoje.',
    ru: 'Продать предмет в руке.',
    fr: 'Vends l’objet dans ta main.',
    de: 'Verkaufe das Item in deiner Hand.',
  },
  'Manage your player chest shop (create and edit your own shop).': {
    lt: 'Valdyk savo chest shop (kurk ir redaguok savo parduotuvę).',
    ru: 'Управление своим chest shop (создание и редактирование).',
    fr: 'Gère ta boutique coffre (créer et modifier ta boutique).',
    de: 'Verwalte deinen Chest-Shop (eigenen Shop anlegen und bearbeiten).',
  },
  'Worlds & travel': {
    lt: 'Pasauliai ir kelionės',
    ru: 'Миры и перемещение',
    fr: 'Mondes et voyage',
    de: 'Welten & Reisen',
  },
  'Some worlds and lobby portals are reached by walking through portal regions — no command needed.': {
    lt: 'Kai kurie pasauliai ir lobby portalai pasiekiami einant per portalų zonas — komandos nereikia.',
    ru: 'Некоторые миры и порталы лobby — просто пройдите через портал, команда не нужна.',
    fr: 'Certains mondes et portails du lobby se prennent en traversant une zone portail — pas de commande.',
    de: 'Manche Welten und Lobby-Portale erreichst du durch Portalzonen — kein Befehl nötig.',
  },
  'List worlds you can travel to.': {
    lt: 'Išvardyk pasaulius, į kuriuos gali keliauti.',
    ru: 'Список миров, куда можно телепортироваться.',
    fr: 'Liste les mondes où tu peux te téléporter.',
    de: 'Liste Welten auf, zu denen du reisen darfst.',
  },
  'Teleport yourself to an allowed world.': {
    lt: 'Teleportuokis į leidžiamą pasaulį.',
    ru: 'Телепорт в разрешённый мир.',
    fr: 'Téléporte-toi vers un monde autorisé.',
    de: 'Teleportiere dich in eine erlaubte Welt.',
  },
  Tags: { lt: 'Žymos', ru: 'Теги', fr: 'Tags', de: 'Tags' },
  'Open the cosmetic tags menu and pick a tag you have unlocked.': {
    lt: 'Atidaryk kosmetinių žymų meniu ir pasirink atrakintą žymą.',
    ru: 'Открыть меню косметических тегов и выбрать разблокированный тег.',
    fr: 'Ouvre le menu de tags cosmétiques et choisis un tag débloqué.',
    de: 'Öffne das Kosmetik-Tags-Menü und wähle ein freigeschaltetes Tag.',
  },
  Previous: { lt: 'Ankstesnis', ru: 'Предыдущий', fr: 'Précédent', de: 'Vorherige' },
  'Community-wide conduct': { lt: 'Bendruomenės elgesys', ru: 'Правила поведения сообщества', fr: 'Conduite communautaire', de: 'Community-weites Verhalten' },
  'Hours played': { lt: 'Žaista valandų', ru: 'Часов сыграно', fr: 'Heures jouées', de: 'Gespielte Stunden' },
  'Blocks mined': { lt: 'Iškasta blokų', ru: 'Блоков добыто', fr: 'Blocs minés', de: 'Blöcke abgebaut' },
  'Blocks placed': { lt: 'Padėta blokų', ru: 'Блоков поставлено', fr: 'Blocs placés', de: 'Blöcke platziert' },
  Advancements: { lt: 'Pasiekimai', ru: 'Достижения', fr: 'Progrès', de: 'Fortschritte' },
  'Top players': { lt: 'Geriausi žaidėjai', ru: 'Лучшие игроки', fr: 'Meilleurs joueurs', de: 'Top-Spieler' },
  'Chunk not found': { lt: 'Chunk nerastas', ru: 'Чанк не найден', fr: 'Chunk introuvable', de: 'Chunk nicht gefunden' },
  "This block doesn't exist on our map. Let's get you back to spawn.": { lt: 'Šio bloko mūsų žemėlapyje nėra. Grąžinkime tave į spawn.', ru: 'Этого блока нет на нашей карте. Вернем тебя на спавн.', fr: 'Ce bloc n’existe pas sur notre carte. Ramenons-toi au spawn.', de: 'Diesen Block gibt es auf unserer Karte nicht. Zurück zum Spawn.' },
  'Check back soon for community updates and announcements.': { lt: 'Netrukus sugrįžk bendruomenės naujienoms ir pranešimams.', ru: 'Загляни скоро за новостями и объявлениями сообщества.', fr: 'Reviens bientôt pour les mises à jour et annonces de la communauté.', de: 'Schau bald wieder für Community-Updates und Ankündigungen vorbei.' },
  'Check back soon for server updates and announcements.': { lt: 'Netrukus sugrįžk serverio naujienoms ir pranešimams.', ru: 'Загляни скоро за обновлениями сервера и объявлениями.', fr: 'Reviens bientôt pour les mises à jour serveur et les annonces.', de: 'Schau bald wieder für Server-Updates und Ankündigungen vorbei.' },
  'Announcements, updates, and everything happening across the Uliunai community.': { lt: 'Pranešimai, atnaujinimai ir viskas, kas vyksta Uliunai bendruomenėje.', ru: 'Объявления, обновления и всё, что происходит в сообществе Uliunai.', fr: 'Annonces, mises à jour et tout ce qui se passe dans la communauté Uliunai.', de: 'Ankündigungen, Updates und alles, was in der Uliunai Community passiert.' },
  'Server updates, new perks, events, and announcements from the Killing Floor front.': { lt: 'Serverio atnaujinimai, nauji perkai, renginiai ir pranešimai iš Killing Floor fronto.', ru: 'Обновления сервера, новые перки, события и объявления с фронта Killing Floor.', fr: 'Mises à jour serveur, nouveaux perks, événements et annonces du front Killing Floor.', de: 'Server-Updates, neue Perks, Events und Ankündigungen von der Killing-Floor-Front.' },
  "Couldn't load the news": { lt: 'Nepavyko įkelti naujienų', ru: 'Не удалось загрузить новости', fr: 'Impossible de charger les actualités', de: 'News konnten nicht geladen werden' },
  "Couldn't load this post": { lt: 'Nepavyko įkelti šio įrašo', ru: 'Не удалось загрузить эту запись', fr: 'Impossible de charger cet article', de: 'Dieser Beitrag konnte nicht geladen werden' },
  'This update may have been removed or never existed.': { lt: 'Šis atnaujinimas galėjo būti pašalintas arba niekada neegzistavo.', ru: 'Это обновление могло быть удалено или никогда не существовало.', fr: 'Cette mise à jour a peut-être été supprimée ou n’a jamais existé.', de: 'Dieses Update wurde möglicherweise entfernt oder hat nie existiert.' },
  'Last updated:': { lt: 'Paskutinį kartą atnaujinta:', ru: 'Последнее обновление:', fr: 'Dernière mise à jour :', de: 'Zuletzt aktualisiert:' },
  Copyright: { lt: 'Autorių teisės', ru: 'Авторские права', fr: 'Copyright', de: 'Copyright' },
  'All rights reserved.': {
    lt: 'Visos teisės saugomos.',
    ru: 'Все права защищены.',
    fr: 'Tous droits réservés.',
    de: 'Alle Rechte vorbehalten.',
  },
  'Designed by': { lt: 'Sukūrė', ru: 'Дизайн', fr: 'Conçu par', de: 'Entworfen von' },
  characters: { lt: 'simbolių', ru: 'символов', fr: 'caractères', de: 'Zeichen' },
  'Go to featured post': {
    lt: 'Eiti į išskirtą įrašą',
    ru: 'Перейти к важной записи',
    fr: "Aller à l'article à la une",
    de: 'Zum empfohlenen Beitrag',
  },
  'These universal rules apply across': {
    lt: 'Šios bendros taisyklės galioja visuose',
    ru: 'Эти универсальные правила действуют на всех',
    fr: "Ces règles universelles s'appliquent à",
    de: 'Diese gemeinsamen Regeln gelten auf',
  },
  'every Uliunai server': {
    lt: 'Uliunai serveriuose',
    ru: 'серверах Uliunai',
    fr: 'tous les serveurs Uliunai',
    de: 'jedem Uliunai Server',
  },
  "Killing Floor, Minecraft, and everything we host. Each game also has its own game-specific rules on its own site; those never repeat what's below. By playing on any Uliunai server you agree to follow this code.": {
    lt: 'Killing Floor, Minecraft ir viskam, ką hostiname. Kiekvienas žaidimas savo svetainėje taip pat turi žaidimui skirtas taisykles; jos nekartoja to, kas pateikta žemiau. Žaisdamas bet kuriame Uliunai serveryje sutinki laikytis šio kodekso.',
    ru: 'Killing Floor, Minecraft и всем, что мы размещаем. У каждой игры на своем сайте есть дополнительные правила; они не повторяют написанное ниже. Играя на любом сервере Uliunai, ты соглашаешься соблюдать этот кодекс.',
    fr: "Killing Floor, Minecraft et tout ce que nous hébergeons. Chaque jeu a aussi ses règles spécifiques sur son propre site ; elles ne répètent pas ce qui suit. En jouant sur un serveur Uliunai, tu acceptes de respecter ce code.",
    de: 'Killing Floor, Minecraft und alles, was wir hosten. Jedes Spiel hat zusätzlich eigene Regeln auf seiner Website; sie wiederholen nicht, was unten steht. Wenn du auf einem Uliunai Server spielst, stimmst du diesem Kodex zu.',
  },
  'Staff have the final say on enforcement. Breaking these rules may lead to a warning, mute, kick, or ban depending on severity.': {
    lt: 'Galutinį sprendimą dėl taisyklių taikymo priima komanda. Pažeidimai, priklausomai nuo rimtumo, gali baigtis įspėjimu, nutildymu, išmetimu arba draudimu.',
    ru: 'Окончательное решение по применению правил принимает персонал. Нарушения могут привести к предупреждению, муту, кику или бану в зависимости от тяжести.',
    fr: "Le staff a le dernier mot sur l'application des règles. Les infractions peuvent entraîner un avertissement, un mute, un kick ou un bannissement selon leur gravité.",
    de: 'Das Team hat bei der Durchsetzung das letzte Wort. Regelverstöße können je nach Schwere zu Warnung, Mute, Kick oder Ban führen.',
  },
  'Questions about this document? Reach an admin on': {
    lt: 'Klausimų apie šį dokumentą? Susisiek su adminu per',
    ru: 'Есть вопросы по этому документу? Свяжись с админом в',
    fr: 'Des questions sur ce document ? Contacte un admin sur',
    de: 'Fragen zu diesem Dokument? Erreiche einen Admin über',
  },
  'or via the': { lt: 'arba per', ru: 'или через', fr: 'ou via le', de: 'oder über das' },
  'contact form': { lt: 'kontaktinę formą', ru: 'контактную форму', fr: 'formulaire de contact', de: 'Kontaktformular' },
  'How we handle your data': {
    lt: 'Kaip tvarkome tavo duomenis',
    ru: 'Как мы обрабатываем твои данные',
    fr: 'Comment nous gérons tes données',
    de: 'Wie wir mit deinen Daten umgehen',
  },
  Privacy: { lt: 'Privatumas', ru: 'Конфиденциальность', fr: 'Confidentialité', de: 'Datenschutz' },
  Policy: { lt: 'Politika', ru: 'Политика', fr: 'Politique', de: 'Richtlinie' },
  'The rules of the road': {
    lt: 'Pagrindinės taisyklės',
    ru: 'Основные условия',
    fr: 'Les règles du jeu',
    de: 'Die Spielregeln',
  },
  'Terms of': { lt: 'Paslaugų', ru: 'Условия', fr: 'Conditions de', de: 'Nutzungs' },
  Service: { lt: 'sąlygos', ru: 'сервиса', fr: 'service', de: 'bedingungen' },
  'Data We Collect': { lt: 'Kokius duomenis renkame', ru: 'Какие данные мы собираем', fr: 'Données collectées', de: 'Welche Daten wir sammeln' },
  'Description of Service': { lt: 'Paslaugos aprašymas', ru: 'Описание сервиса', fr: 'Description du service', de: 'Beschreibung des Dienstes' },
  'Rules of Conduct': { lt: 'Elgesio taisyklės', ru: 'Правила поведения', fr: 'Règles de conduite', de: 'Verhaltensregeln' },
  'Not Found': { lt: 'Nerasta', ru: 'Не найдено', fr: 'Introuvable', de: 'Nicht gefunden' },

  // --- Hub game badges / CTAs ---
  'Vanilla Live': { lt: 'Vanilla veikia', ru: 'Vanilla онлайн', fr: 'Vanilla en ligne', de: 'Vanilla live' },
  'Modded Soon': { lt: 'Modded netrukus', ru: 'Modded скоро', fr: 'Moddé bientôt', de: 'Modded bald' },
  'Play Vanilla': { lt: 'Žaisti Vanilla', ru: 'Играть Vanilla', fr: 'Jouer Vanilla', de: 'Vanilla spielen' },
  'Vanilla live · Modded coming soon': {
    lt: 'Vanilla veikia · Modded netrukus',
    ru: 'Vanilla онлайн · Modded скоро',
    fr: 'Vanilla en ligne · Moddé bientôt',
    de: 'Vanilla live · Modded bald',
  },

  // --- Minecraft home / chrome ---
  'Play Survival now, with a separate curated Modded adventure planned for Java Edition.': {
    lt: 'Žaisk Survival dabar, o atskira atrinkta Modded kelionė planuojama Java Edition.',
    ru: 'Играй в Survival сейчас; отдельное curated Modded приключение планируется для Java Edition.',
    fr: 'Joue en Survival maintenant, avec une aventure Modded séparée et sélectionnée prévue pour Java Edition.',
    de: 'Spiel jetzt Survival; ein separates kuratiertes Modded-Abenteuer ist für Java Edition geplant.',
  },
  'Play Vanilla survival now, with a separate curated Modded adventure planned for Java Edition.': {
    lt: 'Žaisk Vanilla išgyvenimą dabar, o atskira atrinkta Modded kelionė planuojama Java Edition.',
    ru: 'Играй в Vanilla survival сейчас; отдельное curated Modded приключение планируется для Java Edition.',
    fr: 'Joue en survie Vanilla maintenant, avec une aventure Modded séparée et sélectionnée prévue pour Java Edition.',
    de: 'Spiel jetzt Vanilla Survival; ein separates kuratiertes Modded-Abenteuer ist für Java Edition geplant.',
  },
  'Survival server live now': {
    lt: 'Survival serveris veikia dabar',
    ru: 'Сервер Survival уже онлайн',
    fr: 'Serveur Survival en ligne',
    de: 'Survival Server jetzt live',
  },
  'Vanilla server live now': {
    lt: 'Vanilla serveris veikia dabar',
    ru: 'Vanilla сервер уже онлайн',
    fr: 'Serveur Vanilla en ligne',
    de: 'Vanilla-Server jetzt live',
  },
  'Explore the servers': {
    lt: 'Naršyti serverius',
    ru: 'Смотреть серверы',
    fr: 'Explorer les serveurs',
    de: 'Server entdecken',
  },
  'See the modpack': {
    lt: 'Žiūrėti modpack',
    ru: 'Смотреть modpack',
    fr: 'Voir le modpack',
    de: 'Modpack ansehen',
  },
  'Scroll to servers': {
    lt: 'Slinkti prie serverių',
    ru: 'Перейти к серверам',
    fr: 'Aller aux serveurs',
    de: 'Zu den Servern scrollen',
  },
  'Pick Survival or a curated modded adventure — both run by the same friendly Uliunai crew.': {
    lt: 'Rinkis Survival ar atrinktą modded kelionę — abu veda ta pati draugiška Uliunai komanda.',
    ru: 'Выбери Survival или curated modded приключение — оба ведёт та же дружелюбная команда Uliunai.',
    fr: 'Choisis la Survival ou une aventure moddée sélectionnée — les deux sont gérés par la même équipe Uliunai.',
    de: 'Wähle Survival oder ein kuratiertes Modded-Abenteuer — beides betreut dasselbe freundliche Uliunai-Team.',
  },
  'Pick pure vanilla survival or a curated modded adventure — both run by the same friendly Uliunai crew.': {
    lt: 'Rinkis gryną vanilla išgyvenimą ar atrinktą modded kelionę — abu veda ta pati draugiška Uliunai komanda.',
    ru: 'Выбери чистое vanilla survival или curated modded приключение — оба ведёт та же дружелюбная команда Uliunai.',
    fr: 'Choisis la survie vanilla pure ou une aventure moddée sélectionnée — les deux sont gérés par la même équipe Uliunai.',
    de: 'Wähle reines Vanilla-Survival oder ein kuratiertes Modded-Abenteuer — beides betreut dasselbe freundliche Uliunai-Team.',
  },
  'Survival is online now at': {
    lt: 'Survival dabar veikia adresu',
    ru: 'Survival уже онлайн по адресу',
    fr: 'Survival est en ligne maintenant sur',
    de: 'Survival ist jetzt online unter',
  },
  'Vanilla is online now at': {
    lt: 'Vanilla dabar veikia adresu',
    ru: 'Vanilla уже онлайн по адресу',
    fr: 'Vanilla est en ligne maintenant sur',
    de: 'Vanilla ist jetzt online unter',
  },
  'No client mods or modpack are required.': {
    lt: 'Kliento modų ar modpack nereikia.',
    ru: 'Клиентские моды или modpack не нужны.',
    fr: 'Aucun mods client ni modpack n’est requis.',
    de: 'Keine Client-Mods oder Modpacks nötig.',
  },
  'Browse mods': { lt: 'Naršyti modus', ru: 'Смотреть моды', fr: 'Parcourir les mods', de: 'Mods durchsuchen' },
  'Browse plugins': {
    lt: 'Naršyti pluginus',
    ru: 'Смотреть плагины',
    fr: 'Parcourir les plugins',
    de: 'Plugins durchsuchen',
  },
  'More than a server — a community': {
    lt: 'Daugiau nei serveris — bendruomenė',
    ru: 'Больше, чем сервер — сообщество',
    fr: 'Plus qu’un serveur — une communauté',
    de: 'Mehr als ein Server — eine Community',
  },
  'Uliunai Minecraft is part of the wider Uliunai gaming community: the same people, values and care that have kept our servers running for years, now block by block.': {
    lt: 'Uliunai Minecraft yra platesnės Uliunai žaidimų bendruomenės dalis: tie patys žmonės, vertybės ir rūpestis, kurie mūsų serverius laiko veikiančius metus, dabar blokas po bloko.',
    ru: 'Uliunai Minecraft — часть более широкого игрового сообщества Uliunai: те же люди, ценности и забота, которые годами держат наши серверы, теперь блок за блоком.',
    fr: 'Uliunai Minecraft fait partie de la communauté Uliunai élargie : les mêmes personnes, valeurs et soin qui font tourner nos serveurs depuis des années, bloc par bloc.',
    de: 'Uliunai Minecraft ist Teil der größeren Uliunai Gaming Community: dieselben Menschen, Werte und Fürsorge, die unsere Server seit Jahren am Laufen halten — jetzt Block für Block.',
  },
  'We started as a single game community and grew into a small network of servers. Minecraft is our latest home — built the same way as the rest: friendly first, fair always, and run by people who actually play here.': {
    lt: 'Pradėjome kaip vieno žaidimo bendruomenė ir išaugome į nedidelį serverių tinklą. Minecraft — mūsų naujausi namai, pastatyti taip pat: draugiškumas pirmiausia, sąžiningumas visada, ir juos veda žmonės, kurie čia iš tikrųjų žaidžia.',
    ru: 'Мы начинали как сообщество одной игры и выросли в небольшую сеть серверов. Minecraft — наш новый дом, построенный так же: дружелюбие прежде всего, честность всегда, и им руководят люди, которые здесь реально играют.',
    fr: 'Nous avons commencé comme communauté d’un seul jeu et sommes devenus un petit réseau de serveurs. Minecraft est notre dernière maison — construite de la même façon : amical d’abord, juste toujours, géré par des gens qui y jouent vraiment.',
    de: 'Wir begannen als Community für ein Spiel und wuchsen zu einem kleinen Server-Netzwerk. Minecraft ist unser neues Zuhause — genauso gebaut: freundlich zuerst, fair immer, geführt von Leuten, die hier wirklich spielen.',
  },
  'Whether you came for relaxed Survival or a deep Modded grind, you join one community with shared staff, shared rules and a shared Discord.': {
    lt: 'Ar atėjai dėl ramaus Survival išgyvenimo, ar gilesnio Modded grindo — prisijungi prie vienos bendruomenės su bendra komanda, taisyklėmis ir Discord.',
    ru: 'Пришёл ли ты за спокойным Survival или глубоким Modded grind — ты входишь в одно сообщество с общими staff, правилами и Discord.',
    fr: 'Que tu viennes pour une Survival détendue ou un grind Modded profond, tu rejoins une seule communauté avec staff, règles et Discord partagés.',
    de: 'Ob du für entspanntes Survival oder tiefen Modded-Grind kommst — du trittst einer Community mit gemeinsamem Team, Regeln und Discord bei.',
  },
  'Whether you came for relaxed Vanilla survival or a deep Modded grind, you join one community with shared staff, shared rules and a shared Discord.': {
    lt: 'Ar atėjai dėl ramaus Vanilla išgyvenimo, ar gilesnio Modded grindo — prisijungi prie vienos bendruomenės su bendra komanda, taisyklėmis ir Discord.',
    ru: 'Пришёл ли ты за спокойным Vanilla survival или глубоким Modded grind — ты входишь в одно сообщество с общими staff, правилами и Discord.',
    fr: 'Que tu viennes pour une survie Vanilla détendue ou un grind Modded profond, tu rejoins une seule communauté avec staff, règles et Discord partagés.',
    de: 'Ob du für entspanntes Vanilla-Survival oder tiefen Modded-Grind kommst — du trittst einer Community mit gemeinsamem Team, Regeln und Discord bei.',
  },
  'Fair & friendly': { lt: 'Sąžininga ir draugiška', ru: 'Честно и дружелюбно', fr: 'Juste et amical', de: 'Fair und freundlich' },
  'Clear rules, active staff, friendly chat, and a server that stays fun for everyone.': {
    lt: 'Aiškios taisyklės, aktyvi komanda, draugiškas pokalbis ir serveris, kuris lieka smagus visiems.',
    ru: 'Понятные правила, активный staff, дружелюбный чат и сервер, который остаётся весёлым для всех.',
    fr: 'Règles claires, staff actif, chat amical et un serveur qui reste fun pour tout le monde.',
    de: 'Klare Regeln, aktives Team, freundlicher Chat und ein Server, der für alle spaßig bleibt.',
  },
  'Pick your style': { lt: 'Rinkis stilių', ru: 'Выбери свой стиль', fr: 'Choisis ton style', de: 'Wähle deinen Stil' },
  'Survival or a curated Modded adventure — two worlds, the same welcoming crew.': {
    lt: 'Survival ar atrinkta Modded kelionė — du pasauliai, ta pati svetinga komanda.',
    ru: 'Survival или curated Modded приключение — два мира, одна тёплая команда.',
    fr: 'Survival ou aventure Modded sélectionnée — deux mondes, la même équipe accueillante.',
    de: 'Survival oder kuratiertes Modded-Abenteuer — zwei Welten, dieselbe einladende Crew.',
  },
  'Pure Vanilla survival or a curated Modded adventure — two worlds, the same welcoming crew.': {
    lt: 'Grynas Vanilla išgyvenimas ar atrinkta Modded kelionė — du pasauliai, ta pati svetinga komanda.',
    ru: 'Чистое Vanilla survival или curated Modded приключение — два мира, одна тёплая команда.',
    fr: 'Survie Vanilla pure ou aventure Modded sélectionnée — deux mondes, la même équipe accueillante.',
    de: 'Pures Vanilla-Survival oder kuratiertes Modded-Abenteuer — zwei Welten, dieselbe einladende Crew.',
  },
  'Part of Uliunai': { lt: 'Uliunai dalis', ru: 'Часть Uliunai', fr: 'Partie d’Uliunai', de: 'Teil von Uliunai' },
  'Backed by the long-running Uliunai gaming community. Minecraft is one server in a growing network.': {
    lt: 'Remiama ilgametės Uliunai žaidimų bendruomenės. Minecraft — vienas serveris augančiame tinkle.',
    ru: 'При поддержке давно работающего игрового сообщества Uliunai. Minecraft — один сервер в растущей сети.',
    fr: 'Soutenu par la communauté Uliunai de longue date. Minecraft est un serveur dans un réseau en croissance.',
    de: 'Getragen von der langjährigen Uliunai Gaming Community. Minecraft ist ein Server in einem wachsenden Netzwerk.',
  },
  'Explore the whole Uliunai community': {
    lt: 'Naršyti visą Uliunai bendruomenę',
    ru: 'Изучить всё сообщество Uliunai',
    fr: 'Explorer toute la communauté Uliunai',
    de: 'Die ganze Uliunai Community entdecken',
  },
  'House rules': { lt: 'Namų taisyklės', ru: 'Домашние правила', fr: 'Règles de la maison', de: 'Hausregeln' },
  'How we play together': {
    lt: 'Kaip žaidžiame kartu',
    ru: 'Как мы играем вместе',
    fr: 'Comment on joue ensemble',
    de: 'Wie wir zusammen spielen',
  },
  'A short, shared code of conduct that applies across every Uliunai server. Be decent, play fair, keep it fun.': {
    lt: 'Trumpas bendras elgesio kodeksas, galiojantis visuose Uliunai serveriuose. Būk padorus, žaisk sąžiningai, laikyk smagumą.',
    ru: 'Короткий общий кодекс поведения для всех серверов Uliunai. Будь нормальным, играй честно, сохраняй веселье.',
    fr: 'Un court code de conduite partagé sur tous les serveurs Uliunai. Sois correct, joue fair-play, garde le fun.',
    de: 'Ein kurzer gemeinsamer Verhaltenskodex auf allen Uliunai Servern. Sei anständig, spiel fair, halte es spaßig.',
  },
  'These apply across all Uliunai servers — read the full Code of Conduct': {
    lt: 'Tai galioja visuose Uliunai serveriuose — skaityk visą elgesio kodeksą',
    ru: 'Это действует на всех серверах Uliunai — читай полный кодекс',
    fr: 'Cela s’applique à tous les serveurs Uliunai — lis le code de conduite complet',
    de: 'Das gilt auf allen Uliunai Servern — lies den vollständigen Verhaltenskodex',
  },
  'Read the full Minecraft rules': {
    lt: 'Skaityti visas Minecraft taisykles',
    ru: 'Читать все правила Minecraft',
    fr: 'Lire toutes les règles Minecraft',
    de: 'Alle Minecraft-Regeln lesen',
  },
  'From the world': { lt: 'Iš pasaulio', ru: 'Из мира', fr: 'Du monde', de: 'Aus der Welt' },
  'Built by the community': {
    lt: 'Sukurta bendruomenės',
    ru: 'Создано сообществом',
    fr: 'Construit par la communauté',
    de: 'Von der Community gebaut',
  },
  'Villages, boss fights and views from across the Uliunai survival map — shaders on.': {
    lt: 'Kaimai, bosų kovos ir vaizdai iš Uliunai survival žemėlapio — su šeideriais.',
    ru: 'Деревни, боссы и виды с survival-карты Uliunai — с шейдерами.',
    fr: 'Villages, combats de boss et vues de la map survival Uliunai — shaders activés.',
    de: 'Dörfer, Bosskämpfe und Ausblicke über die Uliunai Survival-Map — Shader an.',
  },
  'View the full gallery': {
    lt: 'Žiūrėti visą galeriją',
    ru: 'Смотреть всю галерею',
    fr: 'Voir toute la galerie',
    de: 'Ganze Galerie ansehen',
  },
  'Meet the team': { lt: 'Susipažink su komanda', ru: 'Познакомься с командой', fr: "L'équipe", de: 'Das Team' },
  'The people behind the blocks': {
    lt: 'Žmonės už blokų',
    ru: 'Люди за блоками',
    fr: 'Les gens derrière les blocs',
    de: 'Die Menschen hinter den Blöcken',
  },
  'Our shared Uliunai leadership keeps every server — Minecraft included — running and welcoming.': {
    lt: 'Bendra Uliunai vadovybė laiko kiekvieną serverį — ir Minecraft — veikiantį ir svetingą.',
    ru: 'Общее руководство Uliunai поддерживает каждый сервер — включая Minecraft — в рабочем и приветливом состоянии.',
    fr: 'Le leadership partagé Uliunai fait tourner chaque serveur — Minecraft inclus — de façon accueillante.',
    de: 'Die gemeinsame Uliunai-Führung hält jeden Server — Minecraft inklusive — am Laufen und einladend.',
  },
  'Meet the whole team on the Hub': {
    lt: 'Sutik visą komandą Hube',
    ru: 'Вся команда на Hub',
    fr: 'Toute l’équipe sur le Hub',
    de: 'Das ganze Team im Hub',
  },

  // --- Minecraft servers / join / rules ---
  'Our Servers': { lt: 'Mūsų serveriai', ru: 'Наши серверы', fr: 'Nos serveurs', de: 'Unsere Server' },
  'Two distinct ways to play, both on Minecraft Java Edition. Pick the one that fits your style — or play both.': {
    lt: 'Du skirtingi žaidimo būdai, abu Minecraft Java Edition. Rinkis stilių — ar žaisk abiejuose.',
    ru: 'Два разных способа играть, оба на Minecraft Java Edition. Выбери стиль — или играй в обоих.',
    fr: 'Deux façons distinctes de jouer, toutes deux sur Minecraft Java Edition. Choisis ton style — ou joue aux deux.',
    de: 'Zwei verschiedene Spielweisen, beide auf Minecraft Java Edition. Wähle deinen Stil — oder spiele beides.',
  },
  'Live now — connect with an unmodified Java Edition client.': {
    lt: 'Veikia dabar — junkis su nepakeistu Java Edition klientu.',
    ru: 'Уже онлайн — подключайся с обычным клиентом Java Edition.',
    fr: 'En ligne maintenant — connecte-toi avec un client Java Edition non modifié.',
    de: 'Jetzt live — verbinde dich mit einem unmodifizierten Java-Edition-Client.',
  },
  'Land claims, economy/shops, and staff rollback keep the world fair': {
    lt: 'Žemės claimai, ekonomika/parduotuvės ir komandos rollback saugo pasaulį sąžiningą',
    ru: 'Claim’ы, экономика/магазины и staff rollback держат мир честным',
    fr: 'Claims, économie/boutiques et rollback du staff gardent le monde équitable',
    de: 'Land-Claims, Wirtschaft/Shops und Team-Rollback halten die Welt fair',
  },
  'Ranks, nametags, and cosmetic tags via LuckPerms + TAB': {
    lt: 'Rangai, nametagai ir kosmetiniai tagai per LuckPerms + TAB',
    ru: 'Ранги, nametag’и и косметические теги через LuckPerms + TAB',
    fr: 'Rangs, nametags et tags cosmétiques via LuckPerms + TAB',
    de: 'Ränge, Nametags und kosmetische Tags über LuckPerms + TAB',
  },
  'No client modpack required — join with stock Java Edition': {
    lt: 'Kliento modpack nereikia — junkis su standartine Java Edition',
    ru: 'Клиентский modpack не нужен — заходи со стандартной Java Edition',
    fr: 'Aucun modpack client requis — rejoins avec Java Edition standard',
    de: 'Kein Client-Modpack nötig — beitreten mit Standard-Java-Edition',
  },
  'Client-side performance mods (e.g. Sodium) are still allowed': {
    lt: 'Kliento našumo modai (pvz. Sodium) vis dar leidžiami',
    ru: 'Клиентские performance-моды (напр. Sodium) по-прежнему разрешены',
    fr: 'Les mods de performance client (ex. Sodium) restent autorisés',
    de: 'Clientseitige Performance-Mods (z. B. Sodium) sind weiterhin erlaubt',
  },
  'Vanilla survival gameplay — no client mods or modpack required': {
    lt: 'Vanilla išgyvenimo žaidimas — kliento modų ar modpack nereikia',
    ru: 'Vanilla survival — без клиентских модов и modpack',
    fr: 'Gameplay survie Vanilla — aucun mods client ni modpack requis',
    de: 'Vanilla-Survival-Gameplay — keine Client-Mods oder Modpacks nötig',
  },
  'Progression-focused survival with clear rules and active staff': {
    lt: 'Progresu orientuotas išgyvenimas su aiškiomis taisyklėmis ir aktyvia komanda',
    ru: 'Survival с акцентом на прогресс, ясными правилами и активным staff',
    fr: 'Survie orientée progression, règles claires et staff actif',
    de: 'Progressionsorientiertes Survival mit klaren Regeln und aktivem Team',
  },
  'Simple land claims plus staff rollback tools keep builds safe': {
    lt: 'Paprasti žemės claimai ir komandos rollback įrankiai saugo statinius',
    ru: 'Простые claim’ы и rollback-инструменты staff защищают постройки',
    fr: 'Claims simples et outils de rollback du staff protègent les builds',
    de: 'Einfache Land-Claims und Team-Rollback-Tools schützen Builds',
  },
  'Only client-side performance mods (e.g. Sodium) allowed': {
    lt: 'Leidžiami tik kliento našumo modai (pvz. Sodium)',
    ru: 'Разрешены только клиентские performance-моды (напр. Sodium)',
    fr: 'Seuls les mods de performance client (ex. Sodium) sont autorisés',
    de: 'Nur clientseitige Performance-Mods (z. B. Sodium) erlaubt',
  },
  'A curated modpack: tech, magic, world-gen and quality-of-life': {
    lt: 'Atrinktas modpack: tech, magija, world-gen ir QoL',
    ru: 'Curated modpack: tech, магия, world-gen и QoL',
    fr: 'Un modpack sélectionné : tech, magie, world-gen et QoL',
    de: 'Kuratiertes Modpack: Tech, Magie, World-Gen und QoL',
  },
  'Automate everything with Create, AE2 and more': {
    lt: 'Automatizuok viską su Create, AE2 ir daugiau',
    ru: 'Автоматизируй всё с Create, AE2 и другими',
    fr: 'Automatise tout avec Create, AE2 et plus',
    de: 'Automatisiere alles mit Create, AE2 und mehr',
  },
  'New biomes, creatures and adventures to explore': {
    lt: 'Nauji biomai, būtybės ir nuotykiai tyrinėjimui',
    ru: 'Новые биомы, существа и приключения',
    fr: 'Nouveaux biomes, créatures et aventures à explorer',
    de: 'Neue Biome, Kreaturen und Abenteuer zum Erkunden',
  },
  'Installed in one click via the launcher (CurseForge / Modrinth)': {
    lt: 'Įdiegiamas vienu paspaudimu per launcherį (CurseForge / Modrinth)',
    ru: 'Устанавливается в один клик через лаунчер (CurseForge / Modrinth)',
    fr: 'Installé en un clic via le launcher (CurseForge / Modrinth)',
    de: 'Mit einem Klick über den Launcher installiert (CurseForge / Modrinth)',
  },
  'Join Survival now, or get ready for the future modded server.': {
    lt: 'Junkis prie Survival dabar arba ruoškis būsimam modded serveriui.',
    ru: 'Заходи на Survival сейчас или готовься к будущему modded серверу.',
    fr: 'Rejoins Survival maintenant, ou prépare-toi pour le futur serveur moddé.',
    de: 'Tritt Survival jetzt bei oder bereite dich auf den künftigen Modded-Server vor.',
  },
  'Join vanilla now, or get ready for the future modded server.': {
    lt: 'Junkis prie vanilla dabar arba ruoškis būsimam modded serveriui.',
    ru: 'Заходи на vanilla сейчас или готовься к будущему modded серверу.',
    fr: 'Rejoins vanilla maintenant, ou prépare-toi pour le futur serveur moddé.',
    de: 'Tritt Vanilla jetzt bei oder bereite dich auf den künftigen Modded-Server vor.',
  },
  'Survival is live:': {
    lt: 'Survival veikia:',
    ru: 'Survival онлайн:',
    fr: 'Survival est en ligne :',
    de: 'Survival ist live:',
  },
  'Vanilla is live:': { lt: 'Vanilla veikia:', ru: 'Vanilla онлайн:', fr: 'Vanilla est en ligne :', de: 'Vanilla ist live:' },
  use: { lt: 'naudok', ru: 'используй', fr: 'utilise', de: 'nutze' },
  'in Minecraft Java Edition. The modded server remains a separate planned service.': {
    lt: 'Minecraft Java Edition. Modded serveris lieka atskira planuojama paslauga.',
    ru: 'в Minecraft Java Edition. Modded сервер остаётся отдельным планируемым сервисом.',
    fr: 'dans Minecraft Java Edition. Le serveur moddé reste un service séparé prévu.',
    de: 'in Minecraft Java Edition. Der Modded-Server bleibt ein geplanter separater Dienst.',
  },
  'Join Survival': {
    lt: 'Prisijungti prie Survival',
    ru: 'Зайти на Survival',
    fr: 'Rejoindre Survival',
    de: 'Survival beitreten',
  },
  'Join Vanilla': { lt: 'Prisijungti prie Vanilla', ru: 'Зайти на Vanilla', fr: 'Rejoindre Vanilla', de: 'Vanilla beitreten' },
  'Join Modded': { lt: 'Prisijungti prie Modded', ru: 'Зайти на Modded', fr: 'Rejoindre Modded', de: 'Modded beitreten' },
  'Open Minecraft: Java Edition (version': {
    lt: 'Atidaryk Minecraft: Java Edition (versija',
    ru: 'Открой Minecraft: Java Edition (версия',
    fr: 'Ouvre Minecraft : Java Edition (version',
    de: 'Öffne Minecraft: Java Edition (Version',
  },
  'On the main menu, click "Multiplayer".': {
    lt: 'Pagrindiniame meniu spausk „Multiplayer“.',
    ru: 'В главном меню нажми «Multiplayer».',
    fr: 'Dans le menu principal, clique sur « Multiplayer ».',
    de: 'Klicke im Hauptmenü auf „Multiplayer“.',
  },
  'Click "Add Server", give it a name like "Uliunai Survival".': {
    lt: 'Spausk „Add Server“ ir pavadink pvz. „Uliunai Survival“.',
    ru: 'Нажми «Add Server» и назови, например, «Uliunai Survival».',
    fr: 'Clique sur « Add Server » et donne un nom comme « Uliunai Survival ».',
    de: 'Klicke auf „Add Server“ und gib einen Namen wie „Uliunai Survival“.',
  },
  'Click "Add Server", give it a name like "Uliunai Vanilla".': {
    lt: 'Spausk „Add Server“ ir pavadink pvz. „Uliunai Vanilla“.',
    ru: 'Нажми «Add Server» и назови, например, «Uliunai Vanilla».',
    fr: 'Clique sur « Add Server » et donne un nom comme « Uliunai Vanilla ».',
    de: 'Klicke auf „Add Server“ und gib einen Namen wie „Uliunai Vanilla“.',
  },
  'Enter the server address:': {
    lt: 'Įvesk serverio adresą:',
    ru: 'Введи адрес сервера:',
    fr: 'Entre l’adresse du serveur :',
    de: 'Gib die Serveradresse ein:',
  },
  'Click Done, select the server and hit "Join Server". Enjoy!': {
    lt: 'Spausk Done, pasirink serverį ir „Join Server“. Mėgaukis!',
    ru: 'Нажми Done, выбери сервер и «Join Server». Приятной игры!',
    fr: 'Clique Done, sélectionne le serveur et « Join Server ». Amuse-toi !',
    de: 'Klicke Done, wähle den Server und „Join Server“. Viel Spaß!',
  },
  'Install the': { lt: 'Įdiek', ru: 'Установи', fr: 'Installe le', de: 'Installiere den' },
  'launcher.': { lt: 'launcherį.', ru: 'лаунчер.', fr: 'launcher.', de: 'Launcher.' },
  'Search for': { lt: 'Ieškok', ru: 'Найди', fr: 'Cherche', de: 'Suche nach' },
  'and install the modpack.': {
    lt: 'ir įdiek modpack.',
    ru: 'и установи modpack.',
    fr: 'et installe le modpack.',
    de: 'und installiere das Modpack.',
  },
  'Launch Minecraft through the modpack.': {
    lt: 'Paleisk Minecraft per modpack.',
    ru: 'Запусти Minecraft через modpack.',
    fr: 'Lance Minecraft via le modpack.',
    de: 'Starte Minecraft über das Modpack.',
  },
  'Go to Multiplayer → Add Server and enter:': {
    lt: 'Eik į Multiplayer → Add Server ir įvesk:',
    ru: 'Зайди в Multiplayer → Add Server и введи:',
    fr: 'Va dans Multiplayer → Add Server et entre :',
    de: 'Gehe zu Multiplayer → Add Server und gib ein:',
  },
  'Join the server — make sure your modpack version matches!': {
    lt: 'Junkis prie serverio — įsitikink, kad modpack versija sutampa!',
    ru: 'Заходи на сервер — убедись, что версия modpack совпадает!',
    fr: 'Rejoins le serveur — assure-toi que la version du modpack correspond !',
    de: 'Tritt dem Server bei — stelle sicher, dass die Modpack-Version passt!',
  },
  'A few simple rules keep Uliunai friendly for everyone. Play fair, be kind, have fun.': {
    lt: 'Kelios paprastos taisyklės laiko Uliunai draugišką visiems. Žaisk sąžiningai, būk malonus, smagiai leisk laiką.',
    ru: 'Несколько простых правил делают Uliunai дружелюбным для всех. Играй честно, будь добр, получай удовольствие.',
    fr: 'Quelques règles simples gardent Uliunai accueillant pour tous. Joue fair-play, sois gentil, amuse-toi.',
    de: 'Ein paar einfache Regeln halten Uliunai für alle freundlich. Spiel fair, sei nett, hab Spaß.',
  },
  'These are the Minecraft-specific rules': {
    lt: 'Tai Minecraft specifinės taisyklės',
    ru: 'Это правила именно для Minecraft',
    fr: 'Ce sont les règles spécifiques à Minecraft',
    de: 'Das sind die Minecraft-spezifischen Regeln',
  },
  'Griefing, x-ray, allowed mods and more. The universal Uliunai code of conduct (respect, fair play, clean chat) applies across every game.': {
    lt: 'Griefing, x-ray, leidžiami modai ir daugiau. Universalus Uliunai elgesio kodeksas (pagarba, sąžiningas žaidimas, švarus chat) galioja visuose žaidimuose.',
    ru: 'Грифинг, x-ray, разрешённые моды и другое. Универсальный кодекс Uliunai (уважение, fair play, чистый чат) действует во всех играх.',
    fr: 'Griefing, x-ray, mods autorisés et plus. Le code de conduite Uliunai (respect, fair-play, chat propre) s’applique à tous les jeux.',
    de: 'Griefing, X-Ray, erlaubte Mods und mehr. Der universelle Uliunai-Verhaltenskodex (Respekt, Fairplay, sauberer Chat) gilt in jedem Spiel.',
  },
  'Staff have final say on rule enforcement. Breaking the rules may result in a warning, mute, kick or ban depending on severity.': {
    lt: 'Galutinį sprendimą dėl taisyklių taikymo priima komanda. Pažeidimai gali baigtis įspėjimu, mute, kick ar banu priklausomai nuo rimtumo.',
    ru: 'Окончательное слово по правилам у staff. Нарушения могут привести к предупреждению, муту, кику или бану в зависимости от тяжести.',
    fr: 'Le staff a le dernier mot sur les règles. Les infractions peuvent entraîner avertissement, mute, kick ou ban selon la gravité.',
    de: 'Das Team hat bei der Regelanwendung das letzte Wort. Verstöße können je nach Schwere zu Warnung, Mute, Kick oder Ban führen.',
  },
  'Cheating & Mods': { lt: 'Cheat’ai ir modai', ru: 'Читы и моды', fr: 'Triche et mods', de: 'Cheats und Mods' },
  'No x-ray, hacked clients, fly, kill-aura or any combat/movement cheats.': {
    lt: 'Jokio x-ray, hacked clientų, fly, kill-aura ar kitų kovos/judėjimo cheat’ų.',
    ru: 'Без x-ray, взломанных клиентов, fly, kill-aura и других боевых/движение читов.',
    fr: 'Pas de x-ray, clients hackés, fly, kill-aura ou autres cheats de combat/mouvement.',
    de: 'Kein X-Ray, gehackte Clients, Fly, Kill-Aura oder andere Kampf-/Bewegungs-Cheats.',
  },
  'No duplication glitches or item dupes — report the bug instead of abusing it.': {
    lt: 'Jokių dublikavimo glitch’ų ar item dupe — pranešk apie klaidą, o ne piktnaudžiauk.',
    ru: 'Без дюпов и glitch’ей дублирования — сообщи о баге, а не злоупотребляй.',
    fr: 'Pas de glitches de duplication ni de dupes d’items — signale le bug au lieu d’en abuser.',
    de: 'Keine Duplikations-Glitches oder Item-Dupes — melde den Bug, statt ihn auszunutzen.',
  },
  'Only approved client-side performance and quality-of-life mods are allowed (e.g. Sodium); no radar/cave-finder or cheat mods.': {
    lt: 'Leidžiami tik patvirtinti kliento našumo ir QoL modai (pvz. Sodium); jokių radarų/cave-finder ar cheat modų.',
    ru: 'Разрешены только одобренные клиентские performance и QoL моды (напр. Sodium); без радаров/cave-finder и чит-модов.',
    fr: 'Seuls les mods client performance/QoL approuvés sont autorisés (ex. Sodium) ; pas de radar/cave-finder ni mods de triche.',
    de: 'Nur freigegebene clientseitige Performance- und QoL-Mods erlaubt (z. B. Sodium); keine Radar/Cave-Finder- oder Cheat-Mods.',
  },
  'No AFK pools, auto-clickers, macros or machines that farm or bypass server limits while you are away.': {
    lt: 'Jokių AFK baseinų, auto-clickerių, makro ar mašinų, kurios farmina ar apeina limitus, kol tavęs nėra.',
    ru: 'Без AFK-пулов, автокликеров, макросов и машин, которые фармят или обходят лимиты, пока тебя нет.',
    fr: 'Pas de pools AFK, auto-clickers, macros ou machines qui farm/contournent les limites pendant ton absence.',
    de: 'Keine AFK-Pools, Auto-Clicker, Makros oder Maschinen, die farmen oder Limits umgehen, während du weg bist.',
  },
  'Builds, Land & Griefing': {
    lt: 'Statyba, žemė ir griefing',
    ru: 'Постройки, земля и грифинг',
    fr: 'Builds, terrain et griefing',
    de: 'Bauten, Land und Griefing',
  },
  'No griefing, stealing from, or destroying other players’ builds or chests.': {
    lt: 'Jokio griefingo, vagystės ar kitų žaidėjų statinių/skrynių naikinimo.',
    ru: 'Без грифинга, кражи и разрушения чужих построек или сундуков.',
    fr: 'Pas de griefing, vol ou destruction des builds/coffres des autres.',
    de: 'Kein Griefing, Stehlen oder Zerstören fremder Bauten oder Kisten.',
  },
  'Claim and protect your land with the protection tool the server provides.': {
    lt: 'Užimk ir saugok savo žemę su serverio apsaugos įrankiu.',
    ru: 'Заяви и защити землю инструментом защиты сервера.',
    fr: 'Claim et protège ton terrain avec l’outil de protection du serveur.',
    de: 'Beanspruche und schütze dein Land mit dem Schutz-Tool des Servers.',
  },
  'Ask before building close to someone else’s base, and leave reasonable space.': {
    lt: 'Klausk prieš statydamas šalia kito bazės ir palik protingą atstumą.',
    ru: 'Спроси, прежде чем строить рядом с чужой базой, и оставляй разумное пространство.',
    fr: 'Demande avant de construire près d’une base d’autrui et laisse un espace raisonnable.',
    de: 'Frage, bevor du nah an einer fremden Base baust, und lass vernünftigen Abstand.',
  },
  'No lava, fire or TNT griefing near other players’ property.': {
    lt: 'Jokio lavos, ugnies ar TNT griefingo prie kitų nuosavybės.',
    ru: 'Без грифинга лавой, огнём или TNT рядом с чужой собственностью.',
    fr: 'Pas de griefing lave, feu ou TNT près des propriétés d’autrui.',
    de: 'Kein Lava-, Feuer- oder TNT-Griefing bei fremdem Eigentum.',
  },
  'Public and community projects are shared — don’t hoard, vandalise or pave over them.': {
    lt: 'Vieši ir bendruomenės projektai yra bendri — nelaikyk, negriauk ir neuždėliok jų.',
    ru: 'Публичные и community-проекты общие — не захватывай, не вандаль и не застраивай их.',
    fr: 'Les projets publics/communautaires sont partagés — ne les accapare pas, ne les vandalisе pas.',
    de: 'Öffentliche und Community-Projekte sind geteilt — nicht hortieren, vandalieren oder zupflastern.',
  },
  'World & Server Health': {
    lt: 'Pasaulio ir serverio sveikata',
    ru: 'Здоровье мира и сервера',
    fr: 'Santé du monde et du serveur',
    de: 'Welt- und Server-Gesundheit',
  },
  'No lag machines — keep redstone clocks, mob farms and entity stacks within reasonable limits.': {
    lt: 'Jokių lag mašinų — redstone laikrodžiai, mob farm’ai ir entity stack’ai turi būti protingose ribose.',
    ru: 'Без лагомашин — redstone-часы, mob-фермы и entity-стеки в разумных пределах.',
    fr: 'Pas de lag machines — horloges redstone, fermes de mobs et stacks d’entités dans des limites raisonnables.',
    de: 'Keine Lag-Maschinen — Redstone-Clocks, Mob-Farmen und Entity-Stacks in vernünftigen Grenzen.',
  },
  'No intentionally lagging or crashing the server, and no exploiting performance bugs.': {
    lt: 'Nesąmoningai nelagink ir nekrauk serverio, ir neeksploatuok našumo klaidų.',
    ru: 'Не лагай и не крашь сервер намеренно и не используй performance-баги.',
    fr: 'Pas de lag ou crash intentionnel du serveur, ni d’exploitation de bugs de perf.',
    de: 'Kein absichtliches Lagen oder Crashen des Servers und kein Ausnutzen von Performance-Bugs.',
  },
  'Staff may ask you to switch off or downsize a build that is hurting server performance.': {
    lt: 'Komanda gali paprašyti išjungti ar sumažinti statinį, kenkiantį serverio našumui.',
    ru: 'Staff может попросить выключить или уменьшить постройку, вредящую производительности.',
    fr: 'Le staff peut te demander d’éteindre ou réduire un build qui nuit aux perfs.',
    de: 'Das Team kann dich bitten, einen Bau abzuschalten oder zu verkleinern, der die Performance belastet.',
  },
  'PvP & Player Safety': {
    lt: 'PvP ir žaidėjų saugumas',
    ru: 'PvP и безопасность игроков',
    fr: 'PvP et sécurité des joueurs',
    de: 'PvP und Spielersicherheit',
  },
  'PvP only by mutual agreement or in designated PvP areas.': {
    lt: 'PvP tik abipusiu sutarimu arba skirtose PvP zonose.',
    ru: 'PvP только по взаимному согласию или в специальных PvP-зонах.',
    fr: 'PvP seulement par accord mutuel ou dans les zones PvP désignées.',
    de: 'PvP nur bei gegenseitiger Zustimmung oder in ausgewiesenen PvP-Zonen.',
  },
  'No trapping, kill-boxing, or luring players into death traps.': {
    lt: 'Jokio gaudymo, kill-box’ų ar viliojimo į mirties spąstus.',
    ru: 'Без ловушек, kill-box и заманивания в смертельные ловушки.',
    fr: 'Pas de pièges, kill-box ou appâts vers des pièges mortels.',
    de: 'Kein Fangen, Kill-Boxing oder Anlocken in Todesfallen.',
  },
  'No stealing a player’s items on death where protections are meant to apply.': {
    lt: 'Nevogk žaidėjo daiktų po mirties, kai turėtų galioti apsaugos.',
    ru: 'Не кради вещи игрока после смерти там, где защиты должны работать.',
    fr: 'Ne vole pas les items d’un joueur à la mort là où les protections s’appliquent.',
    de: 'Keine Items eines Spielers nach dem Tod stehlen, wo Schutz greifen soll.',
  },

  // --- Gallery captions ---
  'Lanterns over the torii village': {
    lt: 'Žibintai virš torii kaimo',
    ru: 'Фонари над деревней с тории',
    fr: 'Lanternes au-dessus du village torii',
    de: 'Laternen über dem Torii-Dorf',
  },
  'The cliffside stronghold': {
    lt: 'Tvirtovė ant skardžio',
    ru: 'Крепость на утесе',
    fr: 'La forteresse de falaise',
    de: 'Die Klippenfestung',
  },
  'Desert outpost from above': {
    lt: 'Dykumos forpostas iš viršaus',
    ru: 'Пустынный форпост сверху',
    fr: 'Avant-poste du désert vu du ciel',
    de: 'Wüsten-Außenposten von oben',
  },
  'Malachai the Arcane awaits': {
    lt: 'Malachai the Arcane laukia',
    ru: 'Malachai the Arcane ждёт',
    fr: 'Malachai the Arcane attend',
    de: 'Malachai the Arcane wartet',
  },
  'World boss': { lt: 'Pasaulio bosas', ru: 'Мировой босс', fr: 'Boss mondial', de: 'Weltboss' },
  'First light over the highlands': {
    lt: 'Pirmoji šviesa virš aukštumų',
    ru: 'Первый свет над нагорьем',
    fr: 'Première lumière sur les hautes terres',
    de: 'Erstes Licht über dem Hochland',
  },
  'Beacon above the autumn canopy': {
    lt: 'Švyturys virš rudens medžių',
    ru: 'Маяк над осенней кроной',
    fr: 'Balise au-dessus de la canopée d’automne',
    de: 'Leuchtfeuer über dem Herbstlaub',
  },
  'Festival lights in the ravine': {
    lt: 'Šventės šviesos kanjone',
    ru: 'Праздничные огни в ущелье',
    fr: 'Lumières de fête dans le ravin',
    de: 'Festlichter in der Schlucht',
  },
  'Trading post on the green hills': {
    lt: 'Prekybos punktas ant žalių kalvų',
    ru: 'Торговый пост на зелёных холмах',
    fr: 'Comptoir sur les collines vertes',
    de: 'Handelsposten auf grünen Hügeln',
  },
  'The lonely watchtower': {
    lt: 'Vienišas stebėjimo bokštas',
    ru: 'Одинокая сторожевая башня',
    fr: 'La tour de guet solitaire',
    de: 'Der einsame Wachturm',
  },
  'Pinewood lake at the foot of the mountains': {
    lt: 'Pušų ežeras kalnų papėdėje',
    ru: 'Сосновое озеро у подножия гор',
    fr: 'Lac des pins au pied des montagnes',
    de: 'Kiefersee am Fuß der Berge',
  },
  'Where the pines meet the sea': {
    lt: 'Kur pušys susitinka su jūra',
    ru: 'Где сосны встречаются с морем',
    fr: 'Là où les pins rencontrent la mer',
    de: 'Wo die Kiefern aufs Meer treffen',
  },
  'Wildlife on the open plains': {
    lt: 'Laukiniai gyvūnai atvirose lygumose',
    ru: 'Дикая природа на открытых равнинах',
    fr: 'Faune sur les plaines ouvertes',
    de: 'Wildtiere auf den offenen Ebenen',
  },
  'The old stone chapel': {
    lt: 'Senoji akmens koplyčia',
    ru: 'Старая каменная часовня',
    fr: 'La vieille chapelle de pierre',
    de: 'Die alte Steinkapelle',
  },
  'Something watches in the dark': {
    lt: 'Kažkas stebi tamsoje',
    ru: 'Что-то смотрит во тьме',
    fr: 'Quelque chose observe dans le noir',
    de: 'Etwas wacht im Dunkeln',
  },
  'Azure forest over the farmlands': {
    lt: 'Žydras miškas virš dirbamų laukų',
    ru: 'Лазурный лес над полями',
    fr: 'Forêt azur au-dessus des fermes',
    de: 'Azurwald über den Feldern',
  },
  'Rooftops of the trading quarter': {
    lt: 'Prekybos kvartalo stogai',
    ru: 'Крыши торгового квартала',
    fr: 'Toits du quartier commerçant',
    de: 'Dächer des Handelsviertels',
  },
  'Windmill village in the hills': {
    lt: 'Vėjo malūnų kaimas kalvose',
    ru: 'Деревня с мельницами на холмах',
    fr: 'Village de moulins dans les collines',
    de: 'Mühlendorf in den Hügeln',
  },
  'A view across the valley': {
    lt: 'Vaizdas per slėnį',
    ru: 'Вид через долину',
    fr: 'Une vue sur la vallée',
    de: 'Ein Blick über das Tal',
  },
  'Dusk over the rolling hills': {
    lt: 'Sutemos virš banguotų kalvų',
    ru: 'Сумерки над холмами',
    fr: 'Crépuscule sur les collines',
    de: 'Dämmerung über den Hügeln',
  },
  'Market square at the heart of town': {
    lt: 'Turgaus aikštė miesto širdyje',
    ru: 'Рыночная площадь в сердце города',
    fr: 'Place du marché au cœur de la ville',
    de: 'Marktplatz im Herzen der Stadt',
  },
  'Red-tiled streets': {
    lt: 'Raudonų čerpių gatvės',
    ru: 'Улицы с красной черепицей',
    fr: 'Rues aux tuiles rouges',
    de: 'Straßen mit roten Ziegeln',
  },
  'Sunset on the far coast': {
    lt: 'Saulėlydis tolimoje pakrantėje',
    ru: 'Закат на дальнем берегу',
    fr: 'Coucher de soleil sur la côte lointaine',
    de: 'Sonnenuntergang an der fernen Küste',
  },
  'Last light over the water': {
    lt: 'Paskutinė šviesa virš vandens',
    ru: 'Последний свет над водой',
    fr: 'Dernière lumière sur l’eau',
    de: 'Letztes Licht über dem Wasser',
  },
  'Real shots from the Uliunai survival world — villages, builds, boss fights and the road in between, shaders on.': {
    lt: 'Tikri kadrai iš Uliunai survival pasaulio — kaimai, statiniai, bosų kovos ir kelias tarp jų, su šeideriais.',
    ru: 'Реальные кадры из survival-мира Uliunai — деревни, постройки, боссы и дорога между ними, с шейдерами.',
    fr: 'Vraies captures du monde survival Uliunai — villages, builds, boss et la route entre les deux, shaders on.',
    de: 'Echte Aufnahmen aus der Uliunai-Survival-Welt — Dörfer, Bauten, Bosse und der Weg dazwischen, Shader an.',
  },

  // --- Mods / stats / misc UI ---
  by: { lt: 'autorius', ru: 'от', fr: 'par', de: 'von' },
  'Unknown size': { lt: 'Nežinomas dydis', ru: 'Неизвестный размер', fr: 'Taille inconnue', de: 'Unbekannte Größe' },
  'Unknown date': { lt: 'Nežinoma data', ru: 'Неизвестная дата', fr: 'Date inconnue', de: 'Unbekanntes Datum' },
  deps: { lt: 'prikl.', ru: 'завис.', fr: 'deps', de: 'Deps' },
  File: { lt: 'Failas', ru: 'Файл', fr: 'Fichier', de: 'Datei' },
  Project: { lt: 'Projektas', ru: 'Проект', fr: 'Projet', de: 'Projekt' },
  Updated: { lt: 'Atnaujinta', ru: 'Обновлено', fr: 'Mis à jour', de: 'Aktualisiert' },
  Version: { lt: 'Versija', ru: 'Версия', fr: 'Version', de: 'Version' },
  via: { lt: 'per', ru: 'через', fr: 'via', de: 'über' },
  'for Minecraft': { lt: 'Minecraft', ru: 'для Minecraft', fr: 'pour Minecraft', de: 'für Minecraft' },
  'Local CurseForge profile export with': {
    lt: 'Vietinis CurseForge profilio eksportas su',
    ru: 'Локальный экспорт профиля CurseForge с',
    fr: 'Export local du profil CurseForge avec',
    de: 'Lokaler CurseForge-Profil-Export mit',
  },
  'content items. Install the shared pack through CurseForge.': {
    lt: 'turinio elementų. Įdiek bendrą pack per CurseForge.',
    ru: 'элементами контента. Установи общий pack через CurseForge.',
    fr: 'éléments de contenu. Installe le pack partagé via CurseForge.',
    de: 'Inhaltselementen. Installiere das gemeinsame Pack über CurseForge.',
  },
  'Search by mod, author, file name, project ID, file ID or version...': {
    lt: 'Ieškok pagal modą, autorių, failą, project ID, file ID ar versiją...',
    ru: 'Поиск по моду, автору, файлу, project ID, file ID или версии...',
    fr: 'Recherche par mod, auteur, fichier, project ID, file ID ou version...',
    de: 'Suche nach Mod, Autor, Dateiname, Project-ID, File-ID oder Version...',
  },
  Loaded: { lt: 'Įkelta', ru: 'Загружено', fr: 'Chargé', de: 'Geladen' },
  Showing: { lt: 'Rodoma', ru: 'Показано', fr: 'Affiché', de: 'Angezeigt' },
  of: { lt: 'iš', ru: 'из', fr: 'sur', de: 'von' },
  'matching items': {
    lt: 'atitinkančių elementų',
    ru: 'совпадающих элементов',
    fr: 'éléments correspondants',
    de: 'passenden Einträgen',
  },
  'matching plugins': {
    lt: 'atitinkančių pluginų',
    ru: 'совпадающих плагинов',
    fr: 'plugins correspondants',
    de: 'passenden Plugins',
  },
  'Search by plugin name, category or description...': {
    lt: 'Ieškok pagal pluginą, kategoriją ar aprašymą...',
    ru: 'Ищи по плагину, категории или описанию...',
    fr: 'Rechercher par plugin, catégorie ou description...',
    de: 'Nach Plugin, Kategorie oder Beschreibung suchen...',
  },
  'Pinned from minecraft/server/versions.env — update the catalog when pins change.': {
    lt: 'Prisegta iš minecraft/server/versions.env — atnaujink katalogą keičiant pins.',
    ru: 'Закреплено из minecraft/server/versions.env — обновляй каталог при смене pins.',
    fr: 'Issu de minecraft/server/versions.env — mets à jour le catalogue quand les pins changent.',
    de: 'Aus minecraft/server/versions.env — Katalog aktualisieren, wenn Pins wechseln.',
  },
  'Data generated from the installed CurseForge profile on June 15, 2026.': {
    lt: 'Duomenys sugeneruoti iš įdiegto CurseForge profilio 2026-06-15.',
    ru: 'Данные сгенерированы из установленного профиля CurseForge 15 июня 2026.',
    fr: 'Données générées depuis le profil CurseForge installé le 15 juin 2026.',
    de: 'Daten aus dem installierten CurseForge-Profil vom 15. Juni 2026.',
  },
  'No plugins match your search. Try another keyword or category.': {
    lt: 'Jokie pluginai neatitinka paieškos. Bandyk kitą raktinį žodį ar kategoriją.',
    ru: 'Нет плагинов по запросу. Попробуй другое слово или категорию.',
    fr: 'Aucun plugin ne correspond. Essaie un autre mot-clé ou catégorie.',
    de: 'Keine Plugins passen. Versuche ein anderes Stichwort oder eine Kategorie.',
  },
  'No mods match your search. Try another keyword or category.': {
    lt: 'Jokie modai neatitinka paieškos. Bandyk kitą raktinį žodį ar kategoriją.',
    ru: 'Нет модов по запросу. Попробуй другое слово или категорию.',
    fr: 'Aucun mod ne correspond. Essaie un autre mot-clé ou catégorie.',
    de: 'Keine Mods passen. Versuche ein anderes Stichwort oder eine Kategorie.',
  },
  'Survival is live; modded remains a separate planned server.': {
    lt: 'Survival veikia; modded lieka atskiras planuojamas serveris.',
    ru: 'Survival онлайн; modded остаётся отдельным планируемым сервером.',
    fr: 'Survival est en ligne ; le moddé reste un serveur séparé prévu.',
    de: 'Survival ist live; Modded bleibt ein geplanter separater Server.',
  },
  'Player land claims so builds stay protected without grief.': {
    lt: 'Žaidėjų žemės claimai, kad statiniai būtų apsaugoti nuo grief.',
    ru: 'Игрокские claim’ы земли, чтобы постройки были защищены от грифа.',
    fr: 'Claims de terrain joueur pour protéger les builds du grief.',
    de: 'Spieler-Land-Claims, damit Builds vor Grief geschützt bleiben.',
  },
  'Groups and permissions for members, builders, moderators, and admins.': {
    lt: 'Grupės ir leidimai nariams, builders, moderatoriams ir adminams.',
    ru: 'Группы и права для members, builders, модераторов и админов.',
    fr: 'Groupes et permissions pour membres, builders, modérateurs et admins.',
    de: 'Gruppen und Rechte für Members, Builder, Moderatoren und Admins.',
  },
  'Essential survival commands — homes, teleports, kits, and more.': {
    lt: 'Esminės išgyvenimo komandos — namai, teleportai, kitai ir daugiau.',
    ru: 'Базовые survival-команды — дома, телепорты, киты и другое.',
    fr: 'Commandes de survie essentielles — homes, téléports, kits et plus.',
    de: 'Wichtige Survival-Befehle — Homes, Teleports, Kits und mehr.',
  },
  'Block logging and staff rollback when something goes wrong.': {
    lt: 'Blokų logavimas ir komandos rollback, kai kas nors nutinka.',
    ru: 'Логирование блоков и staff rollback, когда что-то идёт не так.',
    fr: 'Journalisation des blocs et rollback staff en cas de problème.',
    de: 'Block-Logging und Team-Rollback, wenn etwas schiefgeht.',
  },
  'Selection and edit tools for trusted builders.': {
    lt: 'Pasirinkimo ir redagavimo įrankiai patikimiems builders.',
    ru: 'Инструменты выделения и редактирования для доверенных builders.',
    fr: 'Outils de sélection et d’édition pour builders de confiance.',
    de: 'Auswahl- und Edit-Tools für vertrauenswürdige Builder.',
  },
  'Region protection for lobby, spawn, and staff builds.': {
    lt: 'Regionų apsauga lobby, spawn ir komandos statiniams.',
    ru: 'Защита регионов для lobby, spawn и staff-построек.',
    fr: 'Protection de régions pour lobby, spawn et builds staff.',
    de: 'Regionsschutz für Lobby, Spawn und Team-Builds.',
  },
  'Multiple worlds under one server (overworld, lobby, and more).': {
    lt: 'Keli pasauliai viename serveryje (overworld, lobby ir daugiau).',
    ru: 'Несколько миров на одном сервере (overworld, lobby и другие).',
    fr: 'Plusieurs mondes sous un même serveur (overworld, lobby et plus).',
    de: 'Mehrere Welten unter einem Server (Overworld, Lobby und mehr).',
  },
  'Portal regions that teleport into Multiverse destinations.': {
    lt: 'Portalo regionai, teleportuojantys į Multiverse paskirties vietas.',
    ru: 'Портальные регионы, телепортирующие в Multiverse-назначения.',
    fr: 'Régions portail qui téléportent vers des destinations Multiverse.',
    de: 'Portalregionen, die zu Multiverse-Zielen teleportieren.',
  },
  'Void world generator used for Multiverse create/import (e.g. lobby).': {
    lt: 'Void pasaulio generatorius Multiverse create/import (pvz. lobby).',
    ru: 'Генератор void-мира для Multiverse create/import (напр. lobby).',
    fr: 'Générateur de monde void pour Multiverse create/import (ex. lobby).',
    de: 'Void-Weltgenerator für Multiverse create/import (z. B. Lobby).',
  },
  'NPCs for guides, shops, and lobby helpers on Paper.': {
    lt: 'NPC gidams, parduotuvėms ir lobby pagalbininkams Paper serveryje.',
    ru: 'NPC для гидов, магазинов и lobby-помощников на Paper.',
    fr: 'PNJ pour guides, boutiques et aides lobby sur Paper.',
    de: 'NPCs für Guides, Shops und Lobby-Helfer auf Paper.',
  },
  'Vault API shim so economy plugins can register providers.': {
    lt: 'Vault API shim, kad ekonomikos pluginai galėtų registruoti providerius.',
    ru: 'Vault API shim, чтобы economy-плагины регистрировали провайдеров.',
    fr: 'Shim Vault API pour que les plugins économie enregistrent des providers.',
    de: 'Vault-API-Shim, damit Economy-Plugins Provider registrieren können.',
  },
  'Server economy provider used by player and server shops.': {
    lt: 'Serverio ekonomikos provideris, naudojamas žaidėjų ir serverio parduotuvėse.',
    ru: 'Провайдер экономики сервера для игровых и серверных магазинов.',
    fr: 'Provider d’économie serveur utilisé par les boutiques joueur et serveur.',
    de: 'Server-Economy-Provider für Spieler- und Server-Shops.',
  },
  'GUI shops for players and the server economy.': {
    lt: 'GUI parduotuvės žaidėjams ir serverio ekonomikai.',
    ru: 'GUI-магазины для игроков и экономики сервера.',
    fr: 'Boutiques GUI pour les joueurs et l’économie serveur.',
    de: 'GUI-Shops für Spieler und die Server-Wirtschaft.',
  },
  'Per-player loot chests — starter kits, VIP chests, and farmable loot.': {
    lt: 'Loot chestai žaidėjui — starter kitai, VIP chestai ir farmable loot.',
    ru: 'Персональные loot chest’ы — стартовые киты, VIP chest’ы и фармовый лут.',
    fr: 'Coffres de loot par joueur — kits de départ, coffres VIP et loot farmable.',
    de: 'Pro-Spieler-Loot-Chests — Starter-Kits, VIP-Chests und farmbarer Loot.',
  },
  'Tab list and nametags, including LuckPerms group prefixes.': {
    lt: 'Tab sąrašas ir nametagai, įskaitant LuckPerms grupių prefixus.',
    ru: 'Tab-лист и nametag’и, включая префиксы групп LuckPerms.',
    fr: 'Liste tab et nametags, y compris les préfixes de groupes LuckPerms.',
    de: 'Tab-Liste und Nametags inkl. LuckPerms-Gruppenpräfixe.',
  },
  'Placeholder expansions used by TAB, tags, and other plugins.': {
    lt: 'Placeholder plėtiniai, naudojami TAB, tagų ir kitų pluginų.',
    ru: 'Placeholder-расширения для TAB, тегов и других плагинов.',
    fr: 'Extensions Placeholder utilisées par TAB, tags et autres plugins.',
    de: 'Placeholder-Erweiterungen für TAB, Tags und andere Plugins.',
  },
  'NBT library required by SupremeTags and related plugins.': {
    lt: 'NBT biblioteka, reikalinga SupremeTags ir susijusiems pluginams.',
    ru: 'NBT-библиотека, нужная SupremeTags и связанным плагинам.',
    fr: 'Bibliothèque NBT requise par SupremeTags et plugins associés.',
    de: 'NBT-Bibliothek, benötigt von SupremeTags und verwandten Plugins.',
  },
  'Cosmetic selectable tags with a player GUI.': {
    lt: 'Kosmetiniai pasirenkami tagai su žaidėjo GUI.',
    ru: 'Косметические выбираемые теги с GUI игрока.',
    fr: 'Tags cosmétiques sélectionnables avec GUI joueur.',
    de: 'Kosmetische wählbare Tags mit Spieler-GUI.',
  },
  'Hybrid online/offline auth on the Velocity proxy (not the Paper backend). Supports premium and cracked logins.': {
    lt: 'Hibridinis online/offline auth Velocity proxy (ne Paper backend). Palaiko premium ir cracked prisijungimus.',
    ru: 'Гибридный online/offline auth на Velocity proxy (не на Paper backend). Поддерживает premium и cracked логины.',
    fr: 'Auth hybride online/offline sur le proxy Velocity (pas le backend Paper). Supporte premium et cracked.',
    de: 'Hybride Online/Offline-Auth auf dem Velocity-Proxy (nicht dem Paper-Backend). Unterstützt Premium- und Cracked-Logins.',
  },
  'Auth limbo waiting room for /register and /login — runs beside Velocity, not on the survival world.': {
    lt: 'Auth limbo laukimo kambarys /register ir /login — veikia šalia Velocity, ne survival pasaulyje.',
    ru: 'Auth limbo комната ожидания для /register и /login — рядом с Velocity, не в survival мире.',
    fr: 'Salle d’attente limbo auth pour /register et /login — à côté de Velocity, pas sur le monde survie.',
    de: 'Auth-Limbo-Warteraum für /register und /login — neben Velocity, nicht in der Survival-Welt.',
  },
  Page: { lt: 'Puslapis', ru: 'Страница', fr: 'Page', de: 'Seite' },
  Tech: { lt: 'Tech', ru: 'Технологии', fr: 'Tech', de: 'Tech' },
  Magic: { lt: 'Magija', ru: 'Магия', fr: 'Magie', de: 'Magie' },
  World: { lt: 'Pasaulis', ru: 'Мир', fr: 'Monde', de: 'Welt' },
  QoL: { lt: 'QoL', ru: 'QoL', fr: 'QoL', de: 'QoL' },
  Performance: { lt: 'Našumas', ru: 'Производительность', fr: 'Performance', de: 'Performance' },
  Adventure: { lt: 'Nuotykiai', ru: 'Приключения', fr: 'Aventure', de: 'Abenteuer' },
  Library: { lt: 'Biblioteka', ru: 'Библиотека', fr: 'Bibliothèque', de: 'Bibliothek' },
  'Resource Pack': { lt: 'Resursų paketas', ru: 'Ресурс-пак', fr: 'Pack de ressources', de: 'Resource Pack' },
  Shader: { lt: 'Šeideris', ru: 'Шейдер', fr: 'Shader', de: 'Shader' },
  'Resource Packs': { lt: 'Resursų paketai', ru: 'Ресурс-паки', fr: 'Packs de ressources', de: 'Resource Packs' },
  'The real Uliunai CurseForge profile: 236 mods, 11 resource packs and 2 shader packs for NeoForge 1.21.1.': {
    lt: 'Tikras Uliunai CurseForge profilis: 236 modai, 11 resursų paketų ir 2 šeiderių paketai NeoForge 1.21.1.',
    ru: 'Настоящий профиль CurseForge Uliunai: 236 модов, 11 ресурс-паков и 2 шейдер-пака для NeoForge 1.21.1.',
    fr: 'Le vrai profil CurseForge Uliunai : 236 mods, 11 packs de ressources et 2 packs de shaders pour NeoForge 1.21.1.',
    de: 'Das echte Uliunai-CurseForge-Profil: 236 Mods, 11 Resource Packs und 2 Shader Packs für NeoForge 1.21.1.',
  },
  'Server updates, events and announcements for the Uliunai Minecraft community.': {
    lt: 'Serverio atnaujinimai, renginiai ir pranešimai Uliunai Minecraft bendruomenei.',
    ru: 'Обновления сервера, события и объявления для сообщества Uliunai Minecraft.',
    fr: 'Mises à jour serveur, événements et annonces pour la communauté Minecraft Uliunai.',
    de: 'Server-Updates, Events und Ankündigungen für die Uliunai Minecraft Community.',
  },
  'Live leaderboards and player statistics.': {
    lt: 'Gyvos lyderių lentelės ir žaidėjų statistika.',
    ru: 'Живые таблицы лидеров и статистика игроков.',
    fr: 'Classements en direct et statistiques joueurs.',
    de: 'Live-Leaderboards und Spielerstatistiken.',
  },
  'Stats are enabled — leaderboard wiring goes here.': {
    lt: 'Statistika įjungta — lyderių lentelės laidai čia.',
    ru: 'Статистика включена — wiring лидерборда здесь.',
    fr: 'Les stats sont activées — le wiring du classement va ici.',
    de: 'Stats sind aktiviert — Leaderboard-Anbindung kommt hierhin.',
  },
  "Leaderboards, playtime, blocks mined and bragging rights — once the servers are live and the numbers start rolling in, you'll find them all here.": {
    lt: 'Lyderių lentelės, žaidimo laikas, iškasti blokai ir pasigyrimai — kai serveriai veiks ir skaičiai ims tekėti, viską rasi čia.',
    ru: 'Таблицы лидеров, время игры, добытые блоки и поводы для гордости — когда серверы заработают и цифры пойдут, всё будет здесь.',
    fr: 'Classements, temps de jeu, blocs minés et droits de se vanter — une fois les serveurs en ligne et les chiffres en route, tu les trouveras ici.',
    de: 'Leaderboards, Spielzeit, abgebaute Blöcke und Angeber-Rechte — sobald die Server live sind und die Zahlen kommen, findest du alles hier.',
  },
  Modpack: { lt: 'Modpack', ru: 'Modpack', fr: 'Modpack', de: 'Modpack' },

  // --- Server descriptions ---
  'Survival Java with curated server plugins — land claims, economy and shops, rollback, ranks and tags. No client modpack required; performance clients like Sodium are still welcome.': {
    lt: 'Java išgyvenimas su atrinktais serverio pluginais — žemės claimai, ekonomika ir parduotuvės, rollback, rangai ir tagai. Kliento modpack nereikia; našumo klientai kaip Sodium vis dar laukiami.',
    ru: 'Java survival с curated серверными плагинами — claim’ы, экономика и магазины, rollback, ранги и теги. Клиентский modpack не нужен; performance-клиенты вроде Sodium по-прежнему приветствуются.',
    fr: 'Survie Java avec plugins serveur sélectionnés — claims, économie et boutiques, rollback, rangs et tags. Aucun modpack client requis ; les clients perf comme Sodium restent bienvenus.',
    de: 'Java-Survival mit kuratierten Server-Plugins — Land-Claims, Wirtschaft und Shops, Rollback, Ränge und Tags. Kein Client-Modpack nötig; Performance-Clients wie Sodium sind weiterhin willkommen.',
  },
  'Vanilla-style Java survival with land claims, essential commands, rollback protection, clear rules, and no client modpack required.': {
    lt: 'Vanilla stiliaus Java išgyvenimas su žemės claimais, esminėmis komandomis, rollback apsauga, aiškiomis taisyklėmis ir be kliento modpack.',
    ru: 'Vanilla-style Java survival с claim’ами, базовыми командами, rollback-защитой, ясными правилами и без клиентского modpack.',
    fr: 'Survie Java style Vanilla avec claims, commandes essentielles, protection rollback, règles claires, sans modpack client.',
    de: 'Vanilla-artiges Java-Survival mit Land-Claims, wichtigen Befehlen, Rollback-Schutz, klaren Regeln — ohne Client-Modpack.',
  },
  'A NeoForge 1.21.1 modpack packed with tech, magic, exploration and quality-of-life mods. Build wild contraptions, automate everything, and explore strange new worlds with friends.': {
    lt: 'NeoForge 1.21.1 modpack su tech, magija, tyrinėjimu ir QoL modais. Kurk laukines mašinas, automatizuok viską ir tyrinėk keistus pasaulius su draugais.',
    ru: 'Modpack NeoForge 1.21.1 с tech, магией, исследованием и QoL. Строй безумные механизмы, автоматизируй всё и исследуй странные миры с друзьями.',
    fr: 'Modpack NeoForge 1.21.1 rempli de tech, magie, exploration et QoL. Construis des machines folles, automatise tout et explore de nouveaux mondes entre amis.',
    de: 'NeoForge-1.21.1-Modpack mit Tech, Magie, Erkundung und QoL. Baue wilde Maschinen, automatisiere alles und erkunde seltsame Welten mit Freunden.',
  },

  // --- Legal section titles already partially covered; extra bodies via DOM keys ---
  'How We Use Your Data': {
    lt: 'Kaip naudojame tavo duomenis',
    ru: 'Как мы используем твои данные',
    fr: 'Comment nous utilisons tes données',
    de: 'Wie wir deine Daten nutzen',
  },
  Cookies: { lt: 'Slapukai', ru: 'Cookies', fr: 'Cookies', de: 'Cookies' },
  'Third-Party Services': {
    lt: 'Trečiųjų šalių paslaugos',
    ru: 'Сторонние сервисы',
    fr: 'Services tiers',
    de: 'Drittanbieter-Dienste',
  },
  'Data Retention': {
    lt: 'Duomenų saugojimas',
    ru: 'Хранение данных',
    fr: 'Conservation des données',
    de: 'Datenspeicherung',
  },
  'Data Security': {
    lt: 'Duomenų saugumas',
    ru: 'Безопасность данных',
    fr: 'Sécurité des données',
    de: 'Datensicherheit',
  },
  "Children's Privacy": {
    lt: 'Vaikų privatumas',
    ru: 'Конфиденциальность детей',
    fr: 'Confidentialité des enfants',
    de: 'Datenschutz von Kindern',
  },
  'Your Rights': { lt: 'Tavo teisės', ru: 'Твои права', fr: 'Tes droits', de: 'Deine Rechte' },
  'Changes to This Policy': {
    lt: 'Šios politikos pakeitimai',
    ru: 'Изменения этой политики',
    fr: 'Modifications de cette politique',
    de: 'Änderungen dieser Richtlinie',
  },
  'Contact Form Submissions': {
    lt: 'Kontaktinės formos pranešimai',
    ru: 'Отправки контактной формы',
    fr: 'Envois du formulaire de contact',
    de: 'Kontaktformular-Einsendungen',
  },
  'Game Server Data': {
    lt: 'Žaidimo serverio duomenys',
    ru: 'Данные игрового сервера',
    fr: 'Données des serveurs de jeu',
    de: 'Gameserver-Daten',
  },
  'Payment Data': { lt: 'Mokėjimo duomenys', ru: 'Платёжные данные', fr: 'Données de paiement', de: 'Zahlungsdaten' },
  'Paid Features (VIP & Donations)': {
    lt: 'Mokamos funkcijos (VIP ir parama)',
    ru: 'Платные функции (VIP и донаты)',
    fr: 'Fonctions payantes (VIP et dons)',
    de: 'Bezahlte Features (VIP und Spenden)',
  },
  'Account Termination and Bans': {
    lt: 'Paskyros nutraukimas ir banai',
    ru: 'Блокировки и баны',
    fr: 'Résiliation de compte et bans',
    de: 'Kontosperrung und Bans',
  },
  'Intellectual Property': {
    lt: 'Intelektinė nuosavybė',
    ru: 'Интеллектуальная собственность',
    fr: 'Propriété intellectuelle',
    de: 'Geistiges Eigentum',
  },
  'Limitation of Liability': {
    lt: 'Atsakomybės ribojimas',
    ru: 'Ограничение ответственности',
    fr: 'Limitation de responsabilité',
    de: 'Haftungsbeschränkung',
  },
  'Changes, Governing Framework & Severability': {
    lt: 'Pakeitimai, taikymo tvarka ir atskirtumas',
    ru: 'Изменения, регулирование и делимость',
    fr: 'Modifications, cadre et divisibilité',
    de: 'Änderungen, Rahmen und Teilbarkeit',
  },
  'Fair Play': { lt: 'Sąžiningas žaidimas', ru: 'Честная игра', fr: 'Fair-play', de: 'Fairplay' },
  'No Cheating or Hacking': {
    lt: 'Jokių cheat’ų ar hacking',
    ru: 'Без читов и хакинга',
    fr: 'Pas de triche ni de hacking',
    de: 'Kein Cheaten oder Hacking',
  },
  Respect: { lt: 'Pagarba', ru: 'Уважение', fr: 'Respect', de: 'Respekt' },
  'Your email address': {
    lt: 'Tavo el. pašto adresas',
    ru: 'Твой email',
    fr: 'Ton adresse e-mail',
    de: 'Deine E-Mail-Adresse',
  },
  'The subject of your message': {
    lt: 'Tavo žinutės tema',
    ru: 'Тема твоего сообщения',
    fr: 'Le sujet de ton message',
    de: 'Der Betreff deiner Nachricht',
  },
  'The content of your message': {
    lt: 'Tavo žinutės turinys',
    ru: 'Содержание твоего сообщения',
    fr: 'Le contenu de ton message',
    de: 'Der Inhalt deiner Nachricht',
  },
  'We collect only what is necessary to operate our services and maintain the community.': {
    lt: 'Renkame tik tai, kas būtina paslaugoms ir bendruomenei palaikyti.',
    ru: 'Мы собираем только то, что нужно для работы сервисов и сообщества.',
    fr: 'Nous collectons uniquement ce qui est nécessaire pour faire tourner nos services et la communauté.',
    de: 'Wir erheben nur, was zum Betrieb unserer Dienste und der Community nötig ist.',
  },
  'When you submit a message through a contact form, we collect what you voluntarily provide:': {
    lt: 'Kai siunti žinutę per kontaktinę formą, renkame tai, ką pateiki savanoriškai:',
    ru: 'Когда ты отправляешь сообщение через форму, мы собираем то, что ты добровольно указываешь:',
    fr: 'Quand tu envoies un message via le formulaire, nous collectons ce que tu fournis volontairement :',
    de: 'Wenn du eine Nachricht über das Kontaktformular sendest, erheben wir, was du freiwillig angibst:',
  },
  'June 2026': { lt: '2026 m. birželis', ru: 'Июнь 2026', fr: 'Juin 2026', de: 'Juni 2026' },
  'Welcome to Uliunai.com ("we," "our," or "us"), a community-operated platform hosting game servers and community sites across multiple games. This Privacy Policy explains how we collect, use, store, and protect your information when you visit any Uliunai site, use our services, or connect to our game servers. It applies to uliunai.com and all of its game subdomains.': {
    lt: 'Sveiki atvykę į Uliunai.com („mes“, „mūsų“), bendruomenės valdomą platformą, hostinančią žaidimų serverius ir svetaines. Ši privatumo politika paaiškina, kaip renkame, naudojame, saugome ir giname jūsų informaciją lankantis bet kurioje Uliunai svetainėje, naudojantis paslaugomis ar jungiantis prie žaidimų serverių. Ji taikoma uliunai.com ir visiems žaidimų subdomenams.',
    ru: 'Добро пожаловать на Uliunai.com («мы», «наш») — сообщество, которое хостит игровые серверы и сайты. Эта политика конфиденциальности объясняет, как мы собираем, используем, храним и защищаем ваши данные при посещении сайтов Uliunai, использовании сервисов или подключении к серверам. Она действует для uliunai.com и всех игровых субдоменов.',
    fr: 'Bienvenue sur Uliunai.com (« nous », « notre »), une plateforme communautaire hébergeant serveurs de jeu et sites. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations lorsque vous visitez un site Uliunai, utilisez nos services ou vous connectez à nos serveurs. Elle s’applique à uliunai.com et à tous ses sous-domaines de jeu.',
    de: 'Willkommen bei Uliunai.com („wir“, „unser“), einer community-betriebenen Plattform mit Gameservern und Community-Seiten. Diese Datenschutzerklärung erklärt, wie wir Ihre Daten erheben, nutzen, speichern und schützen, wenn Sie Uliunai-Seiten besuchen, unsere Dienste nutzen oder sich mit Servern verbinden. Sie gilt für uliunai.com und alle Spiel-Subdomains.',
  },
  'By using our websites and services you acknowledge that you have read and understood this policy. If you do not agree with our practices, please discontinue use of our services.': {
    lt: 'Naudodamiesi mūsų svetainėmis ir paslaugomis patvirtinate, kad perskaitėte ir supratote šią politiką. Jei nesutinkate, nutraukite naudojimąsi mūsų paslaugomis.',
    ru: 'Используя наши сайты и сервисы, вы подтверждаете, что прочитали и поняли эту политику. Если вы не согласны, прекратите пользоваться нашими сервисами.',
    fr: 'En utilisant nos sites et services, vous reconnaissez avoir lu et compris cette politique. Si vous n’êtes pas d’accord, cessez d’utiliser nos services.',
    de: 'Durch die Nutzung unserer Websites und Dienste bestätigen Sie, diese Richtlinie gelesen und verstanden zu haben. Wenn Sie nicht einverstanden sind, stellen Sie die Nutzung ein.',
  },
  'By accessing any Uliunai.com website, connecting to our game servers, or interacting with any of our community services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you must not use our Services.': {
    lt: 'Prieidami prie bet kurios Uliunai.com svetainės, jungdamiesi prie žaidimų serverių ar naudodamiesi bendruomenės paslaugomis (bendrai — „Paslaugos“), sutinkate su šiomis paslaugų sąlygomis („Sąlygos“). Jei nesutinkate, neturite naudotis mūsų Paslaugomis.',
    ru: 'Заходя на любой сайт Uliunai.com, подключаясь к игровым серверам или взаимодействуя с сервисами сообщества (вместе — «Сервисы»), вы соглашаетесь с этими Условиями использования («Условия»). Если вы не согласны, не используйте наши Сервисы.',
    fr: 'En accédant à tout site Uliunai.com, en vous connectant à nos serveurs de jeu ou en interagissant avec nos services communautaires (collectivement, les « Services »), vous acceptez ces Conditions d’utilisation (« Conditions »). Si vous n’acceptez pas, vous ne devez pas utiliser nos Services.',
    de: 'Durch den Zugriff auf eine Uliunai.com-Website, die Verbindung zu unseren Gameservern oder die Nutzung unserer Community-Dienste (zusammen die „Dienste“) stimmen Sie diesen Nutzungsbedingungen („Bedingungen“) zu. Wenn Sie nicht einverstanden sind, dürfen Sie unsere Dienste nicht nutzen.',
  },
  'These Terms form a binding agreement between you ("you," "your," or "player") and the Uliunai.com community administration team. They apply to uliunai.com and all of its game subdomains. Please read them carefully.': {
    lt: 'Šios Sąlygos yra įpareigojantis susitarimas tarp jūsų („jūs“, „žaidėjas“) ir Uliunai.com bendruomenės administracijos. Jos taikomos uliunai.com ir visiems žaidimų subdomenams. Perskaitykite jas atidžiai.',
    ru: 'Эти Условия — обязывающее соглашение между вами («вы», «игрок») и администрацией сообщества Uliunai.com. Они действуют для uliunai.com и всех игровых субдоменов. Прочитайте внимательно.',
    fr: 'Ces Conditions forment un accord contraignant entre vous (« vous », « joueur ») et l’équipe d’administration de la communauté Uliunai.com. Elles s’appliquent à uliunai.com et à tous ses sous-domaines de jeu. Lisez-les attentivement.',
    de: 'Diese Bedingungen sind eine verbindliche Vereinbarung zwischen Ihnen („Sie“, „Spieler“) und dem Administrationsteam der Uliunai.com-Community. Sie gelten für uliunai.com und alle Spiel-Subdomains. Bitte lesen Sie sie sorgfältig.',
  },
}
